import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Line,
  LineChart,
} from 'recharts';
import { useAppSelector } from '../store/hooks';
import {
  cryptoPortfolioService,
  ActivityBotKucoinAccount,
  ActivityBotPositionHistoryRow,
  ActivityBotPreviewLeg,
  ActivityBotSectorOption,
  CryptoPortfolioActivityBot,
  CryptoPortfolioPayload,
  CryptoPortfolioPositionRow,
  CryptoPortfolioProfitTarget,
  FilterProfileConfig,
  fetchPositionPriceHistory,
  PositionPricePoint,
  SaveActivityBotRequest,
  SetPositionExitRulesRequest,
  TradingFilterProfile,
  UpdateActivityBotAiAutoTuneRequest,
} from '../services/cryptoPortfolioService';
import { publicMarketService } from '../services/publicMarketService';
import type { CoinActivity } from '../services/publicMarketService';
import CoinPositionsDrawer from '../components/market/CoinPositionsDrawer';
import { adminCanOpenMarketSignalPositions } from '../utils/accessControl';
import ActivityBotAiAutoTuneModal from '../components/crypto/ActivityBotAiAutoTuneModal';
import GrowthChallengeCard from '../components/crypto/GrowthChallengeCard';
import {
  buildActivityBotImportPrefill,
  downloadActivityBotExport,
  parseActivityBotExportJson,
} from '../utils/activityBotExportImport';
import {
  downloadActivityBotHistoryCsv,
  downloadAllActivityBotsHistoryCsv,
} from '../utils/activityBotHistoryExport';
import {
  buildFilterProfileImportCreatePayload,
  downloadFilterProfileExport,
  parseFilterProfileExportJson,
} from '../utils/filterProfileExportImport';

interface CryptoPortfolioProps {
  isAdminContext?: boolean;
}

type StatusFilter = 'OPEN' | 'CLOSED' | 'CLOSED_LOSS' | 'CLOSED_PROFIT' | 'ALL';
type SourceFilter = 'ALL' | 'BOT' | 'DIRECT';
type RealizedPnlWindow = 5 | 10 | 50;

const REALIZED_PNL_WINDOW_KEY = 'cryptoPortfolio.realizedPnlWindow';
const REALIZED_PNL_WINDOWS: RealizedPnlWindow[] = [5, 10, 50];

function fmtUsd(n: number | null | undefined): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return '—';
  const sign = v >= 0 ? '' : '-';
  return `${sign}$${Math.abs(v).toFixed(2)}`;
}

function fmtPnl(n: number | null | undefined): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return '—';
  const sign = v >= 0 ? '+' : '';
  return `${sign}$${Math.abs(v).toFixed(2)}`;
}

function formatCryptoPrice(n: number | null | undefined): string {
  const v = Number(n ?? 0);
  if (!v || !Number.isFinite(v)) return '—';
  if (v >= 1000) return `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (v >= 1) return `$${v.toFixed(2)}`;
  if (v >= 0.01) return `$${v.toFixed(4)}`;
  if (v >= 0.0001) return `$${v.toFixed(6)}`;
  return `$${v.toExponential(2)}`;
}

function fmtDate(raw: string | null | undefined): string {
  if (!raw) return '—';
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;
    return d.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return raw;
  }
}

function timeAgo(epochSec: number): string {
  const diff = Math.max(0, Math.floor(Date.now() / 1000) - epochSec);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

function getPositionRowKey(row: CryptoPortfolioPositionRow): string {
  return `${row.hedgePositionId ?? `s${row.strategyId}`}-${row.symbol}-${row.side}-${row.openedAt ?? row.closedAt ?? ''}`;
}

function isPositionOpen(row: CryptoPortfolioPositionRow): boolean {
  return String(row.status || '').toUpperCase() === 'OPEN';
}

function positionActivityMs(row: CryptoPortfolioPositionRow): number {
  const raw = isPositionOpen(row) ? row.openedAt : (row.closedAt ?? row.openedAt);
  const t = raw ? new Date(raw).getTime() : 0;
  return Number.isFinite(t) ? t : 0;
}

type HistorySortColumn =
  | 'activity'
  | 'side'
  | 'source'
  | 'strategy'
  | 'opened'
  | 'closed'
  | 'entry'
  | 'exitCurrent'
  | 'pnl';

type HistorySortDirection = 'asc' | 'desc';

function parsePositionTimestamp(value: string | null | undefined): number {
  const t = value ? new Date(value).getTime() : NaN;
  return Number.isFinite(t) ? t : 0;
}

function compareStrings(a: string | null | undefined, b: string | null | undefined): number {
  return String(a ?? '').localeCompare(String(b ?? ''), undefined, { sensitivity: 'base' });
}

function rowPnlValue(row: CryptoPortfolioPositionRow): number {
  const pnl = isPositionOpen(row) ? row.unrealizedPnl : row.realizedPnl;
  const value = Number(pnl ?? 0);
  return Number.isFinite(value) ? value : 0;
}

function rowExitCurrentPrice(row: CryptoPortfolioPositionRow): number {
  const price = isPositionOpen(row) ? row.currentPrice : row.exitPrice;
  const value = Number(price ?? 0);
  return Number.isFinite(value) ? value : 0;
}

function closedSortMs(row: CryptoPortfolioPositionRow, direction: HistorySortDirection): number {
  const t = parsePositionTimestamp(row.closedAt);
  if (t > 0) return t;
  return direction === 'asc' ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;
}

function compareHistoryRows(
  a: CryptoPortfolioPositionRow,
  b: CryptoPortfolioPositionRow,
  column: HistorySortColumn,
  direction: HistorySortDirection,
): number {
  let cmp = 0;
  switch (column) {
    case 'activity':
      cmp = positionActivityMs(a) - positionActivityMs(b);
      break;
    case 'side':
      cmp = compareStrings(a.side, b.side);
      break;
    case 'source':
      cmp = compareStrings(a.source, b.source);
      break;
    case 'strategy':
      cmp = compareStrings(a.strategyName, b.strategyName);
      break;
    case 'opened':
      cmp = parsePositionTimestamp(a.openedAt) - parsePositionTimestamp(b.openedAt);
      break;
    case 'closed':
      cmp = closedSortMs(a, direction) - closedSortMs(b, direction);
      break;
    case 'entry':
      cmp = Number(a.entryPrice ?? 0) - Number(b.entryPrice ?? 0);
      break;
    case 'exitCurrent':
      cmp = rowExitCurrentPrice(a) - rowExitCurrentPrice(b);
      break;
    case 'pnl':
      cmp = rowPnlValue(a) - rowPnlValue(b);
      break;
    default:
      cmp = 0;
  }

  if (cmp === 0) {
    cmp = positionActivityMs(b) - positionActivityMs(a);
  }
  return direction === 'asc' ? cmp : -cmp;
}

function sortHistoryRows(
  rows: CryptoPortfolioPositionRow[],
  column: HistorySortColumn = 'activity',
  direction: HistorySortDirection = 'desc',
): CryptoPortfolioPositionRow[] {
  return [...rows].sort((a, b) => compareHistoryRows(a, b, column, direction));
}

function isClosedLoss(row: CryptoPortfolioPositionRow): boolean {
  const pnl = Number(row.realizedPnl ?? 0);
  return Number.isFinite(pnl) && pnl < 0;
}

function isClosedProfit(row: CryptoPortfolioPositionRow): boolean {
  const pnl = Number(row.realizedPnl ?? 0);
  return Number.isFinite(pnl) && pnl > 0;
}

function sumOpenRows(
  rows: CryptoPortfolioPositionRow[] | undefined,
  pick: (row: CryptoPortfolioPositionRow) => number,
): number {
  return (rows ?? []).reduce((sum, row) => {
    const value = pick(row);
    return sum + (Number.isFinite(value) ? value : 0);
  }, 0);
}

const PORTFOLIO_POLL_MS = 30_000;

function formatPositionRef(row: CryptoPortfolioPositionRow): string {
  if (row.hedgePositionId != null) return `#${row.hedgePositionId}`;
  if (row.strategyId != null) return `S${row.strategyId}`;
  return '—';
}

function formatDuration(from: string | null | undefined, to: string | null | undefined): string {
  const start = from ? new Date(from).getTime() : NaN;
  const end = to ? new Date(to).getTime() : Date.now();
  if (!Number.isFinite(start)) return '—';
  const diffMs = Math.max(0, end - start);
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 48) return `${hrs}h ${mins % 60}m`;
  const days = Math.floor(hrs / 24);
  return `${days}d ${hrs % 24}h`;
}

function priceChangePct(row: CryptoPortfolioPositionRow, isOpen: boolean): number | null {
  const entry = Number(row.entryPrice);
  const mark = isOpen ? Number(row.currentPrice) : Number(row.exitPrice);
  if (!entry || !mark || !Number.isFinite(entry) || !Number.isFinite(mark)) return null;
  const raw = ((mark - entry) / entry) * 100;
  return row.side === 'SHORT' ? -raw : raw;
}

function fmtPct(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  const sign = n >= 0 ? '+' : '';
  return `${sign}${n.toFixed(2)}%`;
}

function fmtLeverage(n: number | null | undefined): string {
  const v = Number(n ?? 0);
  if (!v || !Number.isFinite(v)) return '—';
  const rounded = Math.round(v * 100) / 100;
  return `${rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(2)}x`;
}

function clampProgress(n: number | null | undefined): number {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(100, v));
}

const TARGET_BAR_CAP = 100;

function normalizeTargetProgress(n: number | null | undefined): number {
  const v = Number(n ?? 0);
  return Number.isFinite(v) ? v : 0;
}

function formatTargetProgressLabel(progress: number, achieved: boolean): string {
  if (achieved || progress >= 100) return '100%+';
  const rounded = Math.round(progress);
  if (rounded > 0) return `+${rounded}%`;
  if (rounded < 0) return `${rounded}%`;
  return '0%';
}

function targetProgressLabelColor(progress: number, achieved: boolean): string {
  if (achieved || progress >= 100) return '#15803d';
  if (progress < 0) return '#b91c1c';
  if (progress > 0) return '#15803d';
  return '#64748b';
}

function formatTargetProgressLabelForRow(row: CryptoPortfolioPositionRow): string {
  if (row.source !== 'DIRECT' || row.targetProgressPct == null) return '—';
  return formatTargetProgressLabel(normalizeTargetProgress(row.targetProgressPct), Boolean(row.targetAchieved));
}

function TargetProgressBar({ progress, achieved }: { progress: number; achieved: boolean }) {
  const mag = Math.min(TARGET_BAR_CAP, Math.abs(progress));
  const gainColor = achieved ? '#0dca9d' : '#086afb';

  return (
    <div
      style={{
        position: 'relative',
        marginTop: 4,
        height: 6,
        background: '#e2e8f0',
        borderRadius: 999,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: 0,
          bottom: 0,
          width: 1,
          background: '#94a3b8',
          transform: 'translateX(-50%)',
          zIndex: 1,
        }}
      />
      {progress < 0 && mag > 0 && (
        <div
          style={{
            position: 'absolute',
            right: '50%',
            top: 0,
            height: '100%',
            width: `${mag}%`,
            background: '#ff7171',
            borderRadius: '999px 0 0 999px',
          }}
        />
      )}
      {progress >= 0 && mag > 0 && (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: 0,
            height: '100%',
            width: `${mag}%`,
            background: gainColor,
            borderRadius: '0 999px 999px 0',
          }}
        />
      )}
    </div>
  );
}

function TargetProgressCell({ row }: { row: CryptoPortfolioPositionRow }) {
  const hasTarget = row.source === 'DIRECT' && row.targetProgressPct != null;
  const hasStop = hasStopLoss(row);
  if (!hasTarget && !hasStop) {
    return <span style={{ color: '#94a3b8' }}>—</span>;
  }

  const progress = normalizeTargetProgress(row.targetProgressPct);
  const achieved = Boolean(row.targetAchieved);
  const label = formatTargetProgressLabel(progress, achieved);
  const labelColor = targetProgressLabelColor(progress, achieved);

  return (
    <div style={{ minWidth: 88 }}>
      {hasTarget && (
        <>
          <div
            style={{
              fontWeight: 700,
              fontSize: 12,
              color: labelColor,
              whiteSpace: 'nowrap',
            }}
          >
            {label}
            {achieved ? ' ✓' : ''}
          </div>
          <TargetProgressBar progress={progress} achieved={achieved} />
          {row.targetPct != null && (
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>
              Goal +{Number(row.targetPct).toFixed(1)}%
            </div>
          )}
        </>
      )}
      {hasStop && <StopLossProgressSection row={row} compact />}
    </div>
  );
}

const STOP_LOSS_BAR_CAP = 100;

function hasStopLoss(row: CryptoPortfolioPositionRow): boolean {
  const stop = Number(row.stopLossPct ?? 0);
  return row.source === 'DIRECT' && Number.isFinite(stop) && stop > 0;
}

function computeStopLossUsedPct(
  pnlOnSizePct: number | null | undefined,
  stopLossPct: number | null | undefined,
): number | null {
  const stop = Number(stopLossPct ?? 0);
  const pnl = Number(pnlOnSizePct ?? 0);
  if (!Number.isFinite(stop) || stop <= 0) return null;
  if (!Number.isFinite(pnl) || pnl >= 0) return 0;
  return Math.min(STOP_LOSS_BAR_CAP, (Math.abs(pnl) / stop) * 100);
}

function formatStopLossUsedLabel(usedPct: number, atOrPastStop: boolean): string {
  if (atOrPastStop || usedPct >= 100) return 'At stop';
  if (usedPct <= 0) return '0% used';
  return `${Math.round(usedPct)}% used`;
}

function stopLossUsedLabelColor(usedPct: number, atOrPastStop: boolean): string {
  if (atOrPastStop || usedPct >= 100) return '#b91c1c';
  if (usedPct >= 80) return '#b91c1c';
  if (usedPct >= 50) return '#d97706';
  if (usedPct > 0) return '#ea580c';
  return '#64748b';
}

function stopLossBarColor(usedPct: number, atOrPastStop: boolean): string {
  if (atOrPastStop || usedPct >= 100) return '#b91c1c';
  if (usedPct >= 80) return '#ef4444';
  if (usedPct >= 50) return '#f59e0b';
  if (usedPct > 0) return '#fca5a5';
  return '#e2e8f0';
}

function StopLossProgressBar({ usedPct, atOrPastStop }: { usedPct: number; atOrPastStop: boolean }) {
  const fill = Math.min(STOP_LOSS_BAR_CAP, Math.max(0, usedPct));
  return (
    <div
      style={{
        position: 'relative',
        marginTop: 4,
        height: 6,
        background: '#e2e8f0',
        borderRadius: 999,
        overflow: 'hidden',
      }}
    >
      {fill > 0 && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            height: '100%',
            width: `${fill}%`,
            background: stopLossBarColor(fill, atOrPastStop),
            borderRadius: 999,
            transition: 'width 0.3s ease',
          }}
        />
      )}
    </div>
  );
}

function StopLossProgressSection({
  row,
  compact = false,
}: {
  row: CryptoPortfolioPositionRow;
  compact?: boolean;
}) {
  const stopPct = Number(row.stopLossPct ?? 0);
  const used = computeStopLossUsedPct(row.pnlOnSizePct, stopPct);
  if (used == null) return null;

  const pnlOnSize = Number(row.pnlOnSizePct ?? 0);
  const atOrPastStop = pnlOnSize <= -stopPct;
  const label = formatStopLossUsedLabel(used, atOrPastStop);
  const labelColor = stopLossUsedLabelColor(used, atOrPastStop);
  const sizeUsd = Number(row.sizeUsd ?? 0);
  const maxLossUsd = sizeUsd > 0 ? (sizeUsd * stopPct) / 100 : null;

  return (
    <div style={{ marginTop: compact ? 8 : 0, maxWidth: compact ? undefined : 280 }}>
      {!compact && (
        <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>Stop loss buffer</div>
      )}
      <div
        style={{
          fontWeight: 700,
          fontSize: 12,
          color: labelColor,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </div>
      <StopLossProgressBar usedPct={used} atOrPastStop={atOrPastStop} />
      <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>
        Limit −{stopPct.toFixed(1)}% on size
        {maxLossUsd != null ? ` · ~${fmtUsd(maxLossUsd)} max` : ''}
      </div>
      {!compact && pnlOnSize < 0 && (
        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
          −{Math.abs(pnlOnSize).toFixed(1)}% on size now
        </div>
      )}
      {!compact && pnlOnSize >= 0 && (
        <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>Not at risk while in profit</div>
      )}
    </div>
  );
}

function AutoCloseToggle({
  row,
  disabled,
  onToggle,
}: {
  row: CryptoPortfolioPositionRow;
  disabled?: boolean;
  onToggle: (enabled: boolean) => void;
}) {
  if (!row.canToggleAutoClose) return null;

  return (
    <label
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 11,
        fontWeight: 600,
        color: '#475569',
        cursor: disabled ? 'wait' : 'pointer',
        whiteSpace: 'nowrap',
        userSelect: 'none',
      }}
      onClick={e => e.stopPropagation()}
    >
      <input
        type="checkbox"
        checked={Boolean(row.autoCloseOnTarget)}
        disabled={disabled}
        onChange={e => onToggle(e.target.checked)}
        style={{ accentColor: '#086afb', cursor: disabled ? 'wait' : 'pointer' }}
      />
      Auto-close at target
    </label>
  );
}

const fieldLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 700,
  color: '#64748b',
  marginBottom: 6,
  textTransform: 'uppercase',
  letterSpacing: 0.3,
};

const fieldInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  fontSize: 13,
  boxSizing: 'border-box',
};

const ACTIVITY_BOT_AMOUNT_MIN = 1;
const ACTIVITY_BOT_AMOUNT_MAX = 500;
const ACTIVITY_BOT_AMOUNT_DIAL_STEP = 0.5;
const ACTIVITY_BOT_MAX_LEVERAGE = 10;
const ACTIVITY_BOT_LEVERAGE_SNAP = [1, 2, 3, 5, 10] as const;
const ACTIVITY_BOT_MARGIN_SAFETY_FACTOR = 1.3;

function clampActivityBotAmount(value: number): number {
  if (!Number.isFinite(value)) return ACTIVITY_BOT_AMOUNT_MIN;
  const rounded = Math.round(value * 2) / 2;
  return Math.max(ACTIVITY_BOT_AMOUNT_MIN, Math.min(ACTIVITY_BOT_AMOUNT_MAX, rounded));
}

function snapActivityBotLeverage(value: number): number {
  if (!Number.isFinite(value)) return 1;
  const clamped = Math.max(1, Math.min(ACTIVITY_BOT_MAX_LEVERAGE, Math.round(value)));
  let best: number = ACTIVITY_BOT_LEVERAGE_SNAP[0];
  let bestDiff = Math.abs(clamped - best);
  for (const snap of ACTIVITY_BOT_LEVERAGE_SNAP) {
    const diff = Math.abs(clamped - snap);
    if (diff < bestDiff) {
      best = snap;
      bestDiff = diff;
    }
  }
  return best;
}

function stepActivityBotLeverage(current: number, direction: -1 | 1): number {
  const snapped = snapActivityBotLeverage(current);
  const idx = ACTIVITY_BOT_LEVERAGE_SNAP.findIndex(v => v === snapped);
  const safeIdx = idx >= 0 ? idx : 0;
  const nextIdx = Math.max(0, Math.min(ACTIVITY_BOT_LEVERAGE_SNAP.length - 1, safeIdx + direction));
  return ACTIVITY_BOT_LEVERAGE_SNAP[nextIdx];
}

function estimateActivityBotMarginPerLeg(amountUsd: number, leverage: number): number {
  const amount = Number(amountUsd);
  const lev = Math.max(1, Number(leverage) || 1);
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  return (amount / lev) * ACTIVITY_BOT_MARGIN_SAFETY_FACTOR;
}

const activityBotDialBtnStyle: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 999,
  border: '1px solid #cbd5e1',
  background: '#fff',
  color: '#334155',
  fontSize: 18,
  fontWeight: 700,
  cursor: 'pointer',
  lineHeight: 1,
};

interface ActivityBotDialControlProps {
  label: string;
  value: string;
  min: number;
  max: number;
  dialStep: number;
  inputStep?: number;
  snapValues?: readonly number[];
  formatDisplay: (value: number) => string;
  onChange: (next: string) => void;
  onDialChange?: (next: number) => number;
  onStepDown?: (current: number) => number;
  onStepUp?: (current: number) => number;
  hint?: string;
  inputSuffix?: string;
}

const ActivityBotDialControl: React.FC<ActivityBotDialControlProps> = ({
  label,
  value,
  min,
  max,
  dialStep,
  inputStep = dialStep,
  formatDisplay,
  onChange,
  onDialChange,
  onStepDown,
  onStepUp,
  hint,
  inputSuffix,
  snapValues,
}) => {
  const parsed = Number(value);
  const resolvedValue = Number.isFinite(parsed)
    ? (onDialChange ? onDialChange(parsed) : parsed)
    : (snapValues?.[0] ?? min);
  const dialMin = snapValues ? 0 : min;
  const dialMax = snapValues ? Math.max(0, snapValues.length - 1) : max;
  let safeDial = snapValues
    ? snapValues.findIndex(v => v === snapActivityBotLeverage(resolvedValue))
    : resolvedValue;
  if (snapValues && safeDial < 0) safeDial = 0;
  if (!snapValues) {
    safeDial = Math.max(min, Math.min(max, resolvedValue));
  }
  const safe = snapValues ? snapValues[safeDial] ?? snapValues[0] : safeDial;
  const ratio = dialMax > dialMin ? (safeDial - dialMin) / (dialMax - dialMin) : 0;
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - Math.max(0, Math.min(1, ratio)));

  const applyValue = (next: number) => {
    if (snapValues) {
      const idx = Math.max(0, Math.min(snapValues.length - 1, Math.round(next)));
      onChange(String(snapValues[idx]));
      return;
    }
    const normalized = onDialChange ? onDialChange(next) : next;
    const clamped = Math.max(min, Math.min(max, normalized));
    onChange(String(clamped));
  };

  const stepDown = () => {
    if (snapValues && onStepDown) {
      onChange(String(onStepDown(safe)));
      return;
    }
    if (snapValues) {
      applyValue(safeDial - 1);
      return;
    }
    const next = onStepDown ? onStepDown(safe) : safe - dialStep;
    applyValue(next);
  };

  const stepUp = () => {
    if (snapValues && onStepUp) {
      onChange(String(onStepUp(safe)));
      return;
    }
    if (snapValues) {
      applyValue(safeDial + 1);
      return;
    }
    const next = onStepUp ? onStepUp(safe) : safe + dialStep;
    applyValue(next);
  };

  return (
    <div
      style={{
        border: '1px solid #e2e8f0',
        borderRadius: 12,
        padding: '12px 14px 14px',
        background: '#fff',
      }}
    >
      <label style={{ ...fieldLabelStyle, marginBottom: 10 }}>{label}</label>
      <div style={{ position: 'relative', width: 132, height: 132, margin: '0 auto' }}>
        <svg width="132" height="132" viewBox="0 0 100 100" aria-hidden="true">
          <circle cx="50" cy="50" r={radius} fill="none" stroke="#e2e8f0" strokeWidth="7" />
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke="#086afb"
            strokeWidth="7"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            transform="rotate(-90 50 50)"
            style={{ transition: 'stroke-dashoffset 0.15s ease' }}
          />
        </svg>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
            fontWeight: 800,
            color: '#1e293b',
            fontFamily: 'Nunito, sans-serif',
            pointerEvents: 'none',
          }}
        >
          {formatDisplay(safe)}
        </div>
        <input
          type="range"
          min={dialMin}
          max={dialMax}
          step={1}
          value={safeDial}
          onChange={e => applyValue(Number(e.target.value))}
          aria-label={label}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            margin: 0,
            opacity: 0,
            cursor: 'pointer',
          }}
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 10 }}>
        <button type="button" onClick={stepDown} aria-label={`Decrease ${label}`} style={activityBotDialBtnStyle}>
          −
        </button>
        <button type="button" onClick={stepUp} aria-label={`Increase ${label}`} style={activityBotDialBtnStyle}>
          +
        </button>
      </div>
      <div style={{ marginTop: 10 }}>
        <input
          type="number"
          min={min}
          max={max}
          step={inputStep}
          value={value}
          onChange={e => {
            const next = Number(e.target.value);
            if (!Number.isFinite(next)) {
              onChange(e.target.value);
              return;
            }
            applyValue(next);
          }}
          style={{ ...fieldInputStyle, textAlign: 'center' }}
        />
        {inputSuffix && (
          <div style={{ marginTop: 4, fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>{inputSuffix}</div>
        )}
      </div>
      {hint && (
        <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8', lineHeight: 1.45 }}>{hint}</div>
      )}
    </div>
  );
};

interface ActivityBotMarginPreviewProps {
  amountUsd: string;
  leverage: string;
  totalPositions: string;
}

const ActivityBotMarginPreview: React.FC<ActivityBotMarginPreviewProps> = ({
  amountUsd,
  leverage,
  totalPositions,
}) => {
  const amount = Number(amountUsd);
  const lev = snapActivityBotLeverage(Number(leverage));
  const legs = Number(totalPositions);
  if (!Number.isFinite(amount) || amount <= 0 || !Number.isFinite(lev) || lev < 1) {
    return null;
  }
  const marginPerLeg = estimateActivityBotMarginPerLeg(amount, lev);
  const legCount = Number.isFinite(legs) && legs > 0 ? legs : 1;
  const cycleNotional = amount * legCount;
  const cycleMarginEst = marginPerLeg * legCount;

  return (
    <div
      style={{
        marginTop: 12,
        padding: '12px 14px',
        borderRadius: 10,
        border: '1px solid #bfdbfe',
        background: 'linear-gradient(180deg, #f8fbff 0%, #eff6ff 100%)',
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, color: '#086afb', textTransform: 'uppercase', letterSpacing: 0.3 }}>
        Margin preview
      </div>
      <div style={{ marginTop: 8, fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
        <strong>Est. margin per leg:</strong>{' '}
        ~{fmtUsd(marginPerLeg)} at {fmtUsd(amount)} · {fmtLeverage(lev)}
      </div>
      <div style={{ marginTop: 4, fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
        <strong>Cycle notional:</strong> ~{fmtUsd(cycleNotional)} ({legCount} leg{legCount === 1 ? '' : 's'})
        {' · '}
        <strong>est. margin:</strong> ~{fmtUsd(cycleMarginEst)}
      </div>
      <div style={{ marginTop: 8, fontSize: 11, color: '#64748b', lineHeight: 1.45 }}>
        Includes a 30% safety buffer. If KuCoin rejects an order, the bot may auto-raise leverage (up to {ACTIVITY_BOT_MAX_LEVERAGE}x) or lower size.
      </div>
    </div>
  );
};

interface ActivityBotSizingControlsProps {
  amountUsd: string;
  leverage: string;
  totalPositions: string;
  onAmountChange: (value: string) => void;
  onLeverageChange: (value: string) => void;
}

const ActivityBotSizingControls: React.FC<ActivityBotSizingControlsProps> = ({
  amountUsd,
  leverage,
  totalPositions,
  onAmountChange,
  onLeverageChange,
}) => (
  <div style={{ gridColumn: '1 / -1' }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 8 }}>
      2) Size &amp; leverage
    </div>
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 12,
      }}
    >
      <ActivityBotDialControl
        label="Amount per position (USD)"
        value={amountUsd}
        min={ACTIVITY_BOT_AMOUNT_MIN}
        max={ACTIVITY_BOT_AMOUNT_MAX}
        dialStep={ACTIVITY_BOT_AMOUNT_DIAL_STEP}
        inputStep={0.01}
        formatDisplay={v => fmtUsd(v)}
        onChange={onAmountChange}
        onDialChange={clampActivityBotAmount}
        onStepDown={v => clampActivityBotAmount(v - ACTIVITY_BOT_AMOUNT_DIAL_STEP)}
        onStepUp={v => clampActivityBotAmount(v + ACTIVITY_BOT_AMOUNT_DIAL_STEP)}
        inputSuffix="Type exact USD if needed"
      />
      <ActivityBotDialControl
        label="Leverage (futures)"
        value={leverage}
        min={1}
        max={ACTIVITY_BOT_MAX_LEVERAGE}
        dialStep={1}
        inputStep={1}
        snapValues={ACTIVITY_BOT_LEVERAGE_SNAP}
        formatDisplay={v => fmtLeverage(v)}
        onChange={onLeverageChange}
        onDialChange={snapActivityBotLeverage}
        onStepDown={v => stepActivityBotLeverage(v, -1)}
        onStepUp={v => stepActivityBotLeverage(v, 1)}
        hint="Applied to new legs opened after save. Max 10x. Existing open positions keep their entry leverage."
        inputSuffix="Snaps to 1, 2, 3, 5, 10 (max 10x)"
      />
    </div>
    <ActivityBotMarginPreview
      amountUsd={amountUsd}
      leverage={leverage}
      totalPositions={totalPositions}
    />
  </div>
);

interface PositionExitRulesPanelProps {
  row: CryptoPortfolioPositionRow;
  saving?: boolean;
  onSave: (payload: SetPositionExitRulesRequest) => Promise<void>;
}

const PositionExitRulesPanel: React.FC<PositionExitRulesPanelProps> = ({ row, saving, onSave }) => {
  const [stopLossPct, setStopLossPct] = useState('');
  const [trailingEnabled, setTrailingEnabled] = useState(false);
  const [trailActivation, setTrailActivation] = useState('5');
  const [trailDistance, setTrailDistance] = useState('4');
  const [breakEntry, setBreakEntry] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    setStopLossPct(row.stopLossPct != null ? String(row.stopLossPct) : '');
    setTrailingEnabled(Boolean(row.trailingStopEnabled));
    setTrailActivation(row.trailingStopActivationPct != null ? String(row.trailingStopActivationPct) : '5');
    setTrailDistance(row.trailingStopDistancePct != null ? String(row.trailingStopDistancePct) : '4');
    setBreakEntry(Boolean(row.breakEntryExitEnabled));
    setLocalError(null);
  }, [
    row.hedgePositionId,
    row.stopLossPct,
    row.trailingStopEnabled,
    row.trailingStopActivationPct,
    row.trailingStopDistancePct,
    row.breakEntryExitEnabled,
  ]);

  const handleSave = async () => {
    setLocalError(null);
    const stopRaw = stopLossPct.trim();
    const stop = stopRaw === '' ? 0 : Number(stopRaw);
    const act = Number(trailActivation);
    const dist = Number(trailDistance);
    if (stopRaw !== '' && (!Number.isFinite(stop) || stop <= 0 || stop > 100)) {
      setLocalError('Stop loss must be between 0 and 100%, or empty to disable.');
      return;
    }
    if (trailingEnabled && (!Number.isFinite(act) || act <= 0 || !Number.isFinite(dist) || dist <= 0)) {
      setLocalError('Trailing activation and distance must be greater than 0%.');
      return;
    }
    try {
      await onSave({
        stopLossPct: stop,
        trailingStopEnabled: trailingEnabled,
        trailingStopActivationPct: trailingEnabled ? act : undefined,
        trailingStopDistancePct: trailingEnabled ? dist : undefined,
        breakEntryExitEnabled: breakEntry,
      });
    } catch (err: any) {
      setLocalError(err?.message || 'Failed to save exit rules.');
    }
  };

  const peak = row.peakPriceSinceOpen;
  const trough = row.troughPriceSinceOpen;

  return (
    <div
      style={{
        marginBottom: 16,
        padding: '12px 14px',
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 10,
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>Smart exit rules</div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 12 }}>
        Uses live price vs chart extremes since open. Checked every ~60s.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
        <div>
          <label style={fieldLabelStyle}>Stop loss (% on size)</label>
          <input
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={stopLossPct}
            onChange={e => setStopLossPct(e.target.value)}
            placeholder="e.g. 10"
            style={fieldInputStyle}
          />
        </div>
        <div>
          <label style={{ ...fieldLabelStyle, display: 'flex', alignItems: 'center', gap: 6, marginTop: 20 }}>
            <input
              type="checkbox"
              checked={trailingEnabled}
              onChange={e => setTrailingEnabled(e.target.checked)}
              style={{ accentColor: '#086afb' }}
            />
            Trailing stop
          </label>
          {row.trailingStopActive && (
            <div style={{ fontSize: 10, color: '#15803d', marginTop: 4, fontWeight: 600 }}>Trail active</div>
          )}
        </div>
        <div>
          <label style={fieldLabelStyle}>Trail after +%</label>
          <input
            type="number"
            min="0.1"
            step="0.1"
            value={trailActivation}
            disabled={!trailingEnabled}
            onChange={e => setTrailActivation(e.target.value)}
            style={fieldInputStyle}
          />
        </div>
        <div>
          <label style={fieldLabelStyle}>Trail distance %</label>
          <input
            type="number"
            min="0.1"
            step="0.1"
            value={trailDistance}
            disabled={!trailingEnabled}
            onChange={e => setTrailDistance(e.target.value)}
            style={fieldInputStyle}
          />
        </div>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 12, color: '#475569' }}>
        <input
          type="checkbox"
          checked={breakEntry}
          onChange={e => setBreakEntry(e.target.checked)}
          style={{ accentColor: '#086afb' }}
        />
        Close if price breaks back through entry after a favorable move
      </label>

      {(peak != null || trough != null) && (
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 10 }}>
          Since open: peak {peak != null ? formatCryptoPrice(peak) : '—'}
          {trough != null ? ` · trough ${formatCryptoPrice(trough)}` : ''}
        </div>
      )}

      {localError && (
        <div style={{ marginTop: 10, fontSize: 12, color: '#b91c1c' }}>{localError}</div>
      )}

      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        style={{
          marginTop: 12,
          padding: '7px 14px',
          borderRadius: 999,
          border: 'none',
          background: saving ? '#94a3b8' : '#086afb',
          color: '#fff',
          fontSize: 12,
          fontWeight: 700,
          cursor: saving ? 'wait' : 'pointer',
        }}
      >
        {saving ? 'Saving…' : 'Save exit rules'}
      </button>
    </div>
  );
};

function readStoredRealizedWindow(): RealizedPnlWindow {
  try {
    const raw = localStorage.getItem(REALIZED_PNL_WINDOW_KEY);
    const n = Number(raw);
    if (n === 5 || n === 10 || n === 50) return n;
  } catch {
    // ignore
  }
  return 50;
}

function sortClosedByDateDesc(rows: CryptoPortfolioPositionRow[]): CryptoPortfolioPositionRow[] {
  return [...rows].sort((a, b) => {
    const ta = a.closedAt ? new Date(a.closedAt).getTime() : 0;
    const tb = b.closedAt ? new Date(b.closedAt).getTime() : 0;
    return tb - ta;
  });
}

const cardStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: 12,
  padding: '16px 18px',
  boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
};

const TH: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: 11,
  fontWeight: 700,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  background: '#f8fafc',
  borderBottom: '1px solid #e2e8f0',
  whiteSpace: 'nowrap',
};

const TD: React.CSSProperties = {
  padding: '11px 12px',
  fontSize: 13,
  color: '#334155',
  borderBottom: '1px solid #e2e8f0',
  verticalAlign: 'middle',
};

const MOBILE_BREAKPOINT = 768;
const DESKTOP_PAGE_MAX_WIDTH = 1440;

function useIsMobile(breakpoint = MOBILE_BREAKPOINT): boolean {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

function SideBadge({ side }: { side: string }) {
  const isLong = side === 'LONG';
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: 999,
        background: isLong ? '#dcfce7' : '#fee2e2',
        color: isLong ? '#15803d' : '#b91c1c',
        whiteSpace: 'nowrap',
      }}
    >
      {side}
    </span>
  );
}

function StatusBadge({ open }: { open: boolean }) {
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: 999,
        background: open ? '#dcfce7' : '#e2e8f0',
        color: open ? '#15803d' : '#64748b',
        whiteSpace: 'nowrap',
      }}
    >
      {open ? 'OPEN' : 'CLOSED'}
    </span>
  );
}

function SourceBadge({ source }: { source: string }) {
  const isDirect = source === 'DIRECT';
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 999,
        background: isDirect ? '#eff6ff' : '#f5f3ff',
        color: isDirect ? '#1d4ed8' : '#6d28d9',
        whiteSpace: 'nowrap',
      }}
    >
      {isDirect ? 'Direct' : 'Bot'}
    </span>
  );
}

function activityBotShortName(label: string): string {
  const trimmed = label.trim();
  if (!trimmed) return 'Activity bot';
  const parts = trimmed.split('·').map(part => part.trim()).filter(Boolean);
  return parts[0] || trimmed;
}

function activityBotAiTuneButtonLabel(bot: CryptoPortfolioActivityBot): string {
  if (bot.aiAutoTunePending) return '⏳ AI Pending';
  if (bot.aiAutoTuneEnabled) return '✅ AI On';
  return '🤖 AI Tune';
}

function activityBotAiTuneButtonStyle(bot: CryptoPortfolioActivityBot): React.CSSProperties {
  if (bot.aiAutoTunePending) {
    return { background: '#fffbeb', color: '#b45309', border: '1px solid #fcd34d' };
  }
  if (bot.aiAutoTuneEnabled) {
    return { background: '#ecfdf5', color: '#047857', border: '1px solid #86efac' };
  }
  return { background: '#faf5ff', color: '#6d28d9', border: '1px solid #c4b5fd' };
}

function ActivityBotBadge({ label, wrap, compact }: { label: string; wrap?: boolean; compact?: boolean }) {
  const fullTitle = `Opened by AI Activity Bot: ${label}`;
  const displayName = activityBotShortName(label);

  if (compact) {
    return (
      <span
        style={{
          fontSize: 10,
          fontWeight: 600,
          padding: '2px 8px',
          borderRadius: 999,
          background: '#fdf4ff',
          color: '#7e22ce',
          display: 'inline-block',
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          lineHeight: 1.35,
        }}
        title={fullTitle}
      >
        {displayName}
      </span>
    );
  }

  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 999,
        background: '#fdf4ff',
        color: '#7e22ce',
        whiteSpace: wrap ? 'normal' : 'nowrap',
        display: wrap ? 'inline-block' : 'inline',
        maxWidth: wrap ? '100%' : undefined,
        overflow: wrap ? 'hidden' : undefined,
        textOverflow: wrap ? 'ellipsis' : undefined,
        wordBreak: wrap ? 'break-word' : undefined,
        lineHeight: 1.35,
      }}
      title={fullTitle}
    >
      Activity · {label}
    </span>
  );
}

function activityBotCardTitle(bot: CryptoPortfolioActivityBot): string {
  const ref = bot.referenceName?.trim();
  if (ref) return ref;
  return `${bot.sectorLabel || bot.sectorId} · ${bot.kucoinAccountName || 'KuCoin'}`;
}

function activityBotSafeguardSummary(bot: CryptoPortfolioActivityBot): string {
  return `target +${Number(bot.targetProfitPct).toFixed(1)}%`;
}

function formatBotStopLoss(bot: CryptoPortfolioActivityBot): string {
  if (bot.stopLossPct != null && bot.stopLossPct > 0) {
    return `−${Number(bot.stopLossPct).toFixed(1)}% on size`;
  }
  return 'Not set';
}

function formatBotTrailingStop(bot: CryptoPortfolioActivityBot): string {
  if (!bot.trailingStopEnabled) return 'Off';
  const act = bot.trailingStopActivationPct ?? 5;
  const dist = bot.trailingStopDistancePct ?? 4;
  return `On · after +${Number(act).toFixed(1)}% · trail ${Number(dist).toFixed(1)}%`;
}

function formatBotMaxLossUsd(bot: CryptoPortfolioActivityBot): string {
  if (bot.maxBotLossUsd != null && bot.maxBotLossUsd > 0) {
    return fmtUsd(bot.maxBotLossUsd);
  }
  return 'Not set';
}

function formatBotMaxLossPct(bot: CryptoPortfolioActivityBot): string {
  if (bot.maxBotLossPct != null && bot.maxBotLossPct > 0) {
    return `${Number(bot.maxBotLossPct).toFixed(1)}% of deployed`;
  }
  return 'Not set';
}

function formatBotEntryMode(bot: CryptoPortfolioActivityBot): string {
  if (bot.filterProfileName) {
    return `Profile: ${bot.filterProfileName}`;
  }
  const mode = (bot.entryMode || 'ACTIVITY_ONLY').toUpperCase();
  if (mode === 'PEAK_TROUGH_TIMING') {
    return 'Peak/trough timing (chart confirmation)';
  }
  return 'Activity only (HOT ranking)';
}

function filterStatusColor(status?: string | null): string {
  const s = (status || '').toUpperCase();
  if (s === 'PASS') return '#15803d';
  if (s === 'WAIT') return '#b45309';
  if (s === 'REJECT' || s === 'BLOCK_ALL') return '#b91c1c';
  return '#64748b';
}

interface FilterProfileDetailRow {
  section: string;
  label: string;
  value: string;
}

function onOff(enabled: boolean | undefined): string {
  return enabled ? 'On' : 'Off';
}

function pctRate(value: number | undefined, digits = 1): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(digits)}%`;
}

function describeFilterProfileConfigDetails(config?: FilterProfileConfig | null): FilterProfileDetailRow[] {
  if (!config) {
    return [{ section: 'Configuration', label: 'Status', value: 'No configuration stored' }];
  }

  const rows: FilterProfileDetailRow[] = [];
  const entry = config.entry ?? {};

  rows.push({ section: 'Entry filters', label: 'Technical entry filters', value: onOff(entry.enabled) });
  if (entry.enabled) {
    rows.push({
      section: 'Entry filters',
      label: 'RSI alignment',
      value: entry.rsiEnabled === false
        ? 'Off'
        : `On — long min ${entry.rsiLongMin ?? 55}, short max ${entry.rsiShortMax ?? 45}`,
    });
    rows.push({
      section: 'Entry filters',
      label: 'EMA trend',
      value: entry.emaEnabled === false
        ? 'Off'
        : `On — period ${entry.emaPeriod ?? 20}`,
    });
    rows.push({
      section: 'Entry filters',
      label: 'Funding rate cap',
      value: entry.fundingRateEnabled === false
        ? 'Off'
        : `On — max ${entry.maxFundingRate ?? 0.0005}`,
    });
    rows.push({
      section: 'Entry filters',
      label: 'Peak/trough timing',
      value: onOff(entry.peakTroughTimingEnabled),
    });
  }

  const marketGates = config.marketGates ?? {};
  rows.push({
    section: 'Market gates',
    label: 'Participation gate',
    value: marketGates.participationEnabled
      ? `On — min ${pctRate(marketGates.minParticipationRate ?? 0.25)}`
      : 'Off',
  });
  rows.push({
    section: 'Market gates',
    label: '₿ Major coin participation',
    value: marketGates.majorCoinParticipationEnabled
      ? `On — min ${pctRate(marketGates.minMajorCoinParticipationRate ?? 0.25)}`
      : 'Off',
  });

  const oracle = config.oracle ?? {};
  rows.push({
    section: 'Coin selection oracle',
    label: 'Oracle ranking',
    value: oracle.enabled
      ? `On — min confidence ${oracle.confidenceThreshold ?? 0.5}, shortlist ${oracle.shortlistSize ?? 20}`
      : 'Off',
  });

  const microReversal = config.microReversal ?? {};
  rows.push({
    section: 'Micro-reversal',
    label: 'BTC 5m micro-reversal block',
    value: microReversal.enabled
      ? `On — drop ${microReversal.minDropPercent ?? -0.5}% to ${microReversal.maxDropPercent ?? -0.1}%, lookback ${microReversal.lookbackCandles ?? 12} candles`
      : 'Off',
  });

  const volatility = config.volatility ?? {};
  rows.push({
    section: 'Volatility band',
    label: 'Meme-market volatility band',
    value: volatility.enabled
      ? `On — min ${volatility.minMarketVolatility ?? 'any'}%, max ${volatility.maxMarketVolatility ?? 'any'}%`
      : 'Off',
  });

  const btcDump = config.btcDump ?? {};
  rows.push({
    section: 'BTC dump protection',
    label: 'Pause after BTC drop',
    value: btcDump.enabled
      ? `On — threshold ${btcDump.dumpThreshold ?? 1.5}%, cooldown ${btcDump.cooldownMinutes ?? 20} min`
      : 'Off',
  });

  const lossCooldown = config.lossCooldown ?? {};
  rows.push({
    section: 'Consecutive loss cooldown',
    label: 'Pause after consecutive losses',
    value: lossCooldown.enabled
      ? `On — ${lossCooldown.consecutiveLosses ?? 2} losses in ${lossCooldown.lossWindowHours ?? 24}h → cooldown ${lossCooldown.cooldownHours ?? 24}h`
      : 'Off',
  });

  const regime = config.regime ?? {};
  rows.push({
    section: 'Regime gates',
    label: 'Block opens in low participation',
    value: onOff(regime.blockOpensInLowParticipation),
  });
  rows.push({
    section: 'Regime gates',
    label: 'Block opens in bearish BTC',
    value: onOff(regime.blockOpensInBearishBtc),
  });
  rows.push({
    section: 'Regime gates',
    label: 'Block long legs in bearish BTC',
    value: onOff(regime.blockLongInBearishBtc),
  });
  rows.push({
    section: 'Regime gates',
    label: 'Block short legs unless BTC bearish',
    value: onOff(regime.blockShortUnlessBearishBtc),
  });

  return rows;
}

function summarizeFilterProfileBrief(config?: FilterProfileConfig | null): string {
  if (!config) return 'No configuration';
  const tags: string[] = [];
  const entry = config.entry;
  if (entry?.enabled) {
    const parts: string[] = [];
    if (entry.rsiEnabled !== false) parts.push('RSI');
    if (entry.emaEnabled !== false) parts.push('EMA');
    if (entry.fundingRateEnabled !== false) parts.push('Funding');
    if (entry.peakTroughTimingEnabled) parts.push('Peak/trough');
    if (parts.length > 0) tags.push(`Entry: ${parts.join(', ')}`);
  }
  if (config.marketGates?.participationEnabled) tags.push('Meme participation');
  if (config.marketGates?.majorCoinParticipationEnabled) tags.push('Major coin participation');
  if (config.oracle?.enabled) tags.push('Oracle ranking');
  if (config.microReversal?.enabled) tags.push('Micro-reversal');
  if (config.volatility?.enabled) tags.push('Volatility band');
  if (config.btcDump?.enabled) tags.push('BTC dump protection');
  if (config.lossCooldown?.enabled) tags.push('Loss cooldown');
  const regime = config.regime;
  if (regime?.blockOpensInLowParticipation || regime?.blockOpensInBearishBtc || regime?.blockLongInBearishBtc || regime?.blockShortUnlessBearishBtc) {
    tags.push('Regime gates');
  }
  return tags.length > 0 ? tags.join(' · ') : 'No filters enabled';
}

function FilterProfileConfigDetails({
  config,
  compact = false,
}: {
  config?: FilterProfileConfig | null;
  compact?: boolean;
}) {
  const rows = describeFilterProfileConfigDetails(config);
  const sections = Array.from(new Set(rows.map(row => row.section)));

  return (
    <div
      style={{
        marginTop: compact ? 8 : 10,
        padding: compact ? '10px 12px' : '12px 14px',
        borderRadius: 8,
        border: '1px solid #e2e8f0',
        background: '#fff',
      }}
    >
      {sections.map(section => (
        <div key={section} style={{ marginBottom: section === sections[sections.length - 1] ? 0 : 12 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6 }}>
            {section}
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {rows.filter(row => row.section === section).map(row => (
              <div
                key={`${section}-${row.label}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(140px, 42%) 1fr',
                  gap: '4px 12px',
                  fontSize: 12,
                  lineHeight: 1.45,
                }}
              >
                <span style={{ color: '#64748b' }}>{row.label}</span>
                <span style={{ color: '#1e293b', fontWeight: 600 }}>{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

interface FilterProfileEditorDraft {
  name: string;
  description: string;
  config: FilterProfileConfig;
}

function defaultFilterProfileConfig(): FilterProfileConfig {
  return {
    schemaVersion: 1,
    entry: {
      enabled: true,
      fundingRateEnabled: true,
      maxFundingRate: 0.0005,
      rsiEnabled: true,
      rsiLongMin: 55,
      rsiShortMax: 45,
      emaEnabled: true,
      emaPeriod: 20,
      peakTroughTimingEnabled: false,
    },
    marketGates: {
      participationEnabled: false,
      minParticipationRate: 0.25,
      majorCoinParticipationEnabled: false,
      minMajorCoinParticipationRate: 0.25,
    },
    regime: {
      blockOpensInLowParticipation: false,
      blockOpensInBearishBtc: false,
      blockLongInBearishBtc: false,
      blockShortUnlessBearishBtc: false,
    },
    microReversal: {
      enabled: false,
      minDropPercent: -0.5,
      maxDropPercent: -0.1,
      lookbackCandles: 12,
    },
    volatility: {
      enabled: false,
      minMarketVolatility: undefined,
      maxMarketVolatility: undefined,
    },
    btcDump: {
      enabled: false,
      dumpThreshold: 1.5,
      cooldownMinutes: 20,
    },
    lossCooldown: {
      enabled: false,
      consecutiveLosses: 2,
      lossWindowHours: 24,
      cooldownHours: 24,
    },
    oracle: {
      enabled: false,
      confidenceThreshold: 0.5,
      weightMomentum: null,
      weightRsi: null,
      weightVolume: null,
      weightFunding: null,
      weightEma: null,
      shortlistSize: 20,
    },
  };
}

function cloneFilterProfileConfig(config?: FilterProfileConfig | null): FilterProfileConfig {
  return JSON.parse(JSON.stringify(config ?? defaultFilterProfileConfig())) as FilterProfileConfig;
}

function emptyFilterProfileEditorDraft(): FilterProfileEditorDraft {
  return { name: '', description: '', config: defaultFilterProfileConfig() };
}

const filterEditorSectionStyle: React.CSSProperties = {
  padding: '12px 14px',
  border: '1px solid #e2e8f0',
  borderRadius: 10,
  background: '#f8fafc',
};

const filterEditorCheckLabel: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 13,
  color: '#334155',
  cursor: 'pointer',
};

function FilterProfileEditorForm({
  draft,
  onChange,
  disabled,
}: {
  draft: FilterProfileEditorDraft;
  onChange: (draft: FilterProfileEditorDraft) => void;
  disabled?: boolean;
}) {
  const entry = draft.config.entry ?? {};
  const marketGates = draft.config.marketGates ?? {};
  const regime = draft.config.regime ?? {};
  const microReversal = draft.config.microReversal ?? {};
  const volatility = draft.config.volatility ?? {};
  const btcDump = draft.config.btcDump ?? {};
  const lossCooldown = draft.config.lossCooldown ?? {};
  const oracle = draft.config.oracle ?? {};

  const patchEntry = (patch: Partial<NonNullable<FilterProfileConfig['entry']>>) => {
    onChange({
      ...draft,
      config: {
        ...draft.config,
        entry: { ...entry, ...patch },
      },
    });
  };

  const patchMarketGates = (patch: Partial<NonNullable<FilterProfileConfig['marketGates']>>) => {
    onChange({
      ...draft,
      config: {
        ...draft.config,
        marketGates: { ...marketGates, ...patch },
      },
    });
  };

  const patchRegime = (patch: Partial<NonNullable<FilterProfileConfig['regime']>>) => {
    onChange({
      ...draft,
      config: {
        ...draft.config,
        regime: { ...regime, ...patch },
      },
    });
  };

  const patchMicroReversal = (patch: Partial<NonNullable<FilterProfileConfig['microReversal']>>) => {
    onChange({
      ...draft,
      config: {
        ...draft.config,
        microReversal: { ...microReversal, ...patch },
      },
    });
  };

  const patchVolatility = (patch: Partial<NonNullable<FilterProfileConfig['volatility']>>) => {
    onChange({
      ...draft,
      config: {
        ...draft.config,
        volatility: { ...volatility, ...patch },
      },
    });
  };

  const patchBtcDump = (patch: Partial<NonNullable<FilterProfileConfig['btcDump']>>) => {
    onChange({
      ...draft,
      config: {
        ...draft.config,
        btcDump: { ...btcDump, ...patch },
      },
    });
  };

  const patchLossCooldown = (patch: Partial<NonNullable<FilterProfileConfig['lossCooldown']>>) => {
    onChange({
      ...draft,
      config: {
        ...draft.config,
        lossCooldown: { ...lossCooldown, ...patch },
      },
    });
  };

  const patchOracle = (patch: Partial<NonNullable<FilterProfileConfig['oracle']>>) => {
    onChange({
      ...draft,
      config: {
        ...draft.config,
        oracle: { ...oracle, ...patch },
      },
    });
  };

  const numInputStyle: React.CSSProperties = {
    ...fieldInputStyle,
    width: 88,
    padding: '6px 8px',
    fontSize: 12,
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div>
        <label style={fieldLabelStyle}>Profile name</label>
        <input
          type="text"
          maxLength={120}
          value={draft.name}
          disabled={disabled}
          onChange={e => onChange({ ...draft, name: e.target.value })}
          placeholder="e.g. My gaming entry filters"
          style={fieldInputStyle}
        />
      </div>
      <div>
        <label style={fieldLabelStyle}>Description (optional)</label>
        <input
          type="text"
          maxLength={512}
          value={draft.description}
          disabled={disabled}
          onChange={e => onChange({ ...draft, description: e.target.value })}
          placeholder="Short note for your team"
          style={fieldInputStyle}
        />
      </div>

      <div style={filterEditorSectionStyle}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 10 }}>Entry filters</div>
        <label style={filterEditorCheckLabel}>
          <input
            type="checkbox"
            checked={Boolean(entry.enabled)}
            disabled={disabled}
            onChange={e => patchEntry({ enabled: e.target.checked })}
          />
          Enable technical entry filters
        </label>
        {entry.enabled && (
          <div style={{ display: 'grid', gap: 10, marginTop: 12, paddingLeft: 4 }}>
            <label style={filterEditorCheckLabel}>
              <input
                type="checkbox"
                checked={entry.rsiEnabled !== false}
                disabled={disabled}
                onChange={e => patchEntry({ rsiEnabled: e.target.checked })}
              />
              RSI alignment
            </label>
            {entry.rsiEnabled !== false && (
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', paddingLeft: 24 }}>
                <div>
                  <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Long min RSI</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    disabled={disabled}
                    value={entry.rsiLongMin ?? 55}
                    onChange={e => patchEntry({ rsiLongMin: Number(e.target.value) })}
                    style={numInputStyle}
                  />
                </div>
                <div>
                  <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Short max RSI</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    disabled={disabled}
                    value={entry.rsiShortMax ?? 45}
                    onChange={e => patchEntry({ rsiShortMax: Number(e.target.value) })}
                    style={numInputStyle}
                  />
                </div>
              </div>
            )}
            <label style={filterEditorCheckLabel}>
              <input
                type="checkbox"
                checked={entry.emaEnabled !== false}
                disabled={disabled}
                onChange={e => patchEntry({ emaEnabled: e.target.checked })}
              />
              EMA trend
            </label>
            {entry.emaEnabled !== false && (
              <div style={{ paddingLeft: 24 }}>
                <label style={{ ...fieldLabelStyle, fontSize: 11 }}>EMA period</label>
                <input
                  type="number"
                  min={2}
                  max={200}
                  step={1}
                  disabled={disabled}
                  value={entry.emaPeriod ?? 20}
                  onChange={e => patchEntry({ emaPeriod: Number(e.target.value) })}
                  style={numInputStyle}
                />
              </div>
            )}
            <label style={filterEditorCheckLabel}>
              <input
                type="checkbox"
                checked={entry.fundingRateEnabled !== false}
                disabled={disabled}
                onChange={e => patchEntry({ fundingRateEnabled: e.target.checked })}
              />
              Funding rate cap
            </label>
            {entry.fundingRateEnabled !== false && (
              <div style={{ paddingLeft: 24 }}>
                <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Max funding rate</label>
                <input
                  type="number"
                  min={0}
                  step={0.0001}
                  disabled={disabled}
                  value={entry.maxFundingRate ?? 0.0005}
                  onChange={e => patchEntry({ maxFundingRate: Number(e.target.value) })}
                  style={{ ...numInputStyle, width: 120 }}
                />
              </div>
            )}
            <label style={filterEditorCheckLabel}>
              <input
                type="checkbox"
                checked={Boolean(entry.peakTroughTimingEnabled)}
                disabled={disabled}
                onChange={e => patchEntry({ peakTroughTimingEnabled: e.target.checked })}
              />
              Peak/trough timing (wait for better entry)
            </label>
          </div>
        )}
      </div>

      <div style={filterEditorSectionStyle}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 10 }}>Market gates</div>
        <label style={filterEditorCheckLabel}>
          <input
            type="checkbox"
            checked={Boolean(marketGates.participationEnabled)}
            disabled={disabled}
            onChange={e => patchMarketGates({ participationEnabled: e.target.checked })}
          />
          Participation gate (meme sector activity)
        </label>
        {marketGates.participationEnabled && (
          <div style={{ marginTop: 10, paddingLeft: 24 }}>
            <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Min participation rate</label>
            <input
              type="number"
              min={0}
              max={1}
              step={0.01}
              disabled={disabled}
              value={marketGates.minParticipationRate ?? 0.25}
              onChange={e => patchMarketGates({ minParticipationRate: Number(e.target.value) })}
              style={{ ...numInputStyle, width: 100 }}
            />
          </div>
        )}
        <div
          style={{
            marginTop: 14,
            padding: '12px 14px',
            borderRadius: 8,
            border: `1px solid ${marketGates.majorCoinParticipationEnabled ? '#a5f3fc' : '#e2e8f0'}`,
            background: marketGates.majorCoinParticipationEnabled ? '#f0fdff' : '#f8fafc',
          }}
        >
          <label style={{ ...filterEditorCheckLabel, marginBottom: 6 }}>
            <input
              type="checkbox"
              checked={Boolean(marketGates.majorCoinParticipationEnabled)}
              disabled={disabled}
              onChange={e => patchMarketGates({ majorCoinParticipationEnabled: e.target.checked })}
            />
            ₿ Major Coin Participation Filter
          </label>
          <p style={{ margin: '0 0 8px 24px', fontSize: 11, color: '#64748b', lineHeight: 1.45 }}>
            Blocks new legs when top major coins (TOP 100 futures universe) have low participation — weak
            follow-through (positive 24h change + above-median volume). Same logic as crypto trading bots.
          </p>
          {marketGates.majorCoinParticipationEnabled ? (
            <div style={{ paddingLeft: 24 }}>
              <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Min active major coins</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <input
                  type="number"
                  min={5}
                  max={100}
                  step={1}
                  disabled={disabled}
                  value={Math.round((marketGates.minMajorCoinParticipationRate ?? 0.25) * 100)}
                  onChange={e => {
                    const pct = Number(e.target.value);
                    if (!Number.isFinite(pct)) return;
                    patchMarketGates({ minMajorCoinParticipationRate: Math.max(0.05, Math.min(1, pct / 100)) });
                  }}
                  style={{ ...numInputStyle, width: 72 }}
                />
                <span style={{ fontSize: 12, color: '#64748b' }}>% of universe must be active</span>
              </div>
            </div>
          ) : (
            <p style={{ margin: '4px 0 0 24px', fontSize: 11, color: '#94a3b8' }}>
              Disabled — legs open regardless of major-coin participation.
            </p>
          )}
        </div>
      </div>

      <div
        style={{
          ...filterEditorSectionStyle,
          border: `1px solid ${oracle.enabled ? '#93c5fd' : '#e2e8f0'}`,
          background: oracle.enabled ? '#eff6ff' : '#f8fafc',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#1d4ed8' }}>Coin Selection Oracle</div>
            <p style={{ margin: '4px 0 0', fontSize: 11, color: '#64748b', lineHeight: 1.45, maxWidth: 520 }}>
              Scores coins in the bot&apos;s sector on RSI, momentum, volume, funding rate &amp; EMA — re-ranks
              candidates before opening legs. Falls back to activity ranking if confidence is too low.
            </p>
          </div>
          <label style={{ ...filterEditorCheckLabel, flexShrink: 0, color: oracle.enabled ? '#1d4ed8' : '#64748b' }}>
            <input
              type="checkbox"
              checked={Boolean(oracle.enabled)}
              disabled={disabled}
              onChange={e => patchOracle({ enabled: e.target.checked })}
            />
            {oracle.enabled ? 'Enabled' : 'Disabled'}
          </label>
        </div>
        <div style={{ opacity: oracle.enabled ? 1 : 0.45, pointerEvents: oracle.enabled ? 'auto' : 'none' }}>
          <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Min confidence threshold (0.0 – 1.0)</label>
          <input
            type="number"
            step={0.05}
            min={0.1}
            max={1}
            disabled={disabled}
            value={oracle.confidenceThreshold ?? 0.5}
            onChange={e => {
              const v = Number(e.target.value);
              if (Number.isFinite(v) && v >= 0 && v <= 1) patchOracle({ confidenceThreshold: v });
            }}
            style={{ ...fieldInputStyle, maxWidth: 120, marginTop: 4 }}
          />
          <p style={{ margin: '4px 0 12px', fontSize: 11, color: '#64748b' }}>
            Below this top side score, activity ranking is used instead. Default: 0.50
          </p>
          <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Shortlist size (activity-ranked coins to score)</label>
          <input
            type="number"
            min={2}
            max={40}
            step={1}
            disabled={disabled}
            value={oracle.shortlistSize ?? 20}
            onChange={e => {
              const n = Number(e.target.value);
              if (Number.isFinite(n)) patchOracle({ shortlistSize: Math.max(2, Math.min(40, n)) });
            }}
            style={{ ...numInputStyle, width: 72, marginTop: 4 }}
          />
          <p style={{ margin: '12px 0 8px', fontSize: 12, fontWeight: 600, color: '#1e3a8a' }}>
            Signal weights <span style={{ fontWeight: 400, color: '#64748b' }}>(blank = global defaults)</span>
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {(
              [
                { key: 'weightMomentum' as const, label: 'Momentum', def: 0.25 },
                { key: 'weightRsi' as const, label: 'RSI', def: 0.3 },
                { key: 'weightVolume' as const, label: 'Volume', def: 0.2 },
                { key: 'weightFunding' as const, label: 'Funding rate', def: 0.15 },
                { key: 'weightEma' as const, label: 'EMA trend', def: 0.1 },
              ] as const
            ).map(({ key, label, def }) => (
              <div key={key}>
                <label style={{ ...fieldLabelStyle, fontSize: 11 }}>{label}</label>
                <input
                  type="number"
                  step={0.05}
                  min={0}
                  max={1}
                  disabled={disabled}
                  placeholder={`Global (${def})`}
                  value={oracle[key] ?? ''}
                  onChange={e => {
                    const raw = e.target.value;
                    if (raw === '') {
                      patchOracle({ [key]: null });
                      return;
                    }
                    const v = Number(raw);
                    if (Number.isFinite(v) && v >= 0 && v <= 1) patchOracle({ [key]: v });
                  }}
                  style={{ ...numInputStyle, width: '100%' }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={filterEditorSectionStyle}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 10 }}>Micro-reversal (BTC 5m)</div>
        <label style={filterEditorCheckLabel}>
          <input
            type="checkbox"
            checked={Boolean(microReversal.enabled)}
            disabled={disabled}
            onChange={e => patchMicroReversal({ enabled: e.target.checked })}
          />
          Block entries in BTC micro-reversal window
        </label>
        {microReversal.enabled && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10, paddingLeft: 4 }}>
            <div>
              <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Min drop %</label>
              <input
                type="number"
                step={0.01}
                disabled={disabled}
                value={microReversal.minDropPercent ?? -0.5}
                onChange={e => patchMicroReversal({ minDropPercent: Number(e.target.value) })}
                style={numInputStyle}
              />
            </div>
            <div>
              <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Max drop %</label>
              <input
                type="number"
                step={0.01}
                disabled={disabled}
                value={microReversal.maxDropPercent ?? -0.1}
                onChange={e => patchMicroReversal({ maxDropPercent: Number(e.target.value) })}
                style={numInputStyle}
              />
            </div>
            <div>
              <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Lookback candles</label>
              <input
                type="number"
                min={3}
                max={60}
                step={1}
                disabled={disabled}
                value={microReversal.lookbackCandles ?? 12}
                onChange={e => patchMicroReversal({ lookbackCandles: Number(e.target.value) })}
                style={numInputStyle}
              />
            </div>
          </div>
        )}
      </div>

      <div style={filterEditorSectionStyle}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 10 }}>Volatility band</div>
        <label style={filterEditorCheckLabel}>
          <input
            type="checkbox"
            checked={Boolean(volatility.enabled)}
            disabled={disabled}
            onChange={e => patchVolatility({ enabled: e.target.checked })}
          />
          Require meme-market volatility within min/max %
        </label>
        {volatility.enabled && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10, paddingLeft: 4 }}>
            <div>
              <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Min % (optional)</label>
              <input
                type="number"
                min={0}
                step={0.1}
                disabled={disabled}
                value={volatility.minMarketVolatility ?? ''}
                onChange={e => patchVolatility({
                  minMarketVolatility: e.target.value === '' ? undefined : Number(e.target.value),
                })}
                style={numInputStyle}
              />
            </div>
            <div>
              <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Max % (optional)</label>
              <input
                type="number"
                min={0}
                step={0.1}
                disabled={disabled}
                value={volatility.maxMarketVolatility ?? ''}
                onChange={e => patchVolatility({
                  maxMarketVolatility: e.target.value === '' ? undefined : Number(e.target.value),
                })}
                style={numInputStyle}
              />
            </div>
          </div>
        )}
      </div>

      <div style={filterEditorSectionStyle}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 10 }}>BTC dump protection</div>
        <label style={filterEditorCheckLabel}>
          <input
            type="checkbox"
            checked={Boolean(btcDump.enabled)}
            disabled={disabled}
            onChange={e => patchBtcDump({ enabled: e.target.checked })}
          />
          Pause opens after sharp BTC drop (10m)
        </label>
        {btcDump.enabled && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10, paddingLeft: 4 }}>
            <div>
              <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Dump threshold %</label>
              <input
                type="number"
                min={0.1}
                step={0.1}
                disabled={disabled}
                value={btcDump.dumpThreshold ?? 1.5}
                onChange={e => patchBtcDump({ dumpThreshold: Number(e.target.value) })}
                style={numInputStyle}
              />
            </div>
            <div>
              <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Cooldown minutes</label>
              <input
                type="number"
                min={1}
                max={240}
                step={1}
                disabled={disabled}
                value={btcDump.cooldownMinutes ?? 20}
                onChange={e => patchBtcDump({ cooldownMinutes: Number(e.target.value) })}
                style={numInputStyle}
              />
            </div>
          </div>
        )}
      </div>

      <div style={filterEditorSectionStyle}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 10 }}>
          Consecutive loss cooldown
        </div>
        <p style={{ margin: '0 0 10px', fontSize: 12, color: '#64748b', lineHeight: 1.45 }}>
          Reactive risk gate (not an entry filter). After consecutive losing closes within the window,
          the bot pauses new opens for the cooldown period — even if other filters would allow entry.
        </p>
        <label style={filterEditorCheckLabel}>
          <input
            type="checkbox"
            checked={Boolean(lossCooldown.enabled)}
            disabled={disabled}
            onChange={e => patchLossCooldown({ enabled: e.target.checked })}
          />
          Pause opens after consecutive losses
        </label>
        {lossCooldown.enabled && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10, paddingLeft: 4 }}>
            <div>
              <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Consecutive losses</label>
              <input
                type="number"
                min={2}
                max={10}
                step={1}
                disabled={disabled}
                value={lossCooldown.consecutiveLosses ?? 2}
                onChange={e => patchLossCooldown({ consecutiveLosses: Number(e.target.value) })}
                style={numInputStyle}
              />
            </div>
            <div>
              <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Loss window (hours)</label>
              <input
                type="number"
                min={1}
                max={168}
                step={1}
                disabled={disabled}
                value={lossCooldown.lossWindowHours ?? 24}
                onChange={e => patchLossCooldown({ lossWindowHours: Number(e.target.value) })}
                style={numInputStyle}
              />
            </div>
            <div>
              <label style={{ ...fieldLabelStyle, fontSize: 11 }}>Cooldown (hours)</label>
              <input
                type="number"
                min={1}
                max={168}
                step={1}
                disabled={disabled}
                value={lossCooldown.cooldownHours ?? 24}
                onChange={e => patchLossCooldown({ cooldownHours: Number(e.target.value) })}
                style={numInputStyle}
              />
            </div>
          </div>
        )}
      </div>

      <div style={filterEditorSectionStyle}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 10 }}>Regime gates</div>
        <div style={{ display: 'grid', gap: 8 }}>
          <label style={filterEditorCheckLabel}>
            <input
              type="checkbox"
              checked={Boolean(regime.blockOpensInLowParticipation)}
              disabled={disabled}
              onChange={e => patchRegime({ blockOpensInLowParticipation: e.target.checked })}
            />
            Block opens in low participation
          </label>
          <label style={filterEditorCheckLabel}>
            <input
              type="checkbox"
              checked={Boolean(regime.blockOpensInBearishBtc)}
              disabled={disabled}
              onChange={e => patchRegime({ blockOpensInBearishBtc: e.target.checked })}
            />
            Block opens in bearish BTC regime
          </label>
          <label style={filterEditorCheckLabel}>
            <input
              type="checkbox"
              checked={Boolean(regime.blockLongInBearishBtc)}
              disabled={disabled}
              onChange={e => patchRegime({ blockLongInBearishBtc: e.target.checked })}
            />
            Block long legs in bearish BTC (shorts still allowed)
          </label>
          <label style={filterEditorCheckLabel}>
            <input
              type="checkbox"
              checked={Boolean(regime.blockShortUnlessBearishBtc)}
              disabled={disabled}
              onChange={e => patchRegime({ blockShortUnlessBearishBtc: e.target.checked })}
            />
            Block short legs unless BTC is bearish (longs still allowed)
          </label>
        </div>
      </div>
    </div>
  );
}

function ActivityBotSafeguardsPanel({ bot }: { bot: CryptoPortfolioActivityBot }) {
  const cellLabel: React.CSSProperties = {
    fontSize: 10,
    fontWeight: 700,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    marginBottom: 2,
  };
  const cellValue: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    color: '#334155',
  };
  const items = [
    { label: 'Entry mode', value: formatBotEntryMode(bot) },
    ...(bot.filterProfileName
      ? [{ label: 'Filter profile', value: bot.filterProfileName }]
      : []),
    { label: 'Target profit', value: `+${Number(bot.targetProfitPct).toFixed(1)}%` },
    { label: 'Stop loss (per leg)', value: formatBotStopLoss(bot) },
    { label: 'Trailing stop', value: formatBotTrailingStop(bot) },
    { label: 'Max bot loss (USD)', value: formatBotMaxLossUsd(bot) },
    { label: 'Max bot loss (%)', value: formatBotMaxLossPct(bot) },
  ];

  return (
    <div
      style={{
        marginTop: 10,
        padding: '10px 12px',
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 8 }}>
        Exit safeguards
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '10px 16px',
        }}
      >
        {items.map(item => (
          <div key={item.label}>
            <div style={cellLabel}>{item.label}</div>
            <div style={cellValue}>{item.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function activityBotCardSubtitle(bot: CryptoPortfolioActivityBot): string | null {
  const ref = bot.referenceName?.trim();
  if (!ref) return null;
  return `${bot.sectorLabel || bot.sectorId} · ${bot.kucoinAccountName || 'KuCoin'}`;
}

function activityBotStatusColor(status: string): string {
  if (status === 'RUNNING') return '#15803d';
  if (status === 'PAUSED') return '#b45309';
  if (status === 'FILTER_WAIT') return '#b45309';
  if (status === 'MONITORING') return '#7e22ce';
  if (status === 'WAITING') return '#0369a1';
  if (status === 'COMPLETED') return '#086afb';
  return '#64748b';
}

/** Short badge label from lastError — restores pre-WAITING UX (reason in Status). */
function activityBotShortReasonLabel(lastError: string): string {
  const t = lastError.trim();
  if (!t) return 'FILTER WAIT';
  if (/micro-?reversal/i.test(t)) return 'MICRO-REVERSAL';
  if (/ENTRY_BLOCK|Entry blocked/i.test(t)) return 'ENTRY BLOCKED';
  if (/major.?coin.?participation|Major coin/i.test(t)) return 'MAJOR COIN GATE';
  if (/participation/i.test(t)) return 'LOW PARTICIPATION';
  if (/BTC dump|dump protection|dump threshold/i.test(t)) return 'BTC DUMP';
  if (/Loss cooldown|consecutive loss/i.test(t)) return 'LOSS COOLDOWN';
  if (/peak\/trough|peak-trough/i.test(t)) return 'PEAK/TROUGH WAIT';
  if (/Waiting for entry filter/i.test(t)) return 'FILTER WAIT';
  if (/volatility/i.test(t)) return 'VOLATILITY GATE';
  if (/Macro BTC bridge/i.test(t)) return 'MACRO BTC GATE';
  if (/AI regime gate|long blocked by AI/i.test(t)) return 'AI LONG BLOCK';
  if (/bearish|regime gate|regime=/i.test(t)) return 'REGIME GATE';
  if (/No tradeable side|long-only so no trade/i.test(t)) return 'SIDE GATE';
  if (/KuCoin account .+ still has OPEN|restart deferred|account .+ OPEN legs/i.test(t)) {
    return 'ACCOUNT BUSY';
  }
  if (/No eligible/i.test(t)) return 'NO CANDIDATES';
  return t.length > 22 ? `${t.slice(0, 20)}…` : t;
}

/**
 * Backend status is cycle-phase (opening entries), not “has live trades”.
 * COMPLETED means this cycle finished allocating legs — open positions may still be managed.
 * When lastError is set on the opening path, Status shows that reason (not a generic WAITING).
 */
function activityBotDisplayStatus(bot: CryptoPortfolioActivityBot): {
  label: string;
  tone: string;
  hint: string | null;
} {
  const openCount = bot.openLegs?.length ?? 0;
  const reason = (bot.lastError || '').trim();

  if (bot.status === 'PAUSED') {
    return {
      label: 'PAUSED',
      tone: 'PAUSED',
      hint: openCount > 0 ? `${openCount} open leg${openCount === 1 ? '' : 's'} still managed` : null,
    };
  }

  // Prefer filter / entry reason in Status (same as before generic WAITING label).
  if (bot.status === 'RUNNING' && reason) {
    return {
      label: activityBotShortReasonLabel(reason),
      tone: 'FILTER_WAIT',
      hint: reason,
    };
  }
  if (bot.status === 'RUNNING') {
    return {
      label: 'RUNNING',
      tone: 'RUNNING',
      hint: 'Hunting new entries this cycle',
    };
  }
  if (bot.status === 'COMPLETED' && openCount > 0) {
    return {
      label: 'MONITORING',
      tone: 'MONITORING',
      hint: bot.continuousRun
        ? 'Cycle filled — managing open legs; restarts when they close'
        : 'Cycle filled — managing open legs until exit',
    };
  }
  // Continuous between cycles — still surface lastError if present (e.g. deferred restart).
  if (bot.status === 'COMPLETED' && bot.continuousRun && reason) {
    return {
      label: activityBotShortReasonLabel(reason),
      tone: 'FILTER_WAIT',
      hint: reason,
    };
  }
  if (bot.status === 'COMPLETED' && bot.continuousRun) {
    return {
      label: 'BETWEEN CYCLES',
      tone: 'WAITING',
      hint: 'Continuous idle — not blocked by a filter; next cycle starts automatically when the runner ticks',
    };
  }
  if (bot.status === 'COMPLETED') {
    return {
      label: 'COMPLETED',
      tone: 'COMPLETED',
      hint: 'Cycle finished — no open legs',
    };
  }
  return { label: bot.status, tone: bot.status, hint: null };
}

function activityBotStatusSuffix(bot: CryptoPortfolioActivityBot): string {
  if (bot.continuousRun && bot.status === 'COMPLETED' && (bot.openLegs?.length ?? 0) > 0) {
    return ' · restarts when open legs close';
  }
  if (bot.continuousRun) {
    return ' · continuous';
  }
  return '';
}

function ActivityBotStatusBadge({ bot }: { bot: CryptoPortfolioActivityBot }) {
  const display = activityBotDisplayStatus(bot);
  const color = activityBotStatusColor(display.tone);
  const bg = display.tone === 'RUNNING'
    ? '#dcfce7'
    : display.tone === 'PAUSED' || display.tone === 'FILTER_WAIT'
      ? '#fef3c7'
      : display.tone === 'MONITORING'
        ? '#f3e8ff'
        : display.tone === 'WAITING'
          ? '#e0f2fe'
          : display.tone === 'COMPLETED'
            ? '#dbeafe'
            : '#f1f5f9';
  return (
    <span
      title={display.hint || undefined}
      style={{
        fontSize: 10,
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: 999,
        background: bg,
        color,
        whiteSpace: 'nowrap',
        maxWidth: 160,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {display.label}
    </span>
  );
}

function ActivityBotAiBadge({ bot }: { bot: CryptoPortfolioActivityBot }) {
  if (bot.aiAutoTunePending) {
    return (
      <span style={{ fontSize: 11, fontWeight: 700, color: '#b45309' }} title="AI tune pending approval">
        ⏳ Pending
      </span>
    );
  }
  if (bot.aiAutoTuneEnabled) {
    return (
      <span style={{ fontSize: 11, fontWeight: 700, color: '#047857' }} title="AI auto-tune enabled">
        AI On
      </span>
    );
  }
  return (
    <span style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8' }} title="AI auto-tune off">
      AI Off
    </span>
  );
}

type ActivityBotRowHandlers = {
  canManage: boolean;
  saving: boolean;
  cloningBotId: number | null;
  togglingContinuousId: number | null;
  logsLoading: boolean;
  logsBotId: number | null;
  onStart: (botId: number) => void;
  onRerun: (botId: number) => void;
  onPause: (botId: number) => void;
  onEdit: (bot: CryptoPortfolioActivityBot) => void;
  onClone: (bot: CryptoPortfolioActivityBot) => void;
  onExport: (bot: CryptoPortfolioActivityBot) => void;
  onExportHistory: (bot: CryptoPortfolioActivityBot) => void;
  historyExportingBotId: number | null;
  onDelete: (botId: number) => void;
  onAiTune: (bot: CryptoPortfolioActivityBot) => void;
  onLogs: (bot: CryptoPortfolioActivityBot) => void;
  onContinuousToggle: (bot: CryptoPortfolioActivityBot, next: boolean) => void;
  onToggleHistory: (botId: number) => void;
  expandedHistoryBotId: number | null;
  historyLoadingId: number | null;
  historyErrorByBotId: Record<number, string>;
  historyByBotId: Record<number, ActivityBotPositionHistoryRow[]>;
};

const activityBotActionBtnBase: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
};

function ActivityBotPrimaryAction({
  bot,
  handlers,
  compact,
}: {
  bot: CryptoPortfolioActivityBot;
  handlers: ActivityBotRowHandlers;
  compact?: boolean;
}) {
  if (!handlers.canManage) return null;
  const disabled = handlers.saving;
  const pad = compact ? '4px 10px' : activityBotActionBtnBase.padding;
  if (bot.status === 'RUNNING') {
    return (
      <button
        type="button"
        onClick={e => { e.stopPropagation(); handlers.onPause(bot.id); }}
        disabled={disabled}
        style={{ ...activityBotActionBtnBase, padding: pad, border: 'none', background: '#fbb445', color: '#1e293b', fontWeight: 700 }}
      >
        Pause
      </button>
    );
  }
  if (bot.status === 'COMPLETED') {
    return (
      <button
        type="button"
        onClick={e => { e.stopPropagation(); handlers.onRerun(bot.id); }}
        disabled={disabled}
        style={{ ...activityBotActionBtnBase, padding: pad, border: 'none', background: '#0dca9d', color: '#fff', fontWeight: 700 }}
      >
        Re-run
      </button>
    );
  }
  if (bot.status !== 'RUNNING') {
    return (
      <button
        type="button"
        onClick={e => { e.stopPropagation(); handlers.onStart(bot.id); }}
        disabled={disabled}
        style={{ ...activityBotActionBtnBase, padding: pad, border: 'none', background: '#0dca9d', color: '#fff', fontWeight: 700 }}
      >
        Start
      </button>
    );
  }
  return null;
}

function ActivityBotMoreMenu({
  bot,
  handlers,
}: {
  bot: CryptoPortfolioActivityBot;
  handlers: ActivityBotRowHandlers;
}) {
  const [open, setOpen] = useState(false);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({ visibility: 'hidden' });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const updateMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const menuWidth = 168;
    const menuHeight = menuRef.current?.offsetHeight ?? 220;
    const gap = 4;
    const pad = 8;

    let left = rect.right - menuWidth;
    let top = rect.bottom + gap;

    if (left < pad) left = rect.left;
    if (left + menuWidth > window.innerWidth - pad) {
      left = Math.max(pad, window.innerWidth - menuWidth - pad);
    }
    if (top + menuHeight > window.innerHeight - pad) {
      top = rect.top - menuHeight - gap;
    }

    setMenuStyle({
      position: 'fixed',
      left,
      top,
      minWidth: menuWidth,
      background: '#fff',
      border: '1px solid #e2e8f0',
      borderRadius: 8,
      boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
      zIndex: 10000,
      overflow: 'hidden',
      visibility: 'visible',
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onLayoutChange = () => updateMenuPosition();
    window.addEventListener('resize', onLayoutChange);
    window.addEventListener('scroll', onLayoutChange, true);
    return () => {
      window.removeEventListener('resize', onLayoutChange);
      window.removeEventListener('scroll', onLayoutChange, true);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
      setHoveredKey(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  if (!handlers.canManage) return null;

  const menuItemStyle = (key: string, danger = false): React.CSSProperties => ({
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '8px 12px',
    border: 'none',
    background: hoveredKey === key ? (danger ? '#fef2f2' : '#f1f5f9') : 'transparent',
    fontSize: 12,
    fontWeight: 600,
    color: danger ? '#b91c1c' : '#334155',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'background 0.12s ease',
  });

  const closeMenu = () => {
    setOpen(false);
    setHoveredKey(null);
  };

  const menu = open
    ? createPortal(
        <div ref={menuRef} role="menu" style={menuStyle} onClick={e => e.stopPropagation()}>
          <button
            type="button"
            role="menuitem"
            style={menuItemStyle('tune')}
            onMouseEnter={() => setHoveredKey('tune')}
            onMouseLeave={() => setHoveredKey(null)}
            onClick={() => { closeMenu(); handlers.onAiTune(bot); }}
          >
            {activityBotAiTuneButtonLabel(bot)}
          </button>
          <button
            type="button"
            role="menuitem"
            style={menuItemStyle('log')}
            disabled={handlers.logsLoading && handlers.logsBotId === bot.id}
            onMouseEnter={() => setHoveredKey('log')}
            onMouseLeave={() => setHoveredKey(null)}
            onClick={() => { closeMenu(); handlers.onLogs(bot); }}
          >
            Log
          </button>
          {bot.status !== 'RUNNING' && (
            <button
              type="button"
              role="menuitem"
              style={menuItemStyle('edit')}
              onMouseEnter={() => setHoveredKey('edit')}
              onMouseLeave={() => setHoveredKey(null)}
              onClick={() => { closeMenu(); handlers.onEdit(bot); }}
            >
              Edit
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            style={menuItemStyle('clone')}
            disabled={handlers.cloningBotId === bot.id}
            onMouseEnter={() => setHoveredKey('clone')}
            onMouseLeave={() => setHoveredKey(null)}
            onClick={() => { closeMenu(); handlers.onClone(bot); }}
          >
            {handlers.cloningBotId === bot.id ? 'Cloning…' : 'Clone'}
          </button>
          <button
            type="button"
            role="menuitem"
            style={menuItemStyle('export')}
            onMouseEnter={() => setHoveredKey('export')}
            onMouseLeave={() => setHoveredKey(null)}
            onClick={() => { closeMenu(); handlers.onExport(bot); }}
          >
            Export JSON
          </button>
          <button
            type="button"
            role="menuitem"
            style={menuItemStyle('exportHistory')}
            disabled={handlers.historyExportingBotId === bot.id}
            onMouseEnter={() => setHoveredKey('exportHistory')}
            onMouseLeave={() => setHoveredKey(null)}
            onClick={() => { closeMenu(); handlers.onExportHistory(bot); }}
          >
            {handlers.historyExportingBotId === bot.id ? 'Exporting history…' : 'Export history CSV'}
          </button>
          {bot.status !== 'RUNNING' && (
            <button
              type="button"
              role="menuitem"
              style={menuItemStyle('delete', true)}
              onMouseEnter={() => setHoveredKey('delete')}
              onMouseLeave={() => setHoveredKey(null)}
              onClick={() => { closeMenu(); handlers.onDelete(bot.id); }}
            >
              Delete
            </button>
          )}
        </div>,
        document.body,
      )
    : null;

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} onClick={e => e.stopPropagation()}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        style={{
          ...activityBotActionBtnBase,
          padding: '6px 10px',
          border: '1px solid #cbd5e1',
          background: open ? '#f1f5f9' : '#fff',
          color: '#334155',
        }}
      >
        ⋯
      </button>
      {menu}
    </div>
  );
}

function ActivityBotExpandedContent({
  bot,
  handlers,
}: {
  bot: CryptoPortfolioActivityBot;
  handlers: ActivityBotRowHandlers;
}) {
  const displayStatus = activityBotDisplayStatus(bot);
  return (
    <div style={{ padding: '12px 14px 14px', background: '#f8fafc' }}>
      <div style={{ fontSize: 12, color: '#64748b' }}>
        {fmtUsd(bot.amountUsd)} per leg · {fmtLeverage(bot.leverage ?? 5)} · {bot.totalPositions} positions ({bot.longCount}L / {bot.shortCount}S)
        · {activityBotSafeguardSummary(bot)}
      </div>
      <ActivityBotSafeguardsPanel bot={bot} />
      {(bot.cycleNetPnl != null || bot.cycleDeployedUsd != null) && (
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
          Cycle P&amp;L {fmtPnl(bot.cycleNetPnl ?? 0)}
          {bot.cycleDeployedUsd != null ? ` · deployed ${fmtUsd(bot.cycleDeployedUsd)}` : ''}
        </div>
      )}
      <div style={{ fontSize: 12, color: activityBotStatusColor(displayStatus.tone), fontWeight: 700, marginTop: 8 }}>
        {displayStatus.label} · opened {bot.positionsOpened}/{bot.totalPositions}
        {activityBotStatusSuffix(bot)}
        {bot.lastError ? ` · ${bot.lastError}` : ''}
      </div>
      {displayStatus.hint && (
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
          {displayStatus.hint}
        </div>
      )}
      {handlers.canManage && (
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 10,
            fontSize: 12,
            color: '#475569',
            cursor: handlers.togglingContinuousId === bot.id ? 'wait' : 'pointer',
            userSelect: 'none',
          }}
          onClick={e => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={Boolean(bot.continuousRun)}
            disabled={handlers.saving || handlers.togglingContinuousId === bot.id}
            onChange={e => handlers.onContinuousToggle(bot, e.target.checked)}
            style={{ width: 14, height: 14, accentColor: '#086afb' }}
          />
          <span>
            Continuous run
            <span style={{ color: '#94a3b8', fontWeight: 400 }}>
              {' '}(auto re-start when cycle finishes and legs are closed)
            </span>
          </span>
        </label>
      )}
      {(bot.previewLegs?.length ?? 0) > 0 && (
        <div style={{ marginTop: 10, fontSize: 12, color: '#475569' }}>
          Plan: {bot.previewLegs!.map(leg => `${leg.side} ${leg.symbol}${leg.hot ? ' 🔥' : ''}`).join(' · ')}
        </div>
      )}
      {(bot.openLegs?.length ?? 0) > 0 && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#7e22ce', fontWeight: 700 }}>
          Open now: {bot.openLegs!.join(' · ')}
        </div>
      )}
      <div
        style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 12 }}
        onClick={e => e.stopPropagation()}
      >
        <ActivityBotPrimaryAction bot={bot} handlers={handlers} />
        <ActivityBotMoreMenu bot={bot} handlers={handlers} />
      </div>
      <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
        <button
          type="button"
          onClick={e => { e.stopPropagation(); handlers.onToggleHistory(bot.id); }}
          style={{
            padding: 0,
            border: 'none',
            background: 'transparent',
            color: '#086afb',
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
            textDecoration: 'underline',
            textUnderlineOffset: 2,
          }}
        >
          {handlers.expandedHistoryBotId === bot.id ? 'Hide trade history ▴' : 'View trade history ▾'}
        </button>
        <button
          type="button"
          onClick={e => { e.stopPropagation(); handlers.onExportHistory(bot); }}
          disabled={handlers.historyExportingBotId === bot.id}
          style={{
            padding: 0,
            border: 'none',
            background: 'transparent',
            color: handlers.historyExportingBotId === bot.id ? '#94a3b8' : '#64748b',
            fontSize: 12,
            fontWeight: 700,
            cursor: handlers.historyExportingBotId === bot.id ? 'wait' : 'pointer',
            textDecoration: 'underline',
            textUnderlineOffset: 2,
          }}
        >
          {handlers.historyExportingBotId === bot.id ? 'Exporting CSV…' : 'Export history CSV'}
        </button>
      </div>
      {handlers.expandedHistoryBotId === bot.id && (
        <div style={{ marginTop: 4 }}>
          {handlers.historyLoadingId === bot.id && (
            <p style={{ fontSize: 12, color: '#94a3b8', margin: '8px 0 0' }}>Loading trade history…</p>
          )}
          {handlers.historyErrorByBotId[bot.id] && (
            <p style={{ fontSize: 12, color: '#b91c1c', margin: '8px 0 0' }}>{handlers.historyErrorByBotId[bot.id]}</p>
          )}
          {handlers.historyLoadingId !== bot.id && !handlers.historyErrorByBotId[bot.id] && (
            <ActivityBotHistoryTable rows={handlers.historyByBotId[bot.id] ?? []} />
          )}
        </div>
      )}
    </div>
  );
}

function ChallengeBadge() {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 999,
        background: '#ede9fe',
        color: '#6d28d9',
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
        verticalAlign: 'middle',
      }}
      title="Managed by a Growth Challenge — sizing is controlled automatically while the challenge is live"
    >
      Challenge
    </span>
  );
}

function ActivityBotListTable({
  bots,
  expandedBotId,
  onToggleExpand,
  handlers,
  challengeBotIds,
}: {
  bots: CryptoPortfolioActivityBot[];
  expandedBotId: number | null;
  onToggleExpand: (botId: number) => void;
  handlers: ActivityBotRowHandlers;
  challengeBotIds?: number[];
}) {
  return (
    <div style={{ marginTop: 14, overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10, background: '#fff' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 880 }}>
        <thead>
          <tr>
            <th style={{ ...TH, width: 36 }} aria-label="Expand" />
            <th style={TH}>Bot</th>
            <th style={TH}>Sector</th>
            <th style={TH}>Account</th>
            <th style={TH}>Status</th>
            <th style={TH}>Progress</th>
            <th style={{ ...TH, textAlign: 'right' }}>Cycle P&amp;L</th>
            <th style={TH}>AI</th>
            <th style={{ ...TH, textAlign: 'right', width: 120 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {bots.map(bot => {
            const expanded = expandedBotId === bot.id;
            const pnl = bot.cycleNetPnl ?? 0;
            const pnlColor = pnl >= 0 ? '#15803d' : '#b91c1c';
            return (
              <React.Fragment key={bot.id}>
                <tr
                  onClick={() => onToggleExpand(bot.id)}
                  style={{
                    cursor: 'pointer',
                    background: expanded ? '#f1f5f9' : '#fff',
                  }}
                  title={bot.lastError || undefined}
                >
                  <td style={{ ...TD, width: 36, color: '#64748b', fontWeight: 700 }}>
                    {expanded ? '▾' : '▸'}
                  </td>
                  <td style={TD}>
                    <div style={{ fontWeight: 800, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      {activityBotCardTitle(bot)}
                      {challengeBotIds?.includes(bot.id) && <ChallengeBadge />}
                    </div>
                    {activityBotCardSubtitle(bot) && (
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                        {fmtUsd(bot.amountUsd)}/leg · {bot.longCount}L/{bot.shortCount}S
                      </div>
                    )}
                    {(bot.openLegs?.length ?? 0) > 0 && (
                      <div style={{ fontSize: 11, color: '#7e22ce', fontWeight: 600, marginTop: 2 }}>
                        Open: {bot.openLegs!.join(' · ')}
                      </div>
                    )}
                  </td>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>{bot.sectorLabel || bot.sectorId}</td>
                  <td style={{ ...TD, whiteSpace: 'nowrap', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {bot.kucoinAccountName || 'KuCoin'}
                  </td>
                  <td style={TD}>
                    <ActivityBotStatusBadge bot={bot} />
                    {bot.lastError && (
                      <div style={{ fontSize: 10, color: '#b45309', marginTop: 4, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {bot.lastError}
                      </div>
                    )}
                  </td>
                  <td style={{ ...TD, fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {bot.positionsOpened}/{bot.totalPositions}
                  </td>
                  <td style={{ ...TD, textAlign: 'right', fontWeight: 700, color: pnlColor, whiteSpace: 'nowrap' }}>
                    {bot.cycleNetPnl != null ? fmtPnl(pnl) : '—'}
                  </td>
                  <td style={TD}>
                    <ActivityBotAiBadge bot={bot} />
                  </td>
                  <td style={{ ...TD, textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end' }}>
                      <ActivityBotPrimaryAction bot={bot} handlers={handlers} compact />
                      <ActivityBotMoreMenu bot={bot} handlers={handlers} />
                    </div>
                  </td>
                </tr>
                {expanded && (
                  <tr>
                    <td colSpan={9} style={{ padding: 0, borderBottom: '1px solid #e2e8f0' }}>
                      <ActivityBotExpandedContent bot={bot} handlers={handlers} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ActivityBotMobileList({
  bots,
  expandedBotId,
  onToggleExpand,
  handlers,
  challengeBotIds,
}: {
  bots: CryptoPortfolioActivityBot[];
  expandedBotId: number | null;
  onToggleExpand: (botId: number) => void;
  handlers: ActivityBotRowHandlers;
  challengeBotIds?: number[];
}) {
  return (
    <div style={{ marginTop: 14, display: 'grid', gap: 8 }}>
      {bots.map(bot => {
        const expanded = expandedBotId === bot.id;
        const pnl = bot.cycleNetPnl ?? 0;
        const pnlColor = pnl >= 0 ? '#15803d' : '#b91c1c';
        const displayStatus = activityBotDisplayStatus(bot);
        return (
          <div
            key={bot.id}
            style={{
              border: '1px solid #e2e8f0',
              borderRadius: 10,
              overflow: 'hidden',
              background: '#fff',
            }}
          >
            <button
              type="button"
              onClick={() => onToggleExpand(bot.id)}
              style={{
                width: '100%',
                textAlign: 'left',
                padding: '12px 14px',
                border: 'none',
                background: expanded ? '#f1f5f9' : '#fff',
                cursor: 'pointer',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ color: '#64748b', fontWeight: 700 }}>{expanded ? '▾' : '▸'}</span>
                    <span style={{ fontWeight: 800, fontSize: 14, color: '#1e293b' }}>{activityBotCardTitle(bot)}</span>
                    {challengeBotIds?.includes(bot.id) && <ChallengeBadge />}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                    {bot.sectorLabel || bot.sectorId} · {bot.kucoinAccountName || 'KuCoin'}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 8 }}>
                    <ActivityBotStatusBadge bot={bot} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>
                      {bot.positionsOpened}/{bot.totalPositions}
                    </span>
                    {bot.cycleNetPnl != null && (
                      <span style={{ fontSize: 12, fontWeight: 700, color: pnlColor }}>{fmtPnl(pnl)}</span>
                    )}
                    <ActivityBotAiBadge bot={bot} />
                  </div>
                  {bot.lastError ? (
                    <div style={{ fontSize: 11, color: '#b45309', marginTop: 6 }}>{bot.lastError}</div>
                  ) : displayStatus.hint ? (
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>{displayStatus.hint}</div>
                  ) : null}
                </div>
                <div onClick={e => e.stopPropagation()}>
                  <ActivityBotPrimaryAction bot={bot} handlers={handlers} compact />
                </div>
              </div>
            </button>
            {expanded && <ActivityBotExpandedContent bot={bot} handlers={handlers} />}
          </div>
        );
      })}
    </div>
  );
}

function ActivityBotHistoryTable({ rows }: { rows: ActivityBotPositionHistoryRow[] }) {
  if (rows.length === 0) {
    return (
      <p style={{ fontSize: 12, color: '#94a3b8', margin: '10px 0 0' }}>
        No trades opened by this bot yet.
      </p>
    );
  }

  const thStyle: React.CSSProperties = {
    padding: '6px 8px',
    fontWeight: 700,
    color: '#64748b',
    borderBottom: '1px solid #e2e8f0',
    whiteSpace: 'nowrap',
  };
  const tdStyle: React.CSSProperties = {
    padding: '8px 8px',
    color: '#334155',
    borderBottom: '1px solid #f1f5f9',
    whiteSpace: 'nowrap',
  };

  return (
    <div style={{ marginTop: 10, overflowX: 'auto', background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ textAlign: 'left' }}>
            <th style={thStyle}>Coin</th>
            <th style={thStyle}>Side</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Opened</th>
            <th style={thStyle}>Closed</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Entry</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Exit / Current</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Size</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>P&L</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>P&L %</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const isOpen = row.status === 'OPEN';
            const pnl = isOpen ? row.unrealizedPnl : row.realizedPnl;
            const pnlNum = Number(pnl ?? 0);
            const exitOrCurrent = isOpen ? row.currentPrice : row.exitPrice;
            return (
              <tr key={`${row.hedgePositionId ?? row.symbol}-${row.openedAt ?? ''}`}>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{row.symbol}</td>
                <td style={tdStyle}>{row.side}</td>
                <td style={tdStyle}>{row.status}</td>
                <td style={tdStyle}>{fmtDate(row.openedAt)}</td>
                <td style={tdStyle}>{isOpen ? '—' : fmtDate(row.closedAt)}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{formatCryptoPrice(row.entryPrice)}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{formatCryptoPrice(exitOrCurrent)}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{fmtUsd(row.sizeUsd)}</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: pnlNum >= 0 ? '#15803d' : '#b91c1c' }}>
                  {fmtPnl(pnl)}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', color: pnlNum >= 0 ? '#15803d' : '#b91c1c' }}>
                  {fmtPct(row.pnlOnSizePct)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MetricCell({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: color ?? '#334155' }}>{value}</div>
    </div>
  );
}

interface PositionDetailPanelProps {
  row: CryptoPortfolioPositionRow;
  isOpen: boolean;
  isMobile: boolean;
  isAdminContext: boolean;
  actions?: React.ReactNode;
  onToggleAutoClose?: (enabled: boolean) => void;
  autoCloseToggling?: boolean;
  onSaveExitRules?: (payload: SetPositionExitRulesRequest) => Promise<void>;
  exitRulesSaving?: boolean;
}

const PositionDetailPanel: React.FC<PositionDetailPanelProps> = ({
  row,
  isOpen,
  isMobile,
  isAdminContext,
  actions,
  onToggleAutoClose,
  autoCloseToggling,
  onSaveExitRules,
  exitRulesSaving,
}) => {
  const [chartPoints, setChartPoints] = useState<PositionPricePoint[]>([]);
  const [chartLoading, setChartLoading] = useState(true);
  const [chartError, setChartError] = useState<string | null>(null);

  const pnl = isOpen ? row.unrealizedPnl : row.realizedPnl;
  const pnlNum = Number(pnl ?? 0);
  const pnlPositive = pnlNum >= 0;
  const priceTo = isOpen ? row.currentPrice : row.exitPrice;
  const changePct = priceChangePct(row, isOpen);
  const sizeUsd = Number(row.sizeUsd ?? 0);
  const pnlPct = sizeUsd > 0 && Number.isFinite(pnlNum) ? (pnlNum / sizeUsd) * 100 : null;
  const durationEnd = isOpen ? null : row.closedAt;

  useEffect(() => {
    let cancelled = false;
    setChartLoading(true);
    setChartError(null);
    setChartPoints([]);

    fetchPositionPriceHistory(row.symbol, row.openedAt, row.closedAt, isOpen, isAdminContext)
      .then(points => {
        if (cancelled) return;
        if (points.length === 0) {
          setChartError('No price history available for this symbol.');
        } else {
          setChartPoints(points);
        }
      })
      .catch(() => {
        if (!cancelled) setChartError('Failed to load price chart.');
      })
      .finally(() => {
        if (!cancelled) setChartLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [row.symbol, row.openedAt, row.closedAt, isOpen, isAdminContext]);

  const entryPrice = Number(row.entryPrice ?? 0);
  const chartStroke = pnlPositive ? '#0dca9d' : '#ff7171';

  return (
    <div
      style={{
        background: '#f8fafc',
        borderTop: '1px solid #e2e8f0',
        padding: isMobile ? '14px 14px 16px' : '16px 18px 18px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 14, color: '#1e293b' }}>
            {row.symbol} · {row.side}
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
            {row.strategyName || '—'}
          </div>
          {row.activityBotLabel && (
            <div style={{ marginTop: 6 }}>
              <ActivityBotBadge label={row.activityBotLabel} />
            </div>
          )}
        </div>
        {actions && <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{actions}</div>}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))',
          gap: '12px 20px',
          marginBottom: 16,
        }}
      >
        <MetricCell label="Entry" value={formatCryptoPrice(row.entryPrice)} />
        <MetricCell label={isOpen ? 'Current' : 'Exit'} value={formatCryptoPrice(priceTo)} />
        <MetricCell
          label="Price change"
          value={fmtPct(changePct)}
          color={changePct == null ? undefined : changePct >= 0 ? '#15803d' : '#b91c1c'}
        />
        <MetricCell label="Size" value={fmtUsd(row.sizeUsd)} />
        <MetricCell label="Leverage" value={fmtLeverage(row.leverage)} />
        <MetricCell
          label={isOpen ? 'Unrealized P&L' : 'Realized P&L'}
          value={fmtPnl(pnl)}
          color={pnlPositive ? '#15803d' : '#b91c1c'}
        />
        <MetricCell
          label="P&L on size"
          value={fmtPct(pnlPct)}
          color={pnlPct == null ? undefined : pnlPct >= 0 ? '#15803d' : '#b91c1c'}
        />
        <MetricCell label="Duration" value={formatDuration(row.openedAt, durationEnd)} />
        <MetricCell label="Status" value={row.status || (isOpen ? 'OPEN' : 'CLOSED')} />
        {row.kucoinAccountName && (
          <MetricCell label="KuCoin account" value={row.kucoinAccountName} />
        )}
        {row.activityBotLabel && (
          <MetricCell label="Activity bot" value={row.activityBotLabel} />
        )}
        {row.source === 'BOT' && row.strategyId != null && (
          <MetricCell label="Strategy ID" value={String(row.strategyId)} />
        )}
        {row.hedgePositionId != null && (
          <MetricCell label="Position ref" value={`#${row.hedgePositionId}`} />
        )}
        {isOpen && row.source === 'DIRECT' && (row.targetProgressPct != null || hasStopLoss(row)) && (
          <MetricCell
            label="Target progress"
            value={row.targetProgressPct != null ? formatTargetProgressLabelForRow(row) : '—'}
            color={
              row.targetProgressPct != null
                ? targetProgressLabelColor(
                    normalizeTargetProgress(row.targetProgressPct),
                    Boolean(row.targetAchieved),
                  )
                : '#64748b'
            }
          />
        )}
        {isOpen && hasStopLoss(row) && (
          <MetricCell
            label="Stop buffer"
            value={formatStopLossUsedLabel(
              computeStopLossUsedPct(row.pnlOnSizePct, row.stopLossPct) ?? 0,
              Number(row.pnlOnSizePct ?? 0) <= Number(row.stopLossPct ?? 0),
            )}
            color={stopLossUsedLabelColor(
              computeStopLossUsedPct(row.pnlOnSizePct, row.stopLossPct) ?? 0,
              Number(row.pnlOnSizePct ?? 0) <= Number(row.stopLossPct ?? 0),
            )}
          />
        )}
      </div>

      {isOpen && row.source === 'DIRECT' && (row.targetProgressPct != null || hasStopLoss(row)) && (
        <div style={{ marginBottom: 16, maxWidth: 280 }}>
          {row.targetProgressPct != null && (
            <>
              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>Target progress vs goal</div>
              <TargetProgressBar
                progress={normalizeTargetProgress(row.targetProgressPct)}
                achieved={Boolean(row.targetAchieved)}
              />
              {row.targetPct != null && (
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
                  Goal +{Number(row.targetPct).toFixed(1)}%
                </div>
              )}
            </>
          )}
          {hasStopLoss(row) && (
            <div style={{ marginTop: row.targetProgressPct != null ? 12 : 0 }}>
              <StopLossProgressSection row={row} />
            </div>
          )}
        </div>
      )}

      {isOpen && row.source === 'DIRECT' && (row.fills?.length ?? 0) > 0 && (
        <div
          style={{
            marginBottom: 16,
            padding: '10px 12px',
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 10,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 8 }}>
            Position fills ({row.fills!.length})
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>
            KuCoin aggregates these into one live position — size above is the combined total.
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {row.fills!.map((fill, idx) => (
              <div
                key={`${fill.openedAt ?? idx}-${fill.amountUsd ?? 0}`}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  fontSize: 12,
                  color: '#475569',
                  padding: '6px 8px',
                  background: '#f8fafc',
                  borderRadius: 6,
                }}
              >
                <span>{fmtDate(fill.openedAt)}</span>
                <span style={{ fontWeight: 700 }}>{fmtUsd(fill.amountUsd)}</span>
                <span>{formatCryptoPrice(fill.entryPrice)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {isOpen && isAdminContext && row.canToggleAutoClose && onToggleAutoClose && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
            marginBottom: 16,
            padding: '10px 12px',
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 10,
          }}
        >
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Auto-close on target</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
              Closes this direct trade when it reaches your +{Number(row.targetPct ?? 0).toFixed(1)}% goal.
            </div>
          </div>
          <AutoCloseToggle
            row={row}
            disabled={autoCloseToggling}
            onToggle={onToggleAutoClose}
          />
        </div>
      )}

      {isOpen && isAdminContext && row.canConfigureExitRules && onSaveExitRules && (
        <PositionExitRulesPanel row={row} saving={exitRulesSaving} onSave={onSaveExitRules} />
      )}

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 10px 6px' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 8, paddingLeft: 4 }}>
          Price since {isOpen ? 'open' : 'open → close'}
        </div>
        {chartLoading && (
          <div style={{ textAlign: 'center', padding: '36px 0', color: '#94a3b8', fontSize: 13 }}>
            Loading chart…
          </div>
        )}
        {!chartLoading && chartError && (
          <div style={{ textAlign: 'center', padding: '28px 12px', color: '#94a3b8', fontSize: 13 }}>
            {chartError}
          </div>
        )}
        {!chartLoading && !chartError && chartPoints.length > 0 && (
          <ResponsiveContainer width="100%" height={isMobile ? 160 : 200}>
            <AreaChart data={chartPoints} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id={`pnlGrad-${getPositionRowKey(row)}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={chartStroke} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={chartStroke} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: '#94a3b8' }}
                interval="preserveStartEnd"
                minTickGap={28}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#94a3b8' }}
                width={56}
                domain={['auto', 'auto']}
                tickFormatter={(v: number) => (v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v}`)}
              />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                formatter={(value: number) => [formatCryptoPrice(value), 'Price']}
                labelFormatter={(label: string) => label}
              />
              {entryPrice > 0 && (
                <ReferenceLine
                  y={entryPrice}
                  stroke="#086afb"
                  strokeDasharray="4 4"
                  label={{ value: 'Entry', position: 'insideTopRight', fill: '#086afb', fontSize: 10 }}
                />
              )}
              <Area
                type="monotone"
                dataKey="price"
                stroke={chartStroke}
                strokeWidth={2}
                fill={`url(#pnlGrad-${getPositionRowKey(row)})`}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

type MultiSeriesPoint = {
  label: string;
  // Recharts dataKeys are dynamic; all other fields are line values.
  [key: string]: string | number | null;
};

interface ActiveOpenPositionsChartCardProps {
  rows: CryptoPortfolioPositionRow[];
  isAdminContext: boolean;
  isMobile: boolean;
  updatedAt?: number | null;
}

const ActiveOpenPositionsChartCard: React.FC<ActiveOpenPositionsChartCardProps> = ({
  rows,
  isAdminContext,
  isMobile,
  updatedAt,
}) => {
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState<string | null>(null);
  const [chartData, setChartData] = useState<MultiSeriesPoint[]>([]);

  const lastFetchAtRef = useRef<number>(0);
  const lastSeriesKeyRef = useRef<string>('');

  const palette = ['#0dca9d', '#086afb', '#e7038e', '#fbb445', '#8b5cf6', '#14b8a6', '#f97316', '#64748b'];
  const MAX_LINES = 8;

  const series = useMemo(() => {
    const candidates = (rows ?? []).filter(r => isPositionOpen(r));
    // Stable selection: largest open size first (reduces churn across polls).
    const sorted = [...candidates].sort((a, b) => Number(b.sizeUsd ?? 0) - Number(a.sizeUsd ?? 0));
    const top = sorted.slice(0, MAX_LINES);

    return top.map((r, idx) => {
      const key = getPositionRowKey(r);
      const label = `${r.symbol} · ${r.side}`;
      const entryPrice = Number(r.entryPrice ?? 0);
      const lineColor = palette[idx % palette.length];
      return { row: r, key, label, entryPrice, lineColor };
    });
  }, [rows]);

  const seriesKey = series.map(s => s.key).join('|');

  const best = useMemo(() => {
    if (!rows || rows.length === 0) return null;
    const candidates = rows.filter(r => isPositionOpen(r));
    if (candidates.length === 0) return null;
    const sorted = [...candidates].sort((a, b) => Number(b.unrealizedPnl ?? 0) - Number(a.unrealizedPnl ?? 0));
    return sorted[0];
  }, [rows]);

  useEffect(() => {
    let cancelled = false;
    const now = Date.now();
    const lastSeriesKey = lastSeriesKeyRef.current;
    const shouldFetchForNewSeries = seriesKey !== lastSeriesKey;
    const shouldFetchForStaleness = now - lastFetchAtRef.current > 60_000; // refresh every ~60s at most

    if (!shouldFetchForNewSeries && !shouldFetchForStaleness) {
      return;
    }

    lastSeriesKeyRef.current = seriesKey;
    lastFetchAtRef.current = now;

    if (!series || series.length === 0) {
      setChartData([]);
      setChartError(null);
      setChartLoading(false);
      return;
    }

    setChartLoading(true);
    setChartError(null);
    setChartData([]);

    Promise.all(
      series.map(async s => {
        const points = await fetchPositionPriceHistory(s.row.symbol, s.row.openedAt, s.row.closedAt, true, isAdminContext);
        return { s, points };
      }),
    )
      .then(results => {
        if (cancelled) return;

        // Merge points by timestamp (time is consistent across klines granularity).
        const timeToLabel = new Map<number, string>();
        const valueByKey = new Map<string, Map<number, number | null>>();

        for (const { s, points } of results) {
          const entry = s.entryPrice;
          const vMap = new Map<number, number | null>();
          for (const p of points) {
            timeToLabel.set(p.time, p.label);
            if (entry > 0) {
              const pct = ((p.price - entry) / entry) * 100;
              vMap.set(p.time, Number.isFinite(pct) ? pct : null);
            } else {
              vMap.set(p.time, null);
            }
          }
          valueByKey.set(s.key, vMap);
        }

        const times = Array.from(timeToLabel.keys()).sort((a, b) => a - b);
        const merged: MultiSeriesPoint[] = times.map(t => {
          const obj: MultiSeriesPoint = { label: timeToLabel.get(t) ?? String(t) };
          for (const s of series) {
            obj[s.key] = valueByKey.get(s.key)?.get(t) ?? null;
          }
          return obj;
        });

        setChartData(merged);
      })
      .catch(() => {
        if (!cancelled) setChartError('Failed to load active positions chart.');
      })
      .finally(() => {
        if (!cancelled) setChartLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [seriesKey, isAdminContext, updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ ...cardStyle, marginTop: 16 }}>
      <div style={{ padding: '12px 14px 0', marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 14, color: '#1e293b', marginBottom: 4 }}>Live: Active open positions</div>
            <div style={{ fontSize: 12, color: '#94a3b8' }}>
              P&L% since entry (0% = entry). {best ? <>Leading: <strong style={{ color: Number(best.unrealizedPnl ?? 0) >= 0 ? '#15803d' : '#b91c1c' }}>{best.symbol}</strong></> : null}
            </div>
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>
            Showing top {series.length} lines by open size
          </div>
        </div>
      </div>

      <div style={{ padding: '0 14px 12px' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
          {series.map((s, idx) => (
            <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 999,
                  background: s.lineColor,
                  display: 'inline-block',
                }}
              />
              <span style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>
                {idx + 1}. {s.label}
              </span>
            </div>
          ))}
        </div>

        {chartLoading && (
          <div style={{ textAlign: 'center', padding: '36px 0', color: '#94a3b8', fontSize: 13 }}>
            Loading chart…
          </div>
        )}

        {!chartLoading && chartError && (
          <div style={{ textAlign: 'center', padding: '28px 12px', color: '#94a3b8', fontSize: 13 }}>
            {chartError}
          </div>
        )}

        {!chartLoading && !chartError && chartData.length > 0 && (
          <ResponsiveContainer width="100%" height={isMobile ? 170 : 220}>
            <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: '#94a3b8' }}
                interval="preserveStartEnd"
                minTickGap={28}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#94a3b8' }}
                width={60}
                domain={['auto', 'auto']}
                tickFormatter={(v: number) => `${v.toFixed(0)}%`}
              />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                formatter={(value: any) => [
                  value == null || !Number.isFinite(Number(value)) ? '—' : `${Number(value).toFixed(2)}%`,
                  '',
                ]}
                labelFormatter={(label: string) => label}
              />
              <ReferenceLine
                y={0}
                stroke="#086afb"
                strokeDasharray="4 4"
                label={{ value: 'Entry', position: 'insideTopRight', fill: '#086afb', fontSize: 10 }}
              />
              {series.map(s => (
                <Line
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.label}
                  stroke={s.lineColor}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 3 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}

        {!chartLoading && !chartError && chartData.length === 0 && (
          <div style={{ textAlign: 'center', padding: '28px 12px', color: '#94a3b8', fontSize: 13 }}>
            No chart data available.
          </div>
        )}
      </div>
    </div>
  );
};

interface ProfitTargetCardProps {
  target: CryptoPortfolioProfitTarget | null | undefined;
  onEdit: () => void;
}

interface ActivityBotPanelProps {
  canManage: boolean;
  onPortfolioRefresh: () => Promise<void>;
  challengeBotIds?: number[];
}

const ActivityBotPanel: React.FC<ActivityBotPanelProps> = ({ canManage, onPortfolioRefresh, challengeBotIds }) => {
  const deepLinkConsumedRef = useRef(false);
  const [sectors, setSectors] = useState<ActivityBotSectorOption[]>([]);
  const [accounts, setAccounts] = useState<ActivityBotKucoinAccount[]>([]);
  const [bots, setBots] = useState<CryptoPortfolioActivityBot[]>([]);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewLegs, setPreviewLegs] = useState<ActivityBotPreviewLeg[]>([]);
  const [previewMarketRegime, setPreviewMarketRegime] = useState<string | null>(null);
  const [filterProfiles, setFilterProfiles] = useState<TradingFilterProfile[]>([]);
  const [filterProfileId, setFilterProfileId] = useState<number | ''>('');
  const [macroBtcBridgeEnabled, setMacroBtcBridgeEnabled] = useState(false);
  const [macroCommodityBridgeEnabled, setMacroCommodityBridgeEnabled] = useState(false);
  const [filtersSectionExpanded, setFiltersSectionExpanded] = useState(false);
  const [filterProfilesEnabled, setFilterProfilesEnabled] = useState(true);
  const [profilesModalOpen, setProfilesModalOpen] = useState(false);
  const [profilesModalView, setProfilesModalView] = useState<'list' | 'edit'>('list');
  const [profileEditorReadOnly, setProfileEditorReadOnly] = useState(false);
  const [profileEditorId, setProfileEditorId] = useState<number | null>(null);
  const [profileEditorDraft, setProfileEditorDraft] = useState<FilterProfileEditorDraft>(emptyFilterProfileEditorDraft);
  const [profileEditorError, setProfileEditorError] = useState<string | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [deletingProfileId, setDeletingProfileId] = useState<number | null>(null);
  const [cloningProfileId, setCloningProfileId] = useState<number | null>(null);
  const [importingFilterProfile, setImportingFilterProfile] = useState(false);
  const [cloningBotId, setCloningBotId] = useState<number | null>(null);
  const [expandedProfileConfigIds, setExpandedProfileConfigIds] = useState<Set<number>>(() => new Set());
  const filterProfileImportInputRef = useRef<HTMLInputElement | null>(null);
  const [editingBotId, setEditingBotId] = useState<number | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [expandedHistoryBotId, setExpandedHistoryBotId] = useState<number | null>(null);
  const [expandedBotId, setExpandedBotId] = useState<number | null>(null);
  const [historyByBotId, setHistoryByBotId] = useState<Record<number, ActivityBotPositionHistoryRow[]>>({});
  const [historyLoadingId, setHistoryLoadingId] = useState<number | null>(null);
  const [historyErrorByBotId, setHistoryErrorByBotId] = useState<Record<number, string>>({});
  const [historyExportingBotId, setHistoryExportingBotId] = useState<number | null>(null);
  const [exportingAllHistories, setExportingAllHistories] = useState(false);
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [logsBot, setLogsBot] = useState<CryptoPortfolioActivityBot | null>(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [activityBotLogs, setActivityBotLogs] = useState<string[]>([]);
  const [logsLineCount, setLogsLineCount] = useState(500);
  const [logsAutoRefresh, setLogsAutoRefresh] = useState(true);
  const [aiTuneModalBot, setAiTuneModalBot] = useState<CryptoPortfolioActivityBot | null>(null);
  const [importNotes, setImportNotes] = useState<string[]>([]);
  const [pendingImportAiTune, setPendingImportAiTune] = useState<UpdateActivityBotAiAutoTuneRequest | null>(null);

  const [referenceName, setReferenceName] = useState('');
  const [kucoinConfigId, setKucoinConfigId] = useState<number | ''>('');
  const [amountUsd, setAmountUsd] = useState('250');
  const [leverage, setLeverage] = useState('5');
  const [sectorId, setSectorId] = useState('GAMING');
  const [totalPositions, setTotalPositions] = useState('5');
  const [longCount, setLongCount] = useState('3');
  const [shortCount, setShortCount] = useState('2');
  const [targetProfitPct, setTargetProfitPct] = useState('8');
  const [stopLossPct, setStopLossPct] = useState('10');
  const [trailingStopEnabled, setTrailingStopEnabled] = useState(false);
  const [trailActivationPct, setTrailActivationPct] = useState('5');
  const [trailDistancePct, setTrailDistancePct] = useState('4');
  const [maxBotLossUsd, setMaxBotLossUsd] = useState('');
  const [maxBotLossPct, setMaxBotLossPct] = useState('');
  const [continuousRun, setContinuousRun] = useState(false);
  const [entryMode, setEntryMode] = useState<'ACTIVITY_ONLY' | 'PEAK_TROUGH_TIMING'>('ACTIVITY_ONLY');
  const [togglingContinuousId, setTogglingContinuousId] = useState<number | null>(null);
  const previewParamsKeyRef = useRef('');
  const runPreviewRef = useRef<() => Promise<void>>(async () => {});
  const activityBotLogsIntervalRef = useRef<number | null>(null);
  const importFileInputRef = useRef<HTMLInputElement>(null);
  const isMobileBots = useIsMobile();

  const loadMeta = useCallback(async () => {
    setLoadingMeta(true);
    setError(null);
    try {
      // Load independently — a slow/failed activity-bots list must not wipe sectors or filter profiles.
      const [sectorsResult, accountsResult, botsResult, catalogResult] = await Promise.allSettled([
        cryptoPortfolioService.listActivityBotSectors(),
        cryptoPortfolioService.listActivityBotKucoinAccounts(),
        cryptoPortfolioService.listActivityBots(),
        cryptoPortfolioService.getFilterProfileCatalog(),
      ]);

      const errors: string[] = [];

      if (sectorsResult.status === 'fulfilled') {
        const sectorRows = sectorsResult.value;
        setSectors(sectorRows);
        setSectorId(prev => (sectorRows.length > 0 && !sectorRows.some(s => s.id === prev) ? sectorRows[0].id : prev));
      } else {
        setSectors([]);
        errors.push(sectorsResult.reason?.message || 'Failed to load sectors');
      }

      if (accountsResult.status === 'fulfilled') {
        const selectable = accountsResult.value.filter(a => a.selectable !== false);
        setAccounts(selectable);
        // Keep an explicit empty selection (import flow requires picking an account).
        setKucoinConfigId(prev => {
          if (prev === '') return prev;
          return selectable.some(a => a.id === prev) ? prev : (selectable[0]?.id ?? '');
        });
      } else {
        setAccounts([]);
        errors.push(accountsResult.reason?.message || 'Failed to load KuCoin accounts');
      }

      if (botsResult.status === 'fulfilled') {
        setBots(botsResult.value);
      } else {
        setBots([]);
        errors.push(botsResult.reason?.message || 'Failed to load activity bots');
      }

      if (catalogResult.status === 'fulfilled') {
        const catalog = catalogResult.value;
        setFilterProfilesEnabled(catalog.enabled !== false);
        setFilterProfiles(catalog.profiles ?? []);
      } else {
        setFilterProfilesEnabled(true);
        setFilterProfiles([]);
        errors.push(catalogResult.reason?.message || 'Failed to load filter profiles');
      }

      if (errors.length > 0) {
        setError(errors.join(' · '));
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load activity bot options.');
    } finally {
      setLoadingMeta(false);
    }
  }, []);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    if (deepLinkConsumedRef.current || loadingMeta || sectors.length === 0) return;
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem('activityBotDeepLink');
    } catch {
      return;
    }
    if (!raw) return;
    deepLinkConsumedRef.current = true;
    try {
      sessionStorage.removeItem('activityBotDeepLink');
    } catch {
      /* ignore */
    }
    let payload: { sector?: string; openNew?: boolean } = {};
    try {
      payload = JSON.parse(raw);
    } catch {
      return;
    }
    const sector = payload.sector;
    if (sector && sectors.some((s) => s.id === sector)) {
      setSectorId(sector);
    }
    if (sector === 'COMMODITIES') {
      setTotalPositions('4');
      setLongCount('2');
      setShortCount('2');
      setLeverage('3');
      setAmountUsd('100');
    }
    if (payload.openNew && canManage) {
      setEditingBotId(null);
      setFormOpen(true);
    }
  }, [loadingMeta, sectors, canManage]);

  const parsedTotal = Number(totalPositions);
  const parsedLong = Number(longCount);
  const parsedShort = Number(shortCount);
  const splitValid = Number.isFinite(parsedTotal) && parsedTotal > 0
    && Number.isFinite(parsedLong) && Number.isFinite(parsedShort)
    && parsedLong + parsedShort === parsedTotal;

  const buildPayload = (): SaveActivityBotRequest | null => {
    const amount = Number(amountUsd);
    const target = Number(targetProfitPct);
    const stopRaw = stopLossPct.trim();
    const stop = stopRaw === '' ? null : Number(stopRaw);
    const trailAct = Number(trailActivationPct);
    const trailDist = Number(trailDistancePct);
    const maxUsdRaw = maxBotLossUsd.trim();
    const maxUsd = maxUsdRaw === '' ? null : Number(maxUsdRaw);
    const maxPctRaw = maxBotLossPct.trim();
    const maxPct = maxPctRaw === '' ? null : Number(maxPctRaw);
    const parsedLeverage = Number(leverage);
    if (!kucoinConfigId || !Number.isFinite(amount) || amount <= 0) return null;
    if (!Number.isFinite(parsedLeverage) || parsedLeverage < 1 || parsedLeverage > ACTIVITY_BOT_MAX_LEVERAGE) return null;
    if (!splitValid) return null;
    if (!Number.isFinite(target) || target <= 0) return null;
    if (stopRaw !== '' && (!Number.isFinite(stop!) || stop! <= 0 || stop! > 100)) return null;
    if (trailingStopEnabled && (!Number.isFinite(trailAct) || trailAct <= 0 || !Number.isFinite(trailDist) || trailDist <= 0)) {
      return null;
    }
    if (maxUsdRaw !== '' && (!Number.isFinite(maxUsd!) || maxUsd! <= 0)) return null;
    if (maxPctRaw !== '' && (!Number.isFinite(maxPct!) || maxPct! <= 0 || maxPct! > 100)) return null;
    const ref = referenceName.trim();
    return {
      referenceName: ref || null,
      kucoinConfigId: Number(kucoinConfigId),
      amountUsd: amount,
      leverage: parsedLeverage,
      sectorId,
      totalPositions: parsedTotal,
      longCount: parsedLong,
      shortCount: parsedShort,
      targetProfitPct: target,
      stopLossPct: stopRaw === '' ? 0 : stop,
      trailingStopEnabled,
      trailingStopActivationPct: trailingStopEnabled ? trailAct : null,
      trailingStopDistancePct: trailingStopEnabled ? trailDist : null,
      maxBotLossUsd: maxUsdRaw === '' ? 0 : maxUsd,
      maxBotLossPct: maxPctRaw === '' ? 0 : maxPct,
      continuousRun,
      entryMode: filterProfileId !== '' ? 'ACTIVITY_ONLY' : entryMode,
      filterProfileId: filterProfileId === '' ? null : Number(filterProfileId),
      macroBtcBridgeEnabled: sectorId === 'COMMODITIES' ? false : macroBtcBridgeEnabled,
      macroCommodityBridgeEnabled: sectorId === 'COMMODITIES' ? macroCommodityBridgeEnabled : false,
    };
  };

  const selectedFilterProfile = useMemo(
    () => filterProfiles.find(p => p.id === filterProfileId) ?? null,
    [filterProfiles, filterProfileId],
  );

  const buildPreviewParamsKey = useCallback((params: {
    sectorId: string;
    totalPositions: number;
    longCount: number;
    shortCount: number;
    filterProfileId: number | '';
    entryMode: 'ACTIVITY_ONLY' | 'PEAK_TROUGH_TIMING';
  }) => JSON.stringify({
    sectorId: params.sectorId,
    totalPositions: params.totalPositions,
    longCount: params.longCount,
    shortCount: params.shortCount,
    filterProfileId: params.filterProfileId === '' ? null : params.filterProfileId,
    entryMode: params.filterProfileId !== '' ? 'ACTIVITY_ONLY' : params.entryMode,
  }), []);

  const runPreview = useCallback(async () => {
    if (!splitValid) {
      setPreviewLegs([]);
      return;
    }
    setPreviewLoading(true);
    setError(null);
    try {
      const preview = await cryptoPortfolioService.previewActivityBot({
        sectorId,
        totalPositions: parsedTotal,
        longCount: parsedLong,
        shortCount: parsedShort,
        filterProfileId: filterProfileId === '' ? null : Number(filterProfileId),
        entryMode: filterProfileId !== '' ? 'ACTIVITY_ONLY' : entryMode,
      });
      setPreviewLegs(preview.legs ?? []);
      setPreviewMarketRegime(preview.marketRegime ?? null);
    } catch (err: any) {
      setPreviewLegs([]);
      setError(err?.message || 'Preview failed.');
    } finally {
      setPreviewLoading(false);
    }
  }, [sectorId, parsedTotal, parsedLong, parsedShort, splitValid, filterProfileId, entryMode]);

  runPreviewRef.current = runPreview;

  useEffect(() => {
    if (!formOpen || !splitValid) return undefined;

    const paramsKey = buildPreviewParamsKey({
      sectorId,
      totalPositions: parsedTotal,
      longCount: parsedLong,
      shortCount: parsedShort,
      filterProfileId,
      entryMode,
    });

    if (paramsKey === previewParamsKeyRef.current) return undefined;

    const timer = window.setTimeout(() => {
      void runPreviewRef.current().then(() => {
        previewParamsKeyRef.current = paramsKey;
      });
    }, 400);

    return () => window.clearTimeout(timer);
  }, [
    formOpen,
    splitValid,
    sectorId,
    parsedTotal,
    parsedLong,
    parsedShort,
    filterProfileId,
    entryMode,
    buildPreviewParamsKey,
  ]);

  const resetForm = () => {
    setEditingBotId(null);
    setReferenceName('');
    setAmountUsd('250');
    setLeverage('5');
    setSectorId('GAMING');
    setTotalPositions('5');
    setLongCount('3');
    setShortCount('2');
    setTargetProfitPct('8');
    setStopLossPct('10');
    setTrailingStopEnabled(false);
    setTrailActivationPct('5');
    setTrailDistancePct('4');
    setMaxBotLossUsd('');
    setMaxBotLossPct('');
    setContinuousRun(false);
    setEntryMode('ACTIVITY_ONLY');
    setFilterProfileId('');
    setMacroBtcBridgeEnabled(false);
    setMacroCommodityBridgeEnabled(false);
    setFiltersSectionExpanded(false);
    setPreviewMarketRegime(null);
    setPreviewLegs([]);
    previewParamsKeyRef.current = '';
    setError(null);
    setImportNotes([]);
    setPendingImportAiTune(null);
    const selectable = accounts.filter(a => a.selectable !== false);
    setKucoinConfigId(selectable[0]?.id ?? '');
  };

  const openCreateForm = () => {
    resetForm();
    setFormOpen(true);
  };

  const handleExportBot = (bot: CryptoPortfolioActivityBot) => {
    try {
      downloadActivityBotExport(bot);
    } catch (err: any) {
      setError(err?.message || 'Failed to export activity bot.');
    }
  };

  const loadHistoryRowsForExport = async (
    botId: number,
  ): Promise<ActivityBotPositionHistoryRow[]> => {
    const cached = historyByBotId[botId];
    if (cached) {
      return cached;
    }
    const rows = await cryptoPortfolioService.getActivityBotHistory(botId);
    setHistoryByBotId(prev => ({ ...prev, [botId]: rows }));
    return rows;
  };

  const handleExportHistory = async (bot: CryptoPortfolioActivityBot) => {
    setHistoryExportingBotId(bot.id);
    setError(null);
    try {
      const rows = await loadHistoryRowsForExport(bot.id);
      downloadActivityBotHistoryCsv(bot, rows);
    } catch (err: any) {
      setError(err?.message || 'Failed to export trade history.');
    } finally {
      setHistoryExportingBotId(null);
    }
  };

  const handleExportAllHistories = async () => {
    if (bots.length === 0) {
      setError('No activity bots to export.');
      return;
    }
    setExportingAllHistories(true);
    setError(null);
    try {
      const entries: Array<{ bot: CryptoPortfolioActivityBot; rows: ActivityBotPositionHistoryRow[] }> = [];
      for (const bot of bots) {
        const rows = await loadHistoryRowsForExport(bot.id);
        entries.push({ bot, rows });
      }
      downloadAllActivityBotsHistoryCsv(entries);
    } catch (err: any) {
      setError(err?.message || 'Failed to export all trade histories.');
    } finally {
      setExportingAllHistories(false);
    }
  };

  const applyImportPrefill = (prefill: ReturnType<typeof buildActivityBotImportPrefill>) => {
    setEditingBotId(null);
    setReferenceName(prefill.referenceName);
    // Force explicit account pick — never copy source kucoinConfigId
    setKucoinConfigId('');
    setAmountUsd(prefill.amountUsd);
    setLeverage(prefill.leverage);
    setSectorId(prefill.sectorId);
    setTotalPositions(prefill.totalPositions);
    setLongCount(prefill.longCount);
    setShortCount(prefill.shortCount);
    setTargetProfitPct(prefill.targetProfitPct);
    setStopLossPct(prefill.stopLossPct);
    setTrailingStopEnabled(prefill.trailingStopEnabled);
    setTrailActivationPct(prefill.trailActivationPct);
    setTrailDistancePct(prefill.trailDistancePct);
    setMaxBotLossUsd(prefill.maxBotLossUsd);
    setMaxBotLossPct(prefill.maxBotLossPct);
    setContinuousRun(prefill.continuousRun);
    setEntryMode(prefill.entryMode);
    setFilterProfileId(prefill.filterProfileId);
    setMacroBtcBridgeEnabled(prefill.macroBtcBridgeEnabled);
    setMacroCommodityBridgeEnabled(prefill.macroCommodityBridgeEnabled);
    setFiltersSectionExpanded(
      prefill.filterProfileId !== ''
        || prefill.macroBtcBridgeEnabled
        || prefill.macroCommodityBridgeEnabled
        || Boolean(prefill.filterProfileWarning),
    );
    setPreviewMarketRegime(null);
    setPreviewLegs([]);
    previewParamsKeyRef.current = '';
    setImportNotes(prefill.importNotes);
    setPendingImportAiTune(prefill.aiTuneToApply);
    setError(null);
    setFormOpen(true);
  };

  const handleImportFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const raw = await file.text();
      const parsed = parseActivityBotExportJson(raw);
      const prefill = buildActivityBotImportPrefill(parsed, filterProfiles);
      applyImportPrefill(prefill);
    } catch (err: any) {
      setError(err?.message || 'Failed to import activity bot JSON.');
    }
  };

  const openEditForm = (bot: CryptoPortfolioActivityBot) => {
    const botFilterProfileId = bot.filterProfileId ?? '';
    const botEntryMode = bot.entryMode === 'PEAK_TROUGH_TIMING' ? 'PEAK_TROUGH_TIMING' : 'ACTIVITY_ONLY';
    const previewKey = buildPreviewParamsKey({
      sectorId: bot.sectorId,
      totalPositions: bot.totalPositions,
      longCount: bot.longCount,
      shortCount: bot.shortCount,
      filterProfileId: botFilterProfileId,
      entryMode: botEntryMode,
    });

    setEditingBotId(bot.id);
    setReferenceName(bot.referenceName ?? '');
    setKucoinConfigId(bot.kucoinConfigId);
    setAmountUsd(String(bot.amountUsd ?? 250));
    setLeverage(String(snapActivityBotLeverage(Number(bot.leverage ?? 5))));
    setSectorId(bot.sectorId);
    setTotalPositions(String(bot.totalPositions));
    setLongCount(String(bot.longCount));
    setShortCount(String(bot.shortCount));
    setTargetProfitPct(String(bot.targetProfitPct));
    setStopLossPct(bot.stopLossPct != null ? String(bot.stopLossPct) : '');
    setTrailingStopEnabled(Boolean(bot.trailingStopEnabled));
    setTrailActivationPct(bot.trailingStopActivationPct != null ? String(bot.trailingStopActivationPct) : '5');
    setTrailDistancePct(bot.trailingStopDistancePct != null ? String(bot.trailingStopDistancePct) : '4');
    setMaxBotLossUsd(bot.maxBotLossUsd != null ? String(bot.maxBotLossUsd) : '');
    setMaxBotLossPct(bot.maxBotLossPct != null ? String(bot.maxBotLossPct) : '');
    setContinuousRun(Boolean(bot.continuousRun));
    setEntryMode(botEntryMode);
    setFilterProfileId(botFilterProfileId);
    setMacroBtcBridgeEnabled(Boolean(bot.macroBtcBridgeEnabled) && bot.sectorId !== 'COMMODITIES');
    setMacroCommodityBridgeEnabled(Boolean(bot.macroCommodityBridgeEnabled) && bot.sectorId === 'COMMODITIES');
    setFiltersSectionExpanded(
      Boolean(bot.filterProfileId) || Boolean(bot.macroBtcBridgeEnabled) || Boolean(bot.macroCommodityBridgeEnabled)
    );
    setPreviewLegs(bot.previewLegs ?? []);
    setPreviewMarketRegime(null);
    previewParamsKeyRef.current = (bot.previewLegs?.length ?? 0) > 0 ? previewKey : '';
    setImportNotes([]);
    setPendingImportAiTune(null);
    setError(null);
    setFormOpen(true);
  };

  const editingBot = useMemo(
    () => (editingBotId != null ? bots.find(b => b.id === editingBotId) ?? null : null),
    [bots, editingBotId],
  );

  const closeFormDrawer = () => {
    if (saving) return;
    setFormOpen(false);
    resetForm();
  };

  useEffect(() => {
    if (!formOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !profilesModalOpen) closeFormDrawer();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [formOpen, profilesModalOpen, saving]);

  const parsedStopLoss = Number(stopLossPct);
  const parsedTargetProfit = Number(targetProfitPct);
  const stopWiderThanTarget = stopLossPct.trim() !== ''
    && Number.isFinite(parsedStopLoss) && parsedStopLoss > 0
    && Number.isFinite(parsedTargetProfit) && parsedTargetProfit > 0
    && parsedStopLoss > parsedTargetProfit;

  const handleSave = async () => {
    if (!kucoinConfigId) {
      setError('Select a KuCoin sub-account before saving.');
      return;
    }
    const payload = buildPayload();
    if (!payload) {
      setError('Check all fields: long + short must equal total; leverage 1–10x; stop loss 0–100%; trailing values > 0; max loss limits valid.');
      return;
    }
    const aiTuneAfterCreate = editingBotId == null ? pendingImportAiTune : null;
    setSaving(true);
    setError(null);
    try {
      let saved = editingBotId != null
        ? await cryptoPortfolioService.updateActivityBot(editingBotId, payload)
        : await cryptoPortfolioService.createActivityBot(payload);

      // Apply imported AI tune settings with auto-tune forced OFF (never starts live tuning).
      if (aiTuneAfterCreate && editingBotId == null) {
        try {
          saved = await cryptoPortfolioService.updateActivityBotAiAutoTuneSettings(saved.id, {
            ...aiTuneAfterCreate,
            aiAutoTuneEnabled: false,
          });
        } catch (aiErr: any) {
          // Bot already created as DRAFT — surface AI apply failure without discarding the bot.
          setBots(prev => (
            prev.some(b => b.id === saved.id)
              ? prev.map(b => (b.id === saved.id ? saved : b))
              : [...prev, saved]
          ));
          setFormOpen(false);
          resetForm();
          await loadMeta();
          setError(aiErr?.message || 'Bot saved, but AI tune settings could not be applied.');
          return;
        }
      }

      setBots(prev => {
        if (editingBotId != null) {
          return prev.map(b => (b.id === saved.id ? saved : b));
        }
        return prev.some(b => b.id === saved.id)
          ? prev.map(b => (b.id === saved.id ? saved : b))
          : [...prev, saved];
      });
      setFormOpen(false);
      resetForm();
      await loadMeta();
    } catch (err: any) {
      setError(err?.message || 'Failed to save activity bot.');
    } finally {
      setSaving(false);
    }
  };

  const handleStart = async (botId: number) => {
    setSaving(true);
    setError(null);
    try {
      await cryptoPortfolioService.startActivityBot(botId);
      await loadMeta();
      await onPortfolioRefresh();
      await refreshBotHistoryIfOpen(botId);
    } catch (err: any) {
      setError(err?.message || 'Failed to start activity bot.');
    } finally {
      setSaving(false);
    }
  };

  const handleRerun = async (botId: number) => {
    setSaving(true);
    setError(null);
    try {
      await cryptoPortfolioService.startActivityBot(botId);
      await loadMeta();
      await onPortfolioRefresh();
      await refreshBotHistoryIfOpen(botId);
    } catch (err: any) {
      setError(err?.message || 'Failed to re-run activity bot.');
    } finally {
      setSaving(false);
    }
  };

  const handleContinuousToggle = async (bot: CryptoPortfolioActivityBot, next: boolean) => {
    setTogglingContinuousId(bot.id);
    setError(null);
    try {
      await cryptoPortfolioService.setActivityBotContinuousRun(bot.id, next);
      await loadMeta();
      if (next) {
        await onPortfolioRefresh();
      }
      await refreshBotHistoryIfOpen(bot.id);
    } catch (err: any) {
      setError(err?.message || 'Failed to update continuous run setting.');
    } finally {
      setTogglingContinuousId(null);
    }
  };

  const handlePause = async (botId: number) => {
    setSaving(true);
    setError(null);
    try {
      await cryptoPortfolioService.pauseActivityBot(botId);
      await loadMeta();
      await refreshBotHistoryIfOpen(botId);
    } catch (err: any) {
      setError(err?.message || 'Failed to pause activity bot.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (botId: number) => {
    if (!window.confirm('Delete this activity bot configuration?')) return;
    setSaving(true);
    setError(null);
    try {
      await cryptoPortfolioService.deleteActivityBot(botId);
      if (editingBotId === botId) {
        setFormOpen(false);
        resetForm();
      }
      if (expandedHistoryBotId === botId) {
        setExpandedHistoryBotId(null);
      }
      setHistoryByBotId(prev => {
        const next = { ...prev };
        delete next[botId];
        return next;
      });
      await loadMeta();
    } catch (err: any) {
      setError(err?.message || 'Failed to delete activity bot.');
    } finally {
      setSaving(false);
    }
  };

  const handleCloneBot = async (bot: CryptoPortfolioActivityBot) => {
    setCloningBotId(bot.id);
    setError(null);
    try {
      await cryptoPortfolioService.cloneActivityBot(bot.id);
      await loadMeta();
    } catch (err: any) {
      setError(err?.message || 'Failed to clone activity bot.');
    } finally {
      setCloningBotId(null);
    }
  };

  const toggleBotHistory = async (botId: number) => {
    if (expandedHistoryBotId === botId) {
      setExpandedHistoryBotId(null);
      return;
    }
    setExpandedHistoryBotId(botId);
    setHistoryLoadingId(botId);
    setHistoryErrorByBotId(prev => {
      const next = { ...prev };
      delete next[botId];
      return next;
    });
    try {
      const rows = await cryptoPortfolioService.getActivityBotHistory(botId);
      setHistoryByBotId(prev => ({ ...prev, [botId]: rows }));
    } catch (err: any) {
      setHistoryErrorByBotId(prev => ({
        ...prev,
        [botId]: err?.message || 'Failed to load trade history.',
      }));
    } finally {
      setHistoryLoadingId(null);
    }
  };

  const toggleBotExpand = (botId: number) => {
    setExpandedBotId(prev => (prev === botId ? null : botId));
  };

  const refreshBotHistoryIfOpen = async (botId: number) => {
    if (expandedHistoryBotId !== botId) return;
    try {
      const rows = await cryptoPortfolioService.getActivityBotHistory(botId);
      setHistoryByBotId(prev => ({ ...prev, [botId]: rows }));
    } catch {
      // keep prior rows on refresh failure
    }
  };

  const fetchActivityBotLogs = useCallback(async (bot: CryptoPortfolioActivityBot, lines: number) => {
    setLogsLoading(true);
    setLogsError(null);
    try {
      const payload = await cryptoPortfolioService.getActivityBotLogs(bot.id, lines);
      setActivityBotLogs(payload.logs ?? []);
    } catch (err: any) {
      setLogsError(err?.message || 'Failed to load bot logs.');
    } finally {
      setLogsLoading(false);
    }
  }, []);

  const openAiTuneModal = (bot: CryptoPortfolioActivityBot) => {
    setAiTuneModalBot(bot);
  };

  const handleAiTuneBotUpdated = (updated: CryptoPortfolioActivityBot) => {
    setBots(prev => prev.map(b => (b.id === updated.id ? updated : b)));
    setAiTuneModalBot(updated);
  };

  const openLogsModal = (bot: CryptoPortfolioActivityBot) => {
    setLogsBot(bot);
    setLogsModalOpen(true);
    void fetchActivityBotLogs(bot, logsLineCount);
  };

  const activityBotRowHandlers = useMemo<ActivityBotRowHandlers>(() => ({
    canManage,
    saving,
    cloningBotId,
    togglingContinuousId,
    logsLoading,
    logsBotId: logsBot?.id ?? null,
    onStart: handleStart,
    onRerun: handleRerun,
    onPause: handlePause,
    onEdit: openEditForm,
    onClone: handleCloneBot,
    onExport: handleExportBot,
    onExportHistory: handleExportHistory,
    historyExportingBotId,
    onDelete: handleDelete,
    onAiTune: openAiTuneModal,
    onLogs: openLogsModal,
    onContinuousToggle: handleContinuousToggle,
    onToggleHistory: toggleBotHistory,
    expandedHistoryBotId,
    historyLoadingId,
    historyErrorByBotId,
    historyByBotId,
  }), [
    canManage,
    saving,
    cloningBotId,
    togglingContinuousId,
    logsLoading,
    logsBot,
    historyExportingBotId,
    expandedHistoryBotId,
    historyLoadingId,
    historyErrorByBotId,
    historyByBotId,
  ]);

  const closeLogsModal = () => {
    setLogsModalOpen(false);
    setLogsBot(null);
    setLogsError(null);
    setActivityBotLogs([]);
  };

  useEffect(() => {
    if (!logsModalOpen || !logsAutoRefresh || !logsBot) return undefined;
    const interval = window.setInterval(() => {
      void fetchActivityBotLogs(logsBot, logsLineCount);
    }, 5000);
    activityBotLogsIntervalRef.current = interval;
    return () => window.clearInterval(interval);
  }, [logsModalOpen, logsAutoRefresh, logsBot, logsLineCount, fetchActivityBotLogs]);

  useEffect(() => {
    if (!logsModalOpen || !logsBot) return;
    void fetchActivityBotLogs(logsBot, logsLineCount);
  }, [logsLineCount, logsModalOpen, logsBot, fetchActivityBotLogs]);

  const handleCloneProfile = async (profileId: number) => {
    setCloningProfileId(profileId);
    setError(null);
    try {
      const cloned = await cryptoPortfolioService.cloneFilterProfile(profileId);
      setFilterProfiles(prev => [...prev, cloned].sort((a, b) => a.name.localeCompare(b.name)));
      setFilterProfileId(cloned.id);
      setProfilesModalOpen(false);
      setProfilesModalView('list');
      setFiltersSectionExpanded(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to clone profile.');
    } finally {
      setCloningProfileId(null);
    }
  };

  const handleExportFilterProfile = (profile: TradingFilterProfile) => {
    try {
      downloadFilterProfileExport(profile);
      setProfileEditorError(null);
    } catch (err: any) {
      setProfileEditorError(err?.message || 'Failed to export profile.');
    }
  };

  const handleImportFilterProfileFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setImportingFilterProfile(true);
    setProfileEditorError(null);
    try {
      const raw = await file.text();
      const parsed = parseFilterProfileExportJson(raw);
      const payload = buildFilterProfileImportCreatePayload(
        parsed,
        filterProfiles.map(p => p.name),
      );
      const created = await cryptoPortfolioService.createFilterProfile(payload);
      const rows = await cryptoPortfolioService.listFilterProfiles();
      setFilterProfiles(rows);
      setFilterProfileId(created.id);
      setProfilesModalOpen(false);
      setProfilesModalView('list');
      setFiltersSectionExpanded(true);
    } catch (err: any) {
      setProfileEditorError(err?.message || 'Failed to import profile.');
    } finally {
      setImportingFilterProfile(false);
    }
  };

  const openProfilesModal = () => {
    setProfilesModalView('list');
    setProfileEditorReadOnly(false);
    setProfileEditorId(null);
    setProfileEditorDraft(emptyFilterProfileEditorDraft());
    setProfileEditorError(null);
    setExpandedProfileConfigIds(new Set());
    setProfilesModalOpen(true);
  };

  const toggleProfileConfigExpanded = (profileId: number) => {
    setExpandedProfileConfigIds(prev => {
      const next = new Set(prev);
      if (next.has(profileId)) next.delete(profileId);
      else next.add(profileId);
      return next;
    });
  };

  const openNewProfileEditor = () => {
    setProfileEditorId(null);
    setProfileEditorReadOnly(false);
    setProfileEditorDraft(emptyFilterProfileEditorDraft());
    setProfileEditorError(null);
    setProfilesModalView('edit');
  };

  const openEditProfileEditor = (profile: TradingFilterProfile) => {
    setProfileEditorId(profile.id);
    setProfileEditorReadOnly(false);
    setProfileEditorDraft({
      name: profile.name,
      description: profile.description ?? '',
      config: cloneFilterProfileConfig(profile.config),
    });
    setProfileEditorError(null);
    setProfilesModalView('edit');
  };

  const openViewProfileEditor = (profile: TradingFilterProfile) => {
    setProfileEditorId(profile.id);
    setProfileEditorReadOnly(true);
    setProfileEditorDraft({
      name: profile.name,
      description: profile.description ?? '',
      config: cloneFilterProfileConfig(profile.config),
    });
    setProfileEditorError(null);
    setProfilesModalView('edit');
  };

  const refreshFilterProfiles = async () => {
    const profileRows = await cryptoPortfolioService.listFilterProfiles();
    setFilterProfiles(profileRows);
    return profileRows;
  };

  const handleSaveProfile = async () => {
    const name = profileEditorDraft.name.trim();
    if (!name) {
      setProfileEditorError('Profile name is required.');
      return;
    }
    setSavingProfile(true);
    setProfileEditorError(null);
    try {
      const payload = {
        name,
        description: profileEditorDraft.description.trim() || null,
        config: profileEditorDraft.config,
      };
      const saved = profileEditorId == null
        ? await cryptoPortfolioService.createFilterProfile(payload)
        : await cryptoPortfolioService.updateFilterProfile(profileEditorId, payload);
      await refreshFilterProfiles();
      setFilterProfileId(saved.id);
      setProfilesModalView('list');
      setProfilesModalOpen(false);
      setFiltersSectionExpanded(true);
    } catch (err: any) {
      setProfileEditorError(err?.message || 'Failed to save profile.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleDeleteProfile = async (profileId: number) => {
    if (!window.confirm('Delete this custom profile? Activity bots using it will fall back to legacy entry behavior.')) {
      return;
    }
    setDeletingProfileId(profileId);
    setProfileEditorError(null);
    try {
      await cryptoPortfolioService.deleteFilterProfile(profileId);
      const rows = await refreshFilterProfiles();
      if (filterProfileId === profileId) {
        setFilterProfileId('');
      }
      setBots(prev => prev.map(bot => (
        bot.filterProfileId === profileId
          ? { ...bot, filterProfileId: null, filterProfileName: null }
          : bot
      )));
      if (profileEditorId === profileId) {
        setProfilesModalView('list');
        setProfileEditorId(null);
      }
      if (rows.length === 0) {
        setProfilesModalOpen(false);
      }
    } catch (err: any) {
      setProfileEditorError(err?.message || 'Failed to delete profile.');
    } finally {
      setDeletingProfileId(null);
    }
  };

  const renderActivityBotFormFields = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={fieldLabelStyle}>Reference name (optional)</label>
          <input
            type="text"
            maxLength={80}
            value={referenceName}
            onChange={e => setReferenceName(e.target.value)}
            placeholder="e.g. Gaming batch A, Layer1 test run"
            style={fieldInputStyle}
          />
        </div>
        <div>
          <label style={fieldLabelStyle}>1) KuCoin sub-account</label>
          <select
            value={kucoinConfigId === '' ? '' : String(kucoinConfigId)}
            onChange={e => {
              const val = e.target.value;
              setKucoinConfigId(val === '' ? '' : Number(val));
            }}
            style={{
              ...fieldInputStyle,
              border: kucoinConfigId === '' ? '1px solid #f59e0b' : fieldInputStyle.border,
            }}
          >
            <option value="">Select KuCoin account…</option>
            {accounts.map(acc => (
              <option key={acc.id} value={acc.id}>{acc.name}{acc.shared ? ' (shared)' : ''}</option>
            ))}
          </select>
          {kucoinConfigId === '' && (
            <p style={{ margin: '6px 0 0', fontSize: 11, color: '#b45309', lineHeight: 1.4 }}>
              Required — imported strategies never copy the source account.
            </p>
          )}
        </div>
        <ActivityBotSizingControls
          amountUsd={amountUsd}
          leverage={leverage}
          totalPositions={totalPositions}
          onAmountChange={setAmountUsd}
          onLeverageChange={setLeverage}
        />
        <div>
          <label style={fieldLabelStyle}>3) Industry sector</label>
          <select
            value={sectorId}
            onChange={e => {
              const next = e.target.value;
              setSectorId(next);
              if (next === 'COMMODITIES') {
                setMacroBtcBridgeEnabled(false);
              } else {
                setMacroCommodityBridgeEnabled(false);
              }
            }}
            style={fieldInputStyle}
          >
            {sectors.map(sector => (
              <option key={sector.id} value={sector.id}>{sector.label}</option>
            ))}
          </select>
          {(sectorId === 'COMMODITIES' || sectorId === 'FUTURES') && (
            <p style={{ margin: '6px 0 0', fontSize: 11, color: '#b45309', lineHeight: 1.45 }}>
              {sectorId === 'COMMODITIES' ? 'Commodities' : 'Stock index'} use their own universe.
              Crypto filter profiles (meme participation, BTC regime, oracle RSI) are skipped.
            </p>
          )}
        </div>
        <div>
          <label style={fieldLabelStyle}>4) Positions to open</label>
          <input type="number" min="1" step="1" value={totalPositions} onChange={e => setTotalPositions(e.target.value)} style={fieldInputStyle} />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <button
            type="button"
            onClick={() => setFiltersSectionExpanded(prev => !prev)}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid #e2e8f0',
              background: '#f8fafc',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 700,
              color: '#334155',
            }}
          >
            <span>Entry filter profile (optional)</span>
            <span style={{ color: '#94a3b8', fontWeight: 600 }}>{filtersSectionExpanded ? '▴' : '▾'}</span>
          </button>
          {filtersSectionExpanded && (
            <div style={{ marginTop: 10, padding: '12px 12px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff' }}>
              {filterProfilesEnabled && (
                <>
                  <label style={fieldLabelStyle}>Filter profile</label>
                  <select
                    value={filterProfileId}
                    onChange={e => {
                      const val = e.target.value;
                      setFilterProfileId(val === '' ? '' : Number(val));
                    }}
                    style={fieldInputStyle}
                  >
                    <option value="">None — HOT ranking only (current default)</option>
                    {filterProfiles.map(profile => (
                      <option key={profile.id} value={profile.id}>
                        {profile.name}{profile.systemTemplate ? ' (template)' : ''}
                      </option>
                    ))}
                  </select>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                    <button
                      type="button"
                      onClick={openProfilesModal}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 999,
                        border: '1px solid #e2e8f0',
                        background: '#fff',
                        fontSize: 12,
                        fontWeight: 600,
                        color: '#334155',
                        cursor: 'pointer',
                      }}
                    >
                      Manage profiles…
                    </button>
                  </div>
                  {filterProfileId === '' ? (
                    <>
                      <label style={{ ...fieldLabelStyle, marginTop: 12 }}>Entry timing mode (no profile)</label>
                      <select
                        value={entryMode}
                        onChange={e => setEntryMode(e.target.value as 'ACTIVITY_ONLY' | 'PEAK_TROUGH_TIMING')}
                        style={fieldInputStyle}
                      >
                        <option value="ACTIVITY_ONLY">Activity only — open from HOT ranking</option>
                        <option value="PEAK_TROUGH_TIMING">Peak/trough timing — require chart confirmation before open</option>
                      </select>
                      <div style={{ marginTop: 4, fontSize: 11, color: '#94a3b8' }}>
                        Peak/trough mode may skip a tick if no coin currently matches timing.
                      </div>
                    </>
                  ) : (
                    <div style={{ marginTop: 12, fontSize: 12, color: '#475569' }}>
                      <div style={{ fontWeight: 700, color: '#64748b', marginBottom: 6 }}>Profile configuration</div>
                      <FilterProfileConfigDetails config={selectedFilterProfile?.config} compact />
                      <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8' }}>
                        Legacy entry timing is replaced by the selected profile. Existing bots without a profile keep current behavior.
                      </div>
                    </div>
                  )}
                </>
              )}
              {sectorId !== 'COMMODITIES' && (
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    marginTop: filterProfilesEnabled ? 12 : 0,
                    fontSize: 13,
                    color: '#475569',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={macroBtcBridgeEnabled}
                    onChange={e => setMacroBtcBridgeEnabled(e.target.checked)}
                    style={{ width: 14, height: 14, marginTop: 2, accentColor: '#086afb' }}
                  />
                  <span>
                    <strong style={{ color: '#334155' }}>Use Market Intelligence macro BTC bias</strong>
                    <span style={{ display: 'block', marginTop: 4, fontSize: 11, color: '#94a3b8', lineHeight: 1.45 }}>
                      When on: bearish MI moods can block LONG opens; SHORTs open only on clear BEARISH bias
                      (frozen / unclear mood still skips shorts). Separate from crypto/meme AI regime. Not used for Commodities.
                    </span>
                  </span>
                </label>
              )}
              {sectorId === 'COMMODITIES' && (
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    marginTop: filterProfilesEnabled ? 12 : 0,
                    fontSize: 13,
                    color: '#475569',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={macroCommodityBridgeEnabled}
                    onChange={e => setMacroCommodityBridgeEnabled(e.target.checked)}
                    style={{ width: 14, height: 14, marginTop: 2, accentColor: '#086afb' }}
                  />
                  <span>
                    <strong style={{ color: '#334155' }}>Use Market Intelligence macro commodity bias</strong>
                    <span style={{ display: 'block', marginTop: 4, fontSize: 11, color: '#94a3b8', lineHeight: 1.45 }}>
                      When on, MI macro skips specific commodity LONG/SHORT legs (e.g. risk-off: skip LONG copper,
                      skip SHORT gold). Does not blanket-block all longs.
                    </span>
                  </span>
                </label>
              )}
            </div>
          )}
        </div>
        <div>
          <label style={fieldLabelStyle}>5) Long positions</label>
          <input type="number" min="0" step="1" value={longCount} onChange={e => setLongCount(e.target.value)} style={fieldInputStyle} />
        </div>
        <div>
          <label style={fieldLabelStyle}>5) Short positions</label>
          <input type="number" min="0" step="1" value={shortCount} onChange={e => setShortCount(e.target.value)} style={fieldInputStyle} />
        </div>
        <div>
          <label style={fieldLabelStyle}>6) Target profit (%)</label>
          <input type="number" min="0.1" step="0.1" value={targetProfitPct} onChange={e => setTargetProfitPct(e.target.value)} style={fieldInputStyle} />
        </div>
        <div>
          <label style={fieldLabelStyle}>7) Stop loss per leg (% on size)</label>
          <input
            type="number"
            min="0.1"
            max="100"
            step="0.1"
            value={stopLossPct}
            onChange={e => setStopLossPct(e.target.value)}
            placeholder="10 — empty to disable"
            style={fieldInputStyle}
          />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 13,
              color: '#475569',
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <input
              type="checkbox"
              checked={trailingStopEnabled}
              onChange={e => setTrailingStopEnabled(e.target.checked)}
              style={{ width: 14, height: 14, accentColor: '#086afb' }}
            />
            <span>Trailing stop — lock in gains after activation (checked every ~60s)</span>
          </label>
        </div>
        {trailingStopEnabled && (
          <>
            <div>
              <label style={fieldLabelStyle}>Trail activation (% on size)</label>
              <input type="number" min="0.1" step="0.1" value={trailActivationPct} onChange={e => setTrailActivationPct(e.target.value)} style={fieldInputStyle} />
            </div>
            <div>
              <label style={fieldLabelStyle}>Trail distance (% from peak)</label>
              <input type="number" min="0.1" step="0.1" value={trailDistancePct} onChange={e => setTrailDistancePct(e.target.value)} style={fieldInputStyle} />
            </div>
          </>
        )}
        <div>
          <label style={fieldLabelStyle}>Max bot loss (USD, this cycle)</label>
          <input
            type="number"
            min="1"
            step="1"
            value={maxBotLossUsd}
            onChange={e => setMaxBotLossUsd(e.target.value)}
            placeholder="e.g. 100 — optional"
            style={fieldInputStyle}
          />
        </div>
        <div>
          <label style={fieldLabelStyle}>Max bot loss (% of deployed)</label>
          <input
            type="number"
            min="0.1"
            max="100"
            step="0.1"
            value={maxBotLossPct}
            onChange={e => setMaxBotLossPct(e.target.value)}
            placeholder="e.g. 15 — optional"
            style={fieldInputStyle}
          />
        </div>
        <div style={{ gridColumn: '1 / -1', fontSize: 11, color: '#94a3b8', lineHeight: 1.5 }}>
          Per-leg stop loss and trailing apply to each position this bot opens. Max bot loss pauses the bot and closes all open legs when combined cycle P&amp;L breaches your USD or % limit (whichever hits first).
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 13,
              color: '#475569',
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <input
              type="checkbox"
              checked={continuousRun}
              onChange={e => setContinuousRun(e.target.checked)}
              style={{ width: 14, height: 14, accentColor: '#086afb' }}
            />
            <span>
              Continuous run — automatically start a new cycle when the current one finishes and all legs are closed
            </span>
          </label>
        </div>
      </div>

      {!splitValid && (
        <p style={{ margin: '10px 0 0', fontSize: 12, color: '#b91c1c' }}>
          Long + short must equal total positions.
        </p>
      )}

      {stopWiderThanTarget && (
        <p
          style={{
            margin: '10px 0 0',
            fontSize: 12,
            color: '#b45309',
            background: '#fffbeb',
            padding: '8px 10px',
            borderRadius: 8,
            border: '1px solid #fde68a',
            lineHeight: 1.5,
          }}
        >
          Stop loss ({parsedStopLoss}%) is wider than target profit ({parsedTargetProfit}%). You can still save — this is allowed for tighter risk control on losing legs.
        </p>
      )}

      <div style={{ marginTop: 14, padding: '12px 14px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>
            Preview picks (Coin Activity Breakdown)
          </div>
          {previewMarketRegime && (
            <div style={{ fontSize: 11, color: '#94a3b8' }}>
              Market regime: <strong style={{ color: '#475569' }}>{previewMarketRegime}</strong>
            </div>
          )}
        </div>
        {previewLoading ? (
          <div style={{ fontSize: 12, color: '#94a3b8' }}>Loading preview…</div>
        ) : previewLegs.length === 0 ? (
          <div style={{ fontSize: 12, color: '#94a3b8' }}>Adjust settings to preview HOT coin picks.</div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {previewLegs.map(leg => (
              <div
                key={`${leg.symbol}-${leg.side}`}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  padding: '8px 10px',
                  borderRadius: 8,
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      padding: '3px 10px',
                      borderRadius: 999,
                      background: leg.side === 'LONG' ? '#dcfce7' : '#fee2e2',
                      color: leg.side === 'LONG' ? '#15803d' : '#b91c1c',
                    }}
                  >
                    {leg.side} {leg.symbol}{leg.hot ? ' 🔥' : ''}
                  </span>
                  {leg.change24h != null && (
                    <span style={{ fontSize: 11, color: '#64748b' }}>
                      {leg.change24h >= 0 ? '+' : ''}{Number(leg.change24h).toFixed(1)}%
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11, textAlign: 'right' }}>
                  <div style={{ fontWeight: 700, color: filterStatusColor(leg.filterOverall) }}>
                    {leg.filterOverall || '—'}
                  </div>
                  {leg.filterMessage && (
                    <div style={{ color: '#94a3b8', marginTop: 2, maxWidth: 280 }}>{leg.filterMessage}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );

  const renderActivityBotDrawer = () => {
    if (!formOpen || !canManage) return null;

    const isImportDraft = editingBotId == null && importNotes.length > 0;
    const drawerTitle = editingBotId != null
      ? 'Edit activity bot'
      : isImportDraft
        ? 'Import activity bot'
        : 'New activity bot';
    const drawerSubtitle = editingBot
      ? activityBotCardSubtitle(editingBot) ?? `${editingBot.sectorLabel || editingBot.sectorId} · ${editingBot.kucoinAccountName || 'KuCoin'}`
      : isImportDraft
        ? 'Review imported config, pick a KuCoin account, then save as DRAFT'
        : 'Configure sub-account, size, sector, and safeguards';

    return (
      <>
        <div
          role="presentation"
          onClick={closeFormDrawer}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            zIndex: 1200,
          }}
        />
        <aside
          role="dialog"
          aria-modal="true"
          aria-labelledby="activity-bot-drawer-title"
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            height: '100%',
            width: 'min(520px, 100vw)',
            background: '#fff',
            boxShadow: '-8px 0 32px rgba(0,0,0,0.12)',
            zIndex: 1201,
            display: 'flex',
            flexDirection: 'column',
            fontFamily: 'Nunito, system-ui, sans-serif',
          }}
        >
          <div
            style={{
              padding: '20px 20px 16px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 12,
              flexShrink: 0,
            }}
          >
            <div>
              <div id="activity-bot-drawer-title" style={{ fontSize: 18, fontWeight: 800, color: '#1e293b' }}>
                {drawerTitle}
              </div>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 4, lineHeight: 1.4 }}>
                {drawerSubtitle}
              </div>
            </div>
            <button
              type="button"
              onClick={closeFormDrawer}
              disabled={saving}
              aria-label="Close"
              style={{
                border: 'none',
                background: '#f1f5f9',
                borderRadius: 8,
                width: 32,
                height: 32,
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: 18,
                lineHeight: 1,
                color: '#475569',
                flexShrink: 0,
              }}
            >
              ×
            </button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px 24px' }}>
            {error && (
              <div
                role="alert"
                style={{
                  marginBottom: 14,
                  padding: '10px 12px',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: 8,
                  color: '#b91c1c',
                  fontSize: 12,
                }}
              >
                {error}
              </div>
            )}
            {isImportDraft && (
              <div
                role="status"
                style={{
                  marginBottom: 14,
                  padding: '10px 12px',
                  background: '#fffbeb',
                  border: '1px solid #fcd34d',
                  borderRadius: 8,
                  color: '#92400e',
                  fontSize: 12,
                  lineHeight: 1.45,
                }}
              >
                <div style={{ fontWeight: 700, marginBottom: 4 }}>Import checklist</div>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {importNotes.map(note => (
                    <li key={note}>{note}</li>
                  ))}
                </ul>
              </div>
            )}
            {renderActivityBotFormFields()}
          </div>

          <div
            style={{
              padding: '14px 20px',
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              gap: 8,
              flexWrap: 'wrap',
              flexShrink: 0,
              background: '#fff',
            }}
          >
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '8px 16px',
                borderRadius: 999,
                border: 'none',
                background: '#086afb',
                color: '#fff',
                fontWeight: 700,
                fontSize: 12,
                cursor: saving ? 'wait' : 'pointer',
              }}
            >
              {saving
                ? 'Saving…'
                : editingBotId != null
                  ? 'Save changes'
                  : importNotes.length > 0
                    ? 'Save as DRAFT'
                    : 'Save bot'}
            </button>
            {editingBotId != null && (
              <button
                type="button"
                onClick={() => {
                  const bot = bots.find(b => b.id === editingBotId);
                  if (bot) {
                    closeFormDrawer();
                    openAiTuneModal(bot);
                  }
                }}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  borderRadius: 999,
                  border: '1px solid #c4b5fd',
                  background: '#faf5ff',
                  color: '#6d28d9',
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: saving ? 'wait' : 'pointer',
                }}
              >
                🤖 AI Tune
              </button>
            )}
            <button
              type="button"
              onClick={closeFormDrawer}
              disabled={saving}
              style={{
                padding: '8px 16px',
                borderRadius: 999,
                border: '1px solid #e2e8f0',
                background: '#fff',
                color: '#334155',
                fontWeight: 600,
                fontSize: 12,
                cursor: saving ? 'wait' : 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </aside>
      </>
    );
  };

  const renderActivityBotLogsModal = () => {
    if (!logsModalOpen || !logsBot) return null;
    const botTitle = logsBot.referenceName?.trim() || logsBot.displayLabel || `Bot #${logsBot.id}`;

    return (
      <div
        role="dialog"
        aria-modal="true"
        onClick={closeLogsModal}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.45)',
          zIndex: 1400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
        }}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{
            width: 'min(1100px, 96vw)',
            maxHeight: '88vh',
            borderRadius: 12,
            background: '#fff',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 20px 45px rgba(2, 6, 23, 0.22)',
          }}
        >
          <div style={{ padding: '16px 18px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#1e293b' }}>AI Activity Bot Logs</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                Viewing logs for <strong style={{ color: '#334155' }}>{botTitle}</strong>
              </div>
            </div>
            <button
              type="button"
              onClick={closeLogsModal}
              style={{ border: 'none', background: 'transparent', fontSize: 24, color: '#64748b', lineHeight: 1, cursor: 'pointer' }}
            >
              ×
            </button>
          </div>

          <div style={{ padding: '10px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 12, color: '#475569', fontWeight: 600 }}>
              Lines:
              <select
                value={logsLineCount}
                onChange={e => setLogsLineCount(Number(e.target.value))}
                style={{ marginLeft: 8, padding: '5px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }}
              >
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
                <option value={1000}>1000</option>
              </select>
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569' }}>
              <input type="checkbox" checked={logsAutoRefresh} onChange={e => setLogsAutoRefresh(e.target.checked)} />
              Auto-refresh (5s)
            </label>
            <button
              type="button"
              onClick={() => void fetchActivityBotLogs(logsBot, logsLineCount)}
              disabled={logsLoading}
              style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
            >
              {logsLoading ? 'Refreshing…' : 'Refresh'}
            </button>
            <button
              type="button"
              onClick={() => navigator.clipboard.writeText(activityBotLogs.join('\n'))}
              disabled={activityBotLogs.length === 0}
              style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
            >
              Copy
            </button>
          </div>

          <div style={{ flex: 1, minHeight: 220, background: '#0f172a', color: '#e2e8f0', overflow: 'auto', padding: '12px 14px', fontFamily: 'Monaco, Menlo, \"Courier New\", monospace', fontSize: 12, lineHeight: 1.5 }}>
            {logsError ? (
              <div style={{ color: '#fecaca' }}>{logsError}</div>
            ) : logsLoading && activityBotLogs.length === 0 ? (
              <div style={{ color: '#94a3b8' }}>Loading logs…</div>
            ) : activityBotLogs.length === 0 ? (
              <div style={{ color: '#94a3b8' }}>No logs found for this activity bot in the selected tail window.</div>
            ) : (
              activityBotLogs.map((line, idx) => (
                <div key={`${idx}-${line.slice(0, 40)}`} style={{ wordBreak: 'break-word', color: line.includes('ERROR') ? '#fca5a5' : line.includes('WARN') ? '#fcd34d' : '#e2e8f0' }}>
                  {line}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ ...cardStyle, marginTop: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>AI Activity Bot</div>
          <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 4 }}>
            Auto-open direct positions from Coin Activity Breakdown (HOT coins in selected sector).
            Status is the opening cycle: RUNNING = hunting entries; filter reasons (e.g. MICRO-REVERSAL) show in Status when blocked; MONITORING = open legs managed; WAITING = continuous idle between cycles.
          </div>
        </div>
        {canManage && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              ref={importFileInputRef}
              type="file"
              accept="application/json,.json"
              style={{ display: 'none' }}
              onChange={handleImportFileChange}
            />
            <button
              type="button"
              onClick={() => importFileInputRef.current?.click()}
              style={{
                padding: '6px 14px',
                borderRadius: 999,
                border: '1px solid #cbd5e1',
                background: '#fff',
                color: '#334155',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Import JSON
            </button>
            <button
              type="button"
              onClick={() => void handleExportAllHistories()}
              disabled={exportingAllHistories || bots.length === 0}
              title="Download trade history for all activity bots as one CSV"
              style={{
                padding: '6px 14px',
                borderRadius: 999,
                border: '1px solid #cbd5e1',
                background: '#fff',
                color: exportingAllHistories || bots.length === 0 ? '#94a3b8' : '#334155',
                fontSize: 12,
                fontWeight: 700,
                cursor: exportingAllHistories || bots.length === 0 ? 'not-allowed' : 'pointer',
              }}
            >
              {exportingAllHistories ? 'Exporting histories…' : 'Export all histories CSV'}
            </button>
            <button
              type="button"
              onClick={openCreateForm}
              style={{
                padding: '6px 14px',
                borderRadius: 999,
                border: 'none',
                background: '#086afb',
                color: '#fff',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              + Create bot
            </button>
          </div>
        )}
      </div>

      {error && !formOpen && (
        <div style={{ marginTop: 12, padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#b91c1c', fontSize: 12 }}>
          {error}
        </div>
      )}

      {loadingMeta ? (
        <p style={{ margin: '14px 0 0', fontSize: 13, color: '#94a3b8' }}>Loading bot options…</p>
      ) : bots.length === 0 && !formOpen ? (
        <p style={{ margin: '14px 0 0', fontSize: 13, color: '#64748b' }}>
          Configure a bot with sub-account, size, sector, long/short split, and target profit. It opens direct trades only.
        </p>
      ) : isMobileBots ? (
        <ActivityBotMobileList
          bots={bots}
          expandedBotId={expandedBotId}
          onToggleExpand={toggleBotExpand}
          handlers={activityBotRowHandlers}
          challengeBotIds={challengeBotIds}
        />
      ) : (
        <ActivityBotListTable
          bots={bots}
          expandedBotId={expandedBotId}
          onToggleExpand={toggleBotExpand}
          handlers={activityBotRowHandlers}
          challengeBotIds={challengeBotIds}
        />
      )}

      {renderActivityBotDrawer()}
      {renderActivityBotLogsModal()}
      {aiTuneModalBot && (
        <ActivityBotAiAutoTuneModal
          bot={aiTuneModalBot}
          open={Boolean(aiTuneModalBot)}
          onClose={() => setAiTuneModalBot(null)}
          onBotUpdated={handleAiTuneBotUpdated}
        />
      )}

      {profilesModalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 1300,
          }}
          onClick={() => {
            setProfilesModalOpen(false);
            setProfilesModalView('list');
            setProfileEditorReadOnly(false);
          }}
        >
          <div
            style={{
              ...cardStyle,
              width: '100%',
              maxWidth: 620,
              maxHeight: '85vh',
              overflow: 'auto',
              padding: 18,
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 16, color: '#1e293b' }}>
                  {profilesModalView === 'edit'
                    ? (profileEditorReadOnly
                      ? 'View filter profile'
                      : (profileEditorId == null ? 'Create filter profile' : 'Edit filter profile'))
                    : 'Filter profiles'}
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                  {profilesModalView === 'edit'
                    ? (profileEditorReadOnly
                      ? 'Read-only view of this profile’s settings. Clone a system template to customize it.'
                      : 'Customize entry, market, and regime gates. System templates stay read-only — clone first to edit.')
                    : 'Reusable entry filters for activity bots. System templates are read-only and cannot be deleted. Export/import shares custom configs as JSON.'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setProfilesModalOpen(false);
                  setProfilesModalView('list');
                  setProfileEditorReadOnly(false);
                }}
                style={{
                  border: 'none',
                  background: 'transparent',
                  fontSize: 18,
                  color: '#94a3b8',
                  cursor: 'pointer',
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>

            {profileEditorError && (
              <div
                role="alert"
                style={{
                  marginBottom: 12,
                  padding: '10px 12px',
                  borderRadius: 8,
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  color: '#b91c1c',
                  fontSize: 12,
                }}
              >
                {profileEditorError}
              </div>
            )}

            {profilesModalView === 'edit' ? (
              <>
                <FilterProfileEditorForm
                  draft={profileEditorDraft}
                  onChange={setProfileEditorDraft}
                  disabled={savingProfile || profileEditorReadOnly}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
                  {!profileEditorReadOnly && (
                    <button
                      type="button"
                      disabled={savingProfile}
                      onClick={handleSaveProfile}
                      style={{
                        padding: '8px 16px',
                        borderRadius: 999,
                        border: 'none',
                        background: '#086afb',
                        color: '#fff',
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: savingProfile ? 'wait' : 'pointer',
                        opacity: savingProfile ? 0.7 : 1,
                      }}
                    >
                      {savingProfile ? 'Saving…' : 'Save profile'}
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={savingProfile}
                    onClick={() => {
                      setProfilesModalView('list');
                      setProfileEditorReadOnly(false);
                      setProfileEditorError(null);
                    }}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 999,
                      border: '1px solid #e2e8f0',
                      background: '#fff',
                      color: '#334155',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Back to list
                  </button>
                  {!profileEditorReadOnly && profileEditorId != null && (
                    <button
                      type="button"
                      disabled={savingProfile || deletingProfileId === profileEditorId}
                      onClick={() => handleDeleteProfile(profileEditorId)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: 999,
                        border: '1px solid #fecaca',
                        background: '#fff',
                        color: '#b91c1c',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: deletingProfileId === profileEditorId ? 'wait' : 'pointer',
                        marginLeft: 'auto',
                      }}
                    >
                      {deletingProfileId === profileEditorId ? 'Deleting…' : 'Delete profile'}
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={openNewProfileEditor}
                    style={{
                      padding: '8px 14px',
                      borderRadius: 999,
                      border: 'none',
                      background: '#086afb',
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    + Create custom profile
                  </button>
                  <button
                    type="button"
                    disabled={importingFilterProfile}
                    onClick={() => filterProfileImportInputRef.current?.click()}
                    style={{
                      padding: '8px 14px',
                      borderRadius: 999,
                      border: '1px solid #e2e8f0',
                      background: '#fff',
                      color: '#334155',
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: importingFilterProfile ? 'wait' : 'pointer',
                      opacity: importingFilterProfile ? 0.7 : 1,
                    }}
                  >
                    {importingFilterProfile ? 'Importing…' : 'Import JSON'}
                  </button>
                  <input
                    ref={filterProfileImportInputRef}
                    type="file"
                    accept="application/json,.json"
                    style={{ display: 'none' }}
                    onChange={e => void handleImportFilterProfileFile(e)}
                  />
                </div>
                <div style={{ display: 'grid', gap: 10 }}>
                  {filterProfiles.map(profile => {
                    const configExpanded = expandedProfileConfigIds.has(profile.id);
                    return (
                    <div
                      key={profile.id}
                      style={{
                        padding: '12px 14px',
                        border: '1px solid #e2e8f0',
                        borderRadius: 10,
                        background: '#f8fafc',
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>
                        {profile.name}
                        {profile.systemTemplate && (
                          <span style={{ marginLeft: 8, fontSize: 11, color: '#64748b' }}>(system template)</span>
                        )}
                      </div>
                      {profile.description && (
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{profile.description}</div>
                      )}
                      <button
                        type="button"
                        onClick={() => toggleProfileConfigExpanded(profile.id)}
                        aria-expanded={configExpanded}
                        style={{
                          width: '100%',
                          marginTop: 10,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 8,
                          padding: '8px 10px',
                          borderRadius: 8,
                          border: '1px solid #e2e8f0',
                          background: '#fff',
                          cursor: 'pointer',
                          fontSize: 12,
                          fontWeight: 700,
                          color: '#334155',
                          textAlign: 'left',
                        }}
                      >
                        <span>Filter configuration</span>
                        <span style={{ color: '#94a3b8', fontWeight: 600, flexShrink: 0 }}>{configExpanded ? '▴' : '▾'}</span>
                      </button>
                      {configExpanded ? (
                        <FilterProfileConfigDetails config={profile.config} compact />
                      ) : (
                        <div style={{ marginTop: 8, fontSize: 12, color: '#64748b', lineHeight: 1.45 }}>
                          {summarizeFilterProfileBrief(profile.config)}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => {
                            setFilterProfileId(profile.id);
                            setProfilesModalOpen(false);
                            setProfilesModalView('list');
                            setFiltersSectionExpanded(true);
                          }}
                          style={{
                            padding: '6px 12px',
                            borderRadius: 999,
                            border: 'none',
                            background: '#086afb',
                            color: '#fff',
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          Use profile
                        </button>
                        <button
                          type="button"
                          onClick={() => handleExportFilterProfile(profile)}
                          style={{
                            padding: '6px 12px',
                            borderRadius: 999,
                            border: '1px solid #e2e8f0',
                            background: '#fff',
                            color: '#334155',
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          Export JSON
                        </button>
                        {profile.systemTemplate ? (
                          <>
                            <button
                              type="button"
                              onClick={() => openViewProfileEditor(profile)}
                              style={{
                                padding: '6px 12px',
                                borderRadius: 999,
                                border: '1px solid #e2e8f0',
                                background: '#fff',
                                color: '#334155',
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: 'pointer',
                              }}
                            >
                              View full form
                            </button>
                            <button
                              type="button"
                              disabled={cloningProfileId === profile.id}
                              onClick={() => handleCloneProfile(profile.id)}
                              style={{
                                padding: '6px 12px',
                                borderRadius: 999,
                                border: '1px solid #e2e8f0',
                                background: '#fff',
                                color: '#334155',
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: cloningProfileId === profile.id ? 'wait' : 'pointer',
                              }}
                            >
                              {cloningProfileId === profile.id ? 'Cloning…' : 'Clone to my profiles'}
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => openEditProfileEditor(profile)}
                              style={{
                                padding: '6px 12px',
                                borderRadius: 999,
                                border: '1px solid #e2e8f0',
                                background: '#fff',
                                color: '#334155',
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: 'pointer',
                              }}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              disabled={deletingProfileId === profile.id}
                              onClick={() => handleDeleteProfile(profile.id)}
                              style={{
                                padding: '6px 12px',
                                borderRadius: 999,
                                border: '1px solid #fecaca',
                                background: '#fff',
                                color: '#b91c1c',
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: deletingProfileId === profile.id ? 'wait' : 'pointer',
                              }}
                            >
                              {deletingProfileId === profile.id ? 'Deleting…' : 'Delete'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ProfitTargetCard: React.FC<ProfitTargetCardProps> = ({ target, onEdit }) => {
  const configured = Boolean(target?.configured);
  const progress = Number(target?.progressPct ?? 0);
  const achieved = Boolean(target?.achieved);
  const barPct = clampProgress(progress);
  const barColor = achieved ? '#0dca9d' : '#086afb';

  return (
    <div style={{ ...cardStyle }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>Profit target</div>
          <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 4 }}>
            Unrealized P&L on open legs vs your % goal on total open value
          </div>
        </div>
        <button
          type="button"
          onClick={onEdit}
          style={{
            padding: '6px 14px',
            borderRadius: 999,
            border: '1px solid #e2e8f0',
            background: '#fff',
            color: '#334155',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {configured ? 'Edit target' : 'Set target'}
        </button>
      </div>

      {!configured ? (
        <p style={{ margin: '14px 0 0', fontSize: 13, color: '#64748b' }}>
          Set a percentage goal (e.g. 10%) and track progress toward your profit target.
        </p>
      ) : (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px 28px', marginTop: 14, fontSize: 13 }}>
            <div>
              <span style={{ color: '#94a3b8' }}>Goal: </span>
              <strong style={{ color: '#1e293b' }}>+{Number(target?.targetPct ?? 0).toFixed(1)}%</strong>
              <span style={{ color: '#64748b' }}> → {fmtUsd(target?.targetAmountUsd)}</span>
            </div>
            <div>
              <span style={{ color: '#94a3b8' }}>Unrealized P&L: </span>
              <strong style={{ color: Number(target?.currentNetPnl ?? 0) >= 0 ? '#15803d' : '#b91c1c' }}>
                {fmtPnl(target?.currentNetPnl)}
              </strong>
              <span style={{ color: '#64748b' }}> ({progress.toFixed(1)}% of goal)</span>
            </div>
            {!achieved && Number(target?.baseOpenValue ?? 0) > 0 && (
              <div>
                <span style={{ color: '#94a3b8' }}>Remaining: </span>
                <strong style={{ color: '#1e293b' }}>{fmtUsd(target?.remainingUsd)}</strong>
              </div>
            )}
            {achieved && (
              <div>
                <span style={{ color: '#15803d', fontWeight: 700 }}>Target reached</span>
              </div>
            )}
          </div>
          <div style={{ marginTop: 12, height: 8, background: '#e2e8f0', borderRadius: 999, overflow: 'hidden' }}>
            <div
              style={{
                width: `${barPct}%`,
                height: '100%',
                background: barColor,
                borderRadius: 999,
                transition: 'width 0.3s ease',
              }}
            />
          </div>
          {Number(target?.baseOpenValue ?? 0) <= 0 && (
            <p style={{ margin: '10px 0 0', fontSize: 12, color: '#94a3b8' }}>
              Open at least one position to calculate target progress.
            </p>
          )}
        </>
      )}
    </div>
  );
};

type ProfitCoinSourceFilter = 'ALL' | 'ACTIVITY_BOT' | 'SINGLE' | 'TRADING_BOT';
type ProfitCoinSort = 'netDesc' | 'netAsc' | 'symbol' | 'trades';

const PROFIT_COIN_TOP_DEFAULT = 10;

interface CoinProfitAggregate {
  symbol: string;
  positionCount: number;
  activityBotCount: number;
  singleCount: number;
  tradingBotCount: number;
  realizedPnl: number;
  unrealizedPnl: number;
  netPnl: number;
}

function getPositionProfitCategory(row: CryptoPortfolioPositionRow): Exclude<ProfitCoinSourceFilter, 'ALL'> {
  if (row.source === 'BOT') return 'TRADING_BOT';
  if (row.activityBotId != null || row.activityBotLabel) return 'ACTIVITY_BOT';
  return 'SINGLE';
}

function matchesProfitCoinSourceFilter(
  row: CryptoPortfolioPositionRow,
  filter: ProfitCoinSourceFilter,
): boolean {
  if (filter === 'ALL') return true;
  return getPositionProfitCategory(row) === filter;
}

function buildCoinProfitSourceLabel(row: CoinProfitAggregate): string {
  const parts: string[] = [];
  if (row.activityBotCount > 0) {
    parts.push(`${row.activityBotCount} activity bot${row.activityBotCount === 1 ? '' : 's'}`);
  }
  if (row.singleCount > 0) {
    parts.push(`${row.singleCount} single trade${row.singleCount === 1 ? '' : 's'}`);
  }
  if (row.tradingBotCount > 0) {
    parts.push(`${row.tradingBotCount} trading bot${row.tradingBotCount === 1 ? '' : 's'}`);
  }
  return parts.join(' · ') || '—';
}

function aggregateCoinProfits(
  openPositions: CryptoPortfolioPositionRow[],
  closedPositions: CryptoPortfolioPositionRow[],
  sourceFilter: ProfitCoinSourceFilter,
): CoinProfitAggregate[] {
  const bySymbol = new Map<string, CoinProfitAggregate>();

  const addRow = (row: CryptoPortfolioPositionRow, isOpen: boolean) => {
    if (!matchesProfitCoinSourceFilter(row, sourceFilter)) return;
    const symbol = String(row.symbol ?? '').trim().toUpperCase();
    if (!symbol) return;

    const category = getPositionProfitCategory(row);
    const existing = bySymbol.get(symbol) ?? {
      symbol,
      positionCount: 0,
      activityBotCount: 0,
      singleCount: 0,
      tradingBotCount: 0,
      realizedPnl: 0,
      unrealizedPnl: 0,
      netPnl: 0,
    };

    existing.positionCount += 1;
    if (category === 'ACTIVITY_BOT') existing.activityBotCount += 1;
    else if (category === 'SINGLE') existing.singleCount += 1;
    else existing.tradingBotCount += 1;

    if (isOpen) {
      const unrealized = Number(row.unrealizedPnl ?? 0);
      if (Number.isFinite(unrealized)) existing.unrealizedPnl += unrealized;
    } else {
      const realized = Number(row.realizedPnl ?? 0);
      if (Number.isFinite(realized)) existing.realizedPnl += realized;
    }

    existing.netPnl = existing.realizedPnl + existing.unrealizedPnl;
    bySymbol.set(symbol, existing);
  };

  for (const row of openPositions) addRow(row, true);
  for (const row of closedPositions) addRow(row, false);

  return Array.from(bySymbol.values());
}

function sortCoinProfitRows(rows: CoinProfitAggregate[], sort: ProfitCoinSort): CoinProfitAggregate[] {
  const sorted = [...rows];
  sorted.sort((a, b) => {
    switch (sort) {
      case 'netAsc':
        return a.netPnl - b.netPnl;
      case 'symbol':
        return a.symbol.localeCompare(b.symbol);
      case 'trades':
        return b.positionCount - a.positionCount || b.netPnl - a.netPnl;
      case 'netDesc':
      default:
        return b.netPnl - a.netPnl;
    }
  });
  return sorted;
}

interface ProfitByCoinCardProps {
  openPositions: CryptoPortfolioPositionRow[];
  closedPositions: CryptoPortfolioPositionRow[];
  isMobile: boolean;
  onViewLegs: (symbol: string) => void;
}

const profitCoinFilterBtn = (active: boolean): React.CSSProperties => ({
  padding: '5px 12px',
  borderRadius: 999,
  border: `1px solid ${active ? '#086afb' : '#e2e8f0'}`,
  background: active ? '#086afb' : '#fff',
  color: active ? '#fff' : '#64748b',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
});

const ProfitByCoinCard: React.FC<ProfitByCoinCardProps> = ({
  openPositions,
  closedPositions,
  isMobile,
  onViewLegs,
}) => {
  const [sourceFilter, setSourceFilter] = useState<ProfitCoinSourceFilter>('ALL');
  const [sort, setSort] = useState<ProfitCoinSort>('netDesc');
  const [showAll, setShowAll] = useState(false);

  const allPositions = useMemo(
    () => [...(openPositions ?? []), ...(closedPositions ?? [])],
    [openPositions, closedPositions],
  );

  const coinRows = useMemo(
    () => sortCoinProfitRows(
      aggregateCoinProfits(openPositions ?? [], closedPositions ?? [], sourceFilter),
      sort,
    ),
    [openPositions, closedPositions, sourceFilter, sort],
  );

  const totalNetPnl = useMemo(
    () => coinRows.reduce((sum, row) => sum + row.netPnl, 0),
    [coinRows],
  );

  const totalPositions = useMemo(
    () => coinRows.reduce((sum, row) => sum + row.positionCount, 0),
    [coinRows],
  );

  const shareDenominator = useMemo(
    () => coinRows.reduce((sum, row) => sum + Math.abs(row.netPnl), 0),
    [coinRows],
  );

  const visibleRows = showAll ? coinRows : coinRows.slice(0, PROFIT_COIN_TOP_DEFAULT);
  const hiddenCount = Math.max(0, coinRows.length - PROFIT_COIN_TOP_DEFAULT);

  if (allPositions.length === 0) return null;

  const renderShareBar = (row: CoinProfitAggregate) => {
    const pct = shareDenominator > 0 ? (Math.abs(row.netPnl) / shareDenominator) * 100 : 0;
    const positive = row.netPnl >= 0;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: isMobile ? undefined : 140 }}>
        <div style={{ flex: 1, height: 6, background: '#e2e8f0', borderRadius: 999, overflow: 'hidden' }}>
          <div
            style={{
              width: `${Math.max(pct, row.netPnl !== 0 ? 4 : 0)}%`,
              height: '100%',
              background: positive ? '#0dca9d' : '#ff7171',
              borderRadius: 999,
            }}
          />
        </div>
        <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600, minWidth: 34, textAlign: 'right' }}>
          {shareDenominator > 0 ? `${pct.toFixed(0)}%` : '—'}
        </span>
      </div>
    );
  };

  const renderMobileRow = (row: CoinProfitAggregate) => {
    const positive = row.netPnl >= 0;
    return (
      <div
        key={row.symbol}
        style={{
          border: '1px solid #e2e8f0',
          borderRadius: 10,
          padding: '12px 14px',
          background: '#f8fafc',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15, color: '#1e293b' }}>{row.symbol}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
              {row.positionCount} position{row.positionCount === 1 ? '' : 's'}
            </div>
          </div>
          <div style={{ fontWeight: 800, fontSize: 16, color: positive ? '#15803d' : '#b91c1c' }}>
            {fmtPnl(row.netPnl)}
          </div>
        </div>
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
          Realized {fmtPnl(row.realizedPnl)} · Unrealized {fmtPnl(row.unrealizedPnl)}
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
          {buildCoinProfitSourceLabel(row)}
        </div>
        <div style={{ marginTop: 10 }}>{renderShareBar(row)}</div>
        <button
          type="button"
          onClick={() => onViewLegs(row.symbol)}
          style={{
            marginTop: 10,
            padding: '6px 12px',
            borderRadius: 999,
            border: '1px solid #e2e8f0',
            background: '#fff',
            color: '#334155',
            fontSize: 11,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          View legs
        </button>
      </div>
    );
  };

  return (
    <div style={{ ...cardStyle, marginTop: 16 }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontWeight: 800, fontSize: 14, color: '#1e293b' }}>Profit by Coin</div>
        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
          Realized and unrealized P&L from activity bots and single trades, grouped by coin
        </div>
      </div>

      <div
        style={{
          padding: '12px 14px',
          borderRadius: 10,
          background: totalNetPnl >= 0 ? '#ecfdf5' : '#fef2f2',
          border: `1px solid ${totalNetPnl >= 0 ? '#bbf7d0' : '#fecaca'}`,
          marginBottom: 14,
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          Total all coins
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '8px 16px', marginTop: 6 }}>
          <span style={{ fontSize: 24, fontWeight: 800, color: totalNetPnl >= 0 ? '#15803d' : '#b91c1c' }}>
            {fmtPnl(totalNetPnl)}
          </span>
          <span style={{ fontSize: 12, color: '#64748b' }}>
            {coinRows.length} coin{coinRows.length === 1 ? '' : 's'} · {totalPositions} position{totalPositions === 1 ? '' : 's'}
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>Source:</span>
        {([
          ['ALL', 'All'],
          ['ACTIVITY_BOT', 'Activity Bot'],
          ['SINGLE', 'Single Trade'],
          ['TRADING_BOT', 'Trading Bot'],
        ] as const).map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => {
              setSourceFilter(value);
              setShowAll(false);
            }}
            style={profitCoinFilterBtn(sourceFilter === value)}
          >
            {label}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 14 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>Sort:</span>
        <select
          value={sort}
          onChange={e => setSort(e.target.value as ProfitCoinSort)}
          style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, fontWeight: 600 }}
        >
          <option value="netDesc">Net P&L (high → low)</option>
          <option value="netAsc">Net P&L (low → high)</option>
          <option value="symbol">Coin A–Z</option>
          <option value="trades">Most positions</option>
        </select>
      </div>

      {coinRows.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '24px 12px', color: '#94a3b8', fontSize: 13 }}>
          No positions match this source filter.
        </div>
      ) : isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {visibleRows.map(renderMobileRow)}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={TH}>Coin</th>
                <th style={{ ...TH, textAlign: 'right' }}>Positions</th>
                <th style={{ ...TH, textAlign: 'right' }}>Realized</th>
                <th style={{ ...TH, textAlign: 'right' }}>Unrealized</th>
                <th style={{ ...TH, textAlign: 'right' }}>Net P&L</th>
                <th style={TH}>Share</th>
                <th style={{ ...TH, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row, i) => {
                const positive = row.netPnl >= 0;
                return (
                  <tr key={row.symbol} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                    <td style={TD}>
                      <div style={{ fontWeight: 800, color: '#1e293b' }}>{row.symbol}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
                        {buildCoinProfitSourceLabel(row)}
                      </div>
                    </td>
                    <td style={{ ...TD, textAlign: 'right', fontWeight: 600 }}>{row.positionCount}</td>
                    <td style={{ ...TD, textAlign: 'right', fontWeight: 600, color: row.realizedPnl >= 0 ? '#15803d' : '#b91c1c' }}>
                      {fmtPnl(row.realizedPnl)}
                    </td>
                    <td style={{ ...TD, textAlign: 'right', fontWeight: 600, color: row.unrealizedPnl >= 0 ? '#15803d' : '#b91c1c' }}>
                      {row.unrealizedPnl === 0 ? '—' : fmtPnl(row.unrealizedPnl)}
                    </td>
                    <td style={{ ...TD, textAlign: 'right', fontWeight: 800, color: positive ? '#15803d' : '#b91c1c' }}>
                      {fmtPnl(row.netPnl)}
                    </td>
                    <td style={TD}>{renderShareBar(row)}</td>
                    <td style={{ ...TD, textAlign: 'right' }}>
                      <button
                        type="button"
                        onClick={() => onViewLegs(row.symbol)}
                        style={{
                          padding: '5px 12px',
                          borderRadius: 999,
                          border: '1px solid #e2e8f0',
                          background: '#fff',
                          color: '#334155',
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        View legs
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!showAll && hiddenCount > 0 && (
        <div style={{ marginTop: 12, fontSize: 12, color: '#64748b', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <span>
            Showing top {PROFIT_COIN_TOP_DEFAULT} coins · {hiddenCount} more
          </span>
          <button
            type="button"
            onClick={() => setShowAll(true)}
            style={{
              padding: '4px 10px',
              borderRadius: 999,
              border: '1px solid #e2e8f0',
              background: '#fff',
              color: '#086afb',
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Show all
          </button>
        </div>
      )}

      {showAll && coinRows.length > PROFIT_COIN_TOP_DEFAULT && (
        <div style={{ marginTop: 12 }}>
          <button
            type="button"
            onClick={() => setShowAll(false)}
            style={{
              padding: '4px 10px',
              borderRadius: 999,
              border: '1px solid #e2e8f0',
              background: '#fff',
              color: '#64748b',
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Show top {PROFIT_COIN_TOP_DEFAULT}
          </button>
        </div>
      )}

      <div style={{ marginTop: 12, fontSize: 11, color: '#94a3b8' }}>
        Closed legs: last 90 days · Open legs: current unrealized P&L included
      </div>
    </div>
  );
};

interface ProfitTargetModalProps {
  open: boolean;
  initialPct: string;
  saving: boolean;
  error: string | null;
  hasTarget: boolean;
  onClose: () => void;
  onSave: (pct: number) => void;
  onClear: () => void;
}

const ProfitTargetModal: React.FC<ProfitTargetModalProps> = ({
  open,
  initialPct,
  saving,
  error,
  hasTarget,
  onClose,
  onSave,
  onClear,
}) => {
  const [value, setValue] = React.useState(initialPct);

  React.useEffect(() => {
    if (open) setValue(initialPct);
  }, [open, initialPct]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{ ...cardStyle, width: '100%', maxWidth: 400, padding: 20 }}
        onClick={e => e.stopPropagation()}
      >
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#1e293b' }}>Profit target</h2>
        <p style={{ margin: '8px 0 16px', fontSize: 13, color: '#64748b' }}>
          Target is calculated as a percentage of your total open position value. Progress uses net P&L
          (unrealized on open legs + realized from closed positions in the last 90 days).
        </p>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>
          Target (%)
        </label>
        <input
          type="number"
          min={0.1}
          max={1000}
          step={0.1}
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder="e.g. 10"
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 8,
            border: '1px solid #e2e8f0',
            fontSize: 14,
            boxSizing: 'border-box',
          }}
        />
        {error && (
          <p style={{ margin: '10px 0 0', fontSize: 12, color: '#b91c1c' }}>{error}</p>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 18, flexWrap: 'wrap' }}>
          <button
            type="button"
            disabled={saving}
            onClick={() => onSave(Number(value))}
            style={{
              flex: 1,
              minWidth: 100,
              padding: '10px 14px',
              borderRadius: 999,
              border: 'none',
              background: '#086afb',
              color: '#fff',
              fontWeight: 700,
              fontSize: 13,
              cursor: saving ? 'wait' : 'pointer',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Saving…' : 'Save target'}
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            style={{
              padding: '10px 14px',
              borderRadius: 999,
              border: '1px solid #e2e8f0',
              background: '#fff',
              color: '#64748b',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          {hasTarget && (
            <button
              type="button"
              disabled={saving}
              onClick={onClear}
              style={{
                width: '100%',
                padding: '8px 14px',
                borderRadius: 999,
                border: '1px solid #fecaca',
                background: '#fff',
                color: '#b91c1c',
                fontWeight: 600,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Remove target
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const CryptoPortfolio: React.FC<CryptoPortfolioProps> = ({ isAdminContext = false }) => {
  const navigate = useNavigate();
  const adminUser = useAppSelector(s => s.auth.adminUser);
  const isMobile = useIsMobile();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedOnceRef = useRef(false);
  const [data, setData] = useState<CryptoPortfolioPayload | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('OPEN');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('ALL');
  const [search, setSearch] = useState('');
  const [closingId, setClosingId] = useState<number | null>(null);
  const [autoCloseTogglingId, setAutoCloseTogglingId] = useState<number | null>(null);
  const [exitRulesSavingId, setExitRulesSavingId] = useState<number | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [expandedRowKey, setExpandedRowKey] = useState<string | null>(null);
  const expandedRowRef = useRef<HTMLDivElement | HTMLTableRowElement | null>(null);
  const [targetModalOpen, setTargetModalOpen] = useState(false);
  const [targetSaving, setTargetSaving] = useState(false);
  const [targetError, setTargetError] = useState<string | null>(null);
  const targetNotifiedRef = useRef(false);
  const [realizedWindow, setRealizedWindow] = useState<RealizedPnlWindow>(() => readStoredRealizedWindow());
  const [historySortColumn, setHistorySortColumn] = useState<HistorySortColumn>('activity');
  const [historySortDirection, setHistorySortDirection] = useState<HistorySortDirection>('desc');
  const loadRequestIdRef = useRef(0);
  const positionHistoryRef = useRef<HTMLDivElement>(null);

  const canManageClose = isAdminContext && adminCanOpenMarketSignalPositions(adminUser);
  const [challengeBotIds, setChallengeBotIds] = useState<number[]>([]);

  // Admin-only: open a single direct LONG/SHORT position (no activity bot creation).
  const [directPositionDrawerOpen, setDirectPositionDrawerOpen] = useState(false);
  const [directPositionCoin, setDirectPositionCoin] = useState<CoinActivity | null>(null);
  const [directPositionSymbol, setDirectPositionSymbol] = useState<string>('');
  const [directPositionQuery, setDirectPositionQuery] = useState<string>('');
  const [directPositionSuggestionsOpen, setDirectPositionSuggestionsOpen] = useState(false);
  const [directPositionUniverse, setDirectPositionUniverse] = useState<string[]>([]);
  const directPositionSearchRef = useRef<HTMLDivElement | null>(null);

  const directPositionSymbols = useMemo(() => {
    const top = data?.allocation?.topAssets?.map(a => a.symbol) ?? [];
    const openSyms = (data?.openPositions ?? []).map(p => p.symbol);
    const closedSyms = (data?.closedPositions ?? []).map(p => p.symbol);
    return Array.from(
      new Set(
        [...directPositionUniverse, ...top, ...openSyms, ...closedSyms]
          .map(s => String(s || '').toUpperCase())
          .filter(Boolean),
      ),
    ).sort((a, b) => a.localeCompare(b));
  }, [data, directPositionUniverse]);

  const directPositionMatches = useMemo(() => {
    const q = directPositionQuery.trim().toUpperCase();
    if (!q) return directPositionSymbols.slice(0, 20);
    return directPositionSymbols.filter(sym => sym.includes(q)).slice(0, 20);
  }, [directPositionQuery, directPositionSymbols]);

  useEffect(() => {
    if (!isAdminContext || !canManageClose) return;
    let cancelled = false;
    (async () => {
      try {
        const snapshot = await publicMarketService.getSectorBreakdown();
        if (cancelled) return;
        const symbols = (snapshot?.sectors ?? [])
          .flatMap(sector => sector.coins ?? [])
          .map(coin => String(coin.symbol || '').toUpperCase())
          .filter(Boolean);
        setDirectPositionUniverse(Array.from(new Set(symbols)));
      } catch {
        if (!cancelled) setDirectPositionUniverse([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdminContext, canManageClose]);

  useEffect(() => {
    if (!directPositionSuggestionsOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (!directPositionSearchRef.current?.contains(e.target as Node)) {
        setDirectPositionSuggestionsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [directPositionSuggestionsOpen]);

  const normalizeDirectSymbol = (raw: string): string => {
    return String(raw || '')
      .trim()
      .toUpperCase()
      .replace(/USDTM?$/i, '')
      .replace(/[^A-Z0-9]/g, '');
  };

  const openDirectPosition = (side: 'LONG' | 'SHORT') => {
    const symbol = normalizeDirectSymbol(directPositionSymbol || directPositionQuery);
    if (!symbol) return;
    setDirectPositionSymbol(symbol);
    setDirectPositionQuery(symbol);
    setDirectPositionSuggestionsOpen(false);
    setDirectPositionCoin({
      symbol,
      active: side === 'LONG',
      change24h: 0,
      volume24h: 0,
    } as CoinActivity);
    setDirectPositionDrawerOpen(true);
  };

  const toggleRowExpand = (row: CryptoPortfolioPositionRow) => {
    const key = getPositionRowKey(row);
    setExpandedRowKey(prev => (prev === key ? null : key));
  };

  useEffect(() => {
    if (!expandedRowKey || !expandedRowRef.current) return;
    expandedRowRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [expandedRowKey]);

  useEffect(() => {
    setExpandedRowKey(null);
  }, [statusFilter, sourceFilter, search]);

  const load = useCallback(async (silent = false) => {
    const requestId = ++loadRequestIdRef.current;
    if (!silent) {
      if (hasLoadedOnceRef.current) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
    } else {
      setRefreshing(true);
    }
    try {
      const payload = isAdminContext
        ? await cryptoPortfolioService.getAdminPortfolio()
        : await cryptoPortfolioService.getRetailPortfolio();
      if (requestId !== loadRequestIdRef.current) {
        return;
      }
      setData(payload);
      hasLoadedOnceRef.current = true;
      if (!silent) setError(null);
    } catch (err: any) {
      if (requestId !== loadRequestIdRef.current) {
        return;
      }
      if (!silent) {
        const code = String(err?.message || err?.response?.status || '');
        if (code.includes('403')) {
          setError('You do not have permission to view the crypto portfolio.');
        } else if (code.includes('401')) {
          setError('Session expired. Please refresh the page or log in again.');
        } else {
          setError('Failed to load crypto portfolio. Please try again.');
        }
      }
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [isAdminContext]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      load(true);
    }, PORTFOLIO_POLL_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        load(true);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  useEffect(() => {
    if (!isAdminContext || !data?.profitTarget?.showAlert) return;
    if (targetNotifiedRef.current) return;
    targetNotifiedRef.current = true;
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      const pct = Number(data.profitTarget?.targetPct ?? 0).toFixed(1);
      const amount = fmtUsd(data.profitTarget?.targetAmountUsd);
      new Notification('Profit target reached', {
        body: `Your +${pct}% target (${amount}) has been achieved.`,
      });
    }
  }, [isAdminContext, data?.profitTarget?.showAlert, data?.profitTarget?.targetPct, data?.profitTarget?.targetAmountUsd]);

  const handleSaveProfitTarget = async (pct: number) => {
    if (!Number.isFinite(pct) || pct <= 0) {
      setTargetError('Enter a target greater than 0%.');
      return;
    }
    setTargetSaving(true);
    setTargetError(null);
    try {
      const updated = await cryptoPortfolioService.setAdminProfitTarget(pct);
      targetNotifiedRef.current = false;
      setData(prev => (prev ? { ...prev, profitTarget: updated } : prev));
      setTargetModalOpen(false);
      await load(true);
    } catch (err: any) {
      setTargetError(err?.message || 'Failed to save target.');
    } finally {
      setTargetSaving(false);
    }
  };

  const handleClearProfitTarget = async () => {
    setTargetSaving(true);
    setTargetError(null);
    try {
      await cryptoPortfolioService.clearAdminProfitTarget();
      targetNotifiedRef.current = false;
      setTargetModalOpen(false);
      await load(true);
    } catch (err: any) {
      setTargetError(err?.message || 'Failed to remove target.');
    } finally {
      setTargetSaving(false);
    }
  };

  const handleDismissProfitAlert = async () => {
    try {
      const updated = await cryptoPortfolioService.dismissAdminProfitTargetAlert();
      setData(prev => (prev ? { ...prev, profitTarget: updated } : prev));
    } catch {
      setActionMsg('Could not dismiss alert. Please try again.');
    }
  };

  const openTargetModal = () => {
    if (isAdminContext && typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => undefined);
    }
    setTargetError(null);
    setTargetModalOpen(true);
  };

  const filteredOpen = useMemo(() => {
    const rows = data?.openPositions ?? [];
    return rows.filter(row => {
      if (sourceFilter !== 'ALL' && row.source !== sourceFilter) return false;
      if (search && !row.symbol?.toUpperCase().includes(search.trim().toUpperCase())) return false;
      return true;
    });
  }, [data?.openPositions, sourceFilter, search]);

  const filteredClosed = useMemo(() => {
    const rows = data?.closedPositions ?? [];
    return rows.filter(row => {
      if (sourceFilter !== 'ALL' && row.source !== sourceFilter) return false;
      if (search && !row.symbol?.toUpperCase().includes(search.trim().toUpperCase())) return false;
      return true;
    });
  }, [data?.closedPositions, sourceFilter, search]);

  const displayRows = useMemo(() => {
    let rows: CryptoPortfolioPositionRow[] = [];
    if (statusFilter === 'OPEN') rows = filteredOpen;
    else if (statusFilter === 'CLOSED') rows = filteredClosed;
    else if (statusFilter === 'CLOSED_LOSS') rows = filteredClosed.filter(isClosedLoss);
    else if (statusFilter === 'CLOSED_PROFIT') rows = filteredClosed.filter(isClosedProfit);
    else rows = [...filteredOpen, ...filteredClosed];
    return sortHistoryRows(rows, historySortColumn, historySortDirection);
  }, [statusFilter, filteredOpen, filteredClosed, historySortColumn, historySortDirection]);

  const handleHistorySort = (column: HistorySortColumn) => {
    setHistorySortColumn(prevColumn => {
      if (prevColumn === column) {
        setHistorySortDirection(prevDir => (prevDir === 'asc' ? 'desc' : 'asc'));
        return prevColumn;
      }
      setHistorySortDirection(
        column === 'opened' || column === 'closed' || column === 'pnl' || column === 'entry' || column === 'exitCurrent'
          ? 'desc'
          : 'asc',
      );
      return column;
    });
  };

  const renderSortableTh = (
    column: HistorySortColumn,
    label: string,
    align: 'left' | 'right' = 'left',
  ) => {
    const active = historySortColumn === column;
    return (
      <th style={{ ...TH, textAlign: align }}>
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            handleHistorySort(column);
          }}
          aria-label={`Sort by ${label}`}
          style={{
            border: 'none',
            background: 'transparent',
            padding: 0,
            margin: 0,
            font: 'inherit',
            textTransform: 'inherit',
            letterSpacing: 'inherit',
            color: active ? '#086afb' : '#64748b',
            fontWeight: 700,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            width: align === 'right' ? '100%' : undefined,
            justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
          }}
        >
          <span>{label}</span>
          <span style={{ fontSize: 10, lineHeight: 1, opacity: active ? 1 : 0.4 }}>
            {active ? (historySortDirection === 'asc' ? '↑' : '↓') : '↕'}
          </span>
        </button>
      </th>
    );
  };

  const showHistoryStatusCol = statusFilter === 'ALL';

  const handleClose = async (row: CryptoPortfolioPositionRow) => {
    if (!row.hedgePositionId || !row.symbol) return;
    const ok = window.confirm(
      `Close ${row.side} ${row.symbol}?\n\nThis sends a real close order to KuCoin.`
    );
    if (!ok) return;
    setClosingId(row.hedgePositionId);
    setActionMsg(null);
    try {
      const result = await publicMarketService.closeAdminCoinPosition({
        symbol: row.symbol,
        hedgePositionId: row.hedgePositionId,
      });
      if (!result.success) {
        setActionMsg(result.message || 'Failed to close position');
        return;
      }
      setActionMsg(result.message || `Closed ${row.side} on ${row.symbol}`);
      await load();
    } catch (err: any) {
      setActionMsg(err?.body?.message || err?.message || 'Failed to close position');
    } finally {
      setClosingId(null);
    }
  };

  const handleToggleAutoClose = async (row: CryptoPortfolioPositionRow, enabled: boolean) => {
    if (!row.hedgePositionId || !row.canToggleAutoClose) return;
    setAutoCloseTogglingId(row.hedgePositionId);
    setActionMsg(null);
    try {
      const updated = await cryptoPortfolioService.setPositionAutoClose(row.hedgePositionId, enabled);
      setData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          openPositions: prev.openPositions.map(p =>
            p.hedgePositionId === updated.hedgePositionId ? { ...p, ...updated } : p
          ),
        };
      });
      setActionMsg(
        enabled
          ? `Auto-close enabled for ${row.symbol} when +${Number(row.targetPct ?? 0).toFixed(1)}% target is hit.`
          : `Auto-close disabled for ${row.symbol}.`
      );
    } catch (err: any) {
      setActionMsg(err?.message || 'Failed to update auto-close setting.');
    } finally {
      setAutoCloseTogglingId(null);
    }
  };

  const handleSaveExitRules = async (row: CryptoPortfolioPositionRow, payload: SetPositionExitRulesRequest) => {
    if (!row.hedgePositionId || !row.canConfigureExitRules) return;
    setExitRulesSavingId(row.hedgePositionId);
    setActionMsg(null);
    try {
      const updated = await cryptoPortfolioService.setPositionExitRules(row.hedgePositionId, payload);
      setData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          openPositions: prev.openPositions.map(p =>
            p.hedgePositionId === updated.hedgePositionId ? { ...p, ...updated } : p
          ),
        };
      });
      setActionMsg(`Exit rules saved for ${row.symbol}.`);
    } catch (err: any) {
      setActionMsg(err?.message || 'Failed to save exit rules.');
      throw err;
    } finally {
      setExitRulesSavingId(null);
    }
  };

  const handleViewStrategy = (row: CryptoPortfolioPositionRow) => {
    if (!row.strategyId) return;
    if (isAdminContext) {
      navigate('/admin/dashboard?tab=tradingBots');
    } else {
      navigate('/trading-bots');
    }
  };

  const summary = data?.summary;
  const allocation = data?.allocation;
  const profitTarget = data?.profitTarget;

  const liveTotalOpenValue = useMemo(
    () => sumOpenRows(data?.openPositions, row => Number(row.sizeUsd ?? 0)),
    [data?.openPositions],
  );

  const liveUnrealizedPnl = useMemo(
    () => sumOpenRows(data?.openPositions, row => Number(row.unrealizedPnl ?? 0)),
    [data?.openPositions],
  );

  const openPositionCount = data?.openPositions?.length ?? summary?.openCount ?? 0;

  const closedForRealizedPnl = useMemo(() => {
    const sorted = sortClosedByDateDesc(data?.closedPositions ?? []);
    return sorted.slice(0, realizedWindow);
  }, [data?.closedPositions, realizedWindow]);

  const windowedRealizedPnl = useMemo(
    () => closedForRealizedPnl.reduce((sum, row) => sum + Number(row.realizedPnl ?? 0), 0),
    [closedForRealizedPnl]
  );

  const windowedNetPnl = useMemo(
    () => liveUnrealizedPnl + windowedRealizedPnl,
    [liveUnrealizedPnl, windowedRealizedPnl],
  );

  const handleRealizedWindowChange = (window: RealizedPnlWindow) => {
    setRealizedWindow(window);
    try {
      localStorage.setItem(REALIZED_PNL_WINDOW_KEY, String(window));
    } catch {
      // ignore
    }
  };

  const handleViewCoinLegs = useCallback((symbol: string) => {
    setSearch(symbol);
    setStatusFilter('ALL');
    setSourceFilter('ALL');
    window.setTimeout(() => {
      positionHistoryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }, []);

  const renderMobileRow = (row: CryptoPortfolioPositionRow) => {
    const isOpen = isPositionOpen(row);
    const pnl = isOpen ? row.unrealizedPnl : row.realizedPnl;
    const pnlPositive = Number(pnl ?? 0) >= 0;
    const priceFrom = row.entryPrice;
    const priceTo = isOpen ? row.currentPrice : row.exitPrice;
    const rowKey = getPositionRowKey(row);
    const isExpanded = expandedRowKey === rowKey;

    return (
      <div
        key={rowKey}
        ref={isExpanded ? (expandedRowRef as React.RefObject<HTMLDivElement>) : undefined}
        style={{
          ...cardStyle,
          marginBottom: 12,
          borderColor: isExpanded ? '#086afb' : '#e2e8f0',
          boxShadow: isExpanded ? '0 0 0 1px #086afb33' : cardStyle.boxShadow,
        }}
      >
        <button
          type="button"
          onClick={() => toggleRowExpand(row)}
          aria-expanded={isExpanded}
          style={{
            width: '100%',
            display: 'block',
            padding: 0,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            textAlign: 'left',
            fontFamily: 'inherit',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, color: '#64748b' }}>{isExpanded ? '▴' : '▾'}</span>
                <span style={{ fontWeight: 800, fontSize: 16, color: '#1e293b' }}>{row.symbol}</span>
                <SideBadge side={row.side} />
                <StatusBadge open={isOpen} />
                <SourceBadge source={row.source} />
              </div>
              {row.activityBotLabel && (
                <div style={{ marginTop: 6, maxWidth: '100%' }}>
                  <ActivityBotBadge label={row.activityBotLabel} compact />
                </div>
              )}
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 6 }}>
                {row.strategyName || '—'}
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                Ref {formatPositionRef(row)}
                {isOpen ? ` · Opened ${fmtDate(row.openedAt)}` : ` · Closed ${fmtDate(row.closedAt)}`}
              </div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 4 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: pnlPositive ? '#15803d' : '#b91c1c', whiteSpace: 'nowrap' }}>
                {fmtPnl(pnl)}
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2, whiteSpace: 'nowrap' }}>
                {isOpen ? 'Unrealized' : 'Realized'}
              </div>
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
              gap: '10px 16px',
              marginTop: 14,
              fontSize: 12,
            }}
          >
            <div>
              <div style={{ color: '#94a3b8' }}>Entry</div>
              <div style={{ fontWeight: 600, color: '#334155' }}>{formatCryptoPrice(priceFrom)}</div>
            </div>
            <div>
              <div style={{ color: '#94a3b8' }}>{isOpen ? 'Current' : 'Exit'}</div>
              <div style={{ fontWeight: 600, color: '#334155' }}>{formatCryptoPrice(priceTo)}</div>
            </div>
            <div>
              <div style={{ color: '#94a3b8' }}>Size</div>
              <div style={{ fontWeight: 600, color: '#334155' }}>{fmtUsd(row.sizeUsd)}</div>
            </div>
            <div>
              <div style={{ color: '#94a3b8' }}>Leverage</div>
              <div style={{ fontWeight: 600, color: '#334155' }}>{fmtLeverage(row.leverage)}</div>
            </div>
            {isOpen && row.source === 'DIRECT' && row.targetProgressPct != null && (
              <div>
                <div style={{ color: '#94a3b8' }}>Target</div>
                <div
                  style={{
                    fontWeight: 600,
                    color: targetProgressLabelColor(
                      normalizeTargetProgress(row.targetProgressPct),
                      Boolean(row.targetAchieved),
                    ),
                  }}
                >
                  {formatTargetProgressLabelForRow(row)}
                </div>
              </div>
            )}
            {isOpen && hasStopLoss(row) && (
              <div>
                <div style={{ color: '#94a3b8' }}>Stop buffer</div>
                <div
                  style={{
                    fontWeight: 600,
                    color: stopLossUsedLabelColor(
                      computeStopLossUsedPct(row.pnlOnSizePct, row.stopLossPct) ?? 0,
                      Number(row.pnlOnSizePct ?? 0) <= Number(row.stopLossPct ?? 0),
                    ),
                  }}
                >
                  {formatStopLossUsedLabel(
                    computeStopLossUsedPct(row.pnlOnSizePct, row.stopLossPct) ?? 0,
                    Number(row.pnlOnSizePct ?? 0) <= Number(row.stopLossPct ?? 0),
                  )}
                </div>
              </div>
            )}
          </div>
        </button>

        {isExpanded && (
          <PositionDetailPanel
            row={row}
            isOpen={isOpen}
            isMobile
            isAdminContext={isAdminContext}
            actions={renderRowActions(row, isOpen, false)}
            onToggleAutoClose={
              row.canToggleAutoClose
                ? enabled => handleToggleAutoClose(row, enabled)
                : undefined
            }
            autoCloseToggling={autoCloseTogglingId === row.hedgePositionId}
            onSaveExitRules={
              row.canConfigureExitRules
                ? payload => handleSaveExitRules(row, payload)
                : undefined
            }
            exitRulesSaving={exitRulesSavingId === row.hedgePositionId}
          />
        )}

        {!isExpanded && (
          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
            {renderRowActions(row, isOpen, false)}
          </div>
        )}
      </div>
    );
  };

  const renderRowActions = (row: CryptoPortfolioPositionRow, isOpen: boolean, compact = false) => {
    const btnStyle: React.CSSProperties = compact
      ? {
          padding: '5px 10px',
          borderRadius: 999,
          border: 'none',
          fontSize: 11,
          fontWeight: 600,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }
      : {
          flex: 1,
          padding: '8px 12px',
          borderRadius: 999,
          border: 'none',
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
        };

    return (
      <div
        style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: compact ? 'flex-end' : undefined, alignItems: 'center' }}
        onClick={e => e.stopPropagation()}
      >
        {isOpen && isAdminContext && row.canToggleAutoClose && (
          <AutoCloseToggle
            row={row}
            disabled={autoCloseTogglingId === row.hedgePositionId}
            onToggle={enabled => handleToggleAutoClose(row, enabled)}
          />
        )}
        {row.canViewStrategy && row.strategyId != null && (
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              handleViewStrategy(row);
            }}
            style={{ ...btnStyle, background: '#086afb', color: '#fff' }}
          >
            View strategy
          </button>
        )}
        {isOpen && canManageClose && row.canClose && row.hedgePositionId != null && (
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              handleClose(row);
            }}
            disabled={closingId === row.hedgePositionId}
            style={{
              ...btnStyle,
              background: closingId === row.hedgePositionId ? '#94a3b8' : '#ef4444',
              color: '#fff',
              fontWeight: 700,
              cursor: closingId === row.hedgePositionId ? 'wait' : 'pointer',
            }}
          >
            {closingId === row.hedgePositionId ? 'Closing…' : 'Close'}
          </button>
        )}
      </div>
    );
  };

  const renderPositionsTable = (rows: CryptoPortfolioPositionRow[]) => {
    const showTargetCol = rows.some(r => isPositionOpen(r));
    const colCount = 13 + (showHistoryStatusCol ? 1 : 0) + (showTargetCol ? 1 : 0);

    return (
      <div style={{ ...cardStyle, padding: 0, overflow: isMobile ? 'hidden' : 'visible' }}>
        <div style={{ overflowX: isMobile ? 'auto' : 'visible' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              tableLayout: isMobile ? 'auto' : 'fixed',
              minWidth: isMobile ? 1180 : undefined,
            }}
          >
            <thead>
              <tr>
                <th style={TH}>Ref</th>
                {showHistoryStatusCol && <th style={TH}>Status</th>}
                <th style={TH}>Coin</th>
                {renderSortableTh('side', 'Side')}
                {renderSortableTh('source', 'Source')}
                {renderSortableTh('strategy', 'Strategy')}
                {renderSortableTh('opened', 'Opened')}
                {renderSortableTh('closed', 'Closed')}
                {renderSortableTh('entry', 'Entry', 'right')}
                {renderSortableTh('exitCurrent', 'Exit / Current', 'right')}
                <th style={{ ...TH, textAlign: 'right' }}>Size</th>
                <th style={{ ...TH, textAlign: 'right' }}>Leverage</th>
                {renderSortableTh('pnl', 'P&L', 'right')}
                {showTargetCol && (
                  <th style={{ ...TH, textAlign: 'right', width: isMobile ? undefined : '9%' }}>Target / Stop</th>
                )}
                <th style={{ ...TH, textAlign: 'right', whiteSpace: 'nowrap', width: isMobile ? undefined : 220 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const isOpen = isPositionOpen(row);
                const pnl = isOpen ? row.unrealizedPnl : row.realizedPnl;
                const pnlPositive = Number(pnl ?? 0) >= 0;
                const priceTo = isOpen ? row.currentPrice : row.exitPrice;
                const rowKey = getPositionRowKey(row);
                const isExpanded = expandedRowKey === rowKey;

                return (
                  <React.Fragment key={rowKey}>
                    <tr
                      ref={isExpanded ? (expandedRowRef as React.RefObject<HTMLTableRowElement>) : undefined}
                      onClick={() => toggleRowExpand(row)}
                      style={{
                        background: isExpanded ? '#eff6ff' : i % 2 === 0 ? '#fff' : '#f8fafc',
                        cursor: 'pointer',
                        boxShadow: isExpanded ? 'inset 3px 0 0 #086afb' : undefined,
                      }}
                    >
                      <td style={{ ...TD, color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap', fontSize: 11 }}>
                        {formatPositionRef(row)}
                      </td>
                      {showHistoryStatusCol && (
                        <td style={TD}><StatusBadge open={isOpen} /></td>
                      )}
                      <td style={{ ...TD, fontWeight: 800, color: '#1e293b' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 10, color: '#64748b', width: 10 }}>{isExpanded ? '▴' : '▾'}</span>
                          {row.symbol}
                        </span>
                      </td>
                      <td style={TD}><SideBadge side={row.side} /></td>
                      <td style={{ ...TD, maxWidth: 148, minWidth: 120 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start', maxWidth: '100%' }}>
                          <SourceBadge source={row.source} />
                          {row.activityBotLabel && <ActivityBotBadge label={row.activityBotLabel} compact />}
                        </div>
                      </td>
                      <td style={{ ...TD, maxWidth: 180, color: '#64748b' }}>
                        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row.strategyName || '—'}
                        </span>
                      </td>
                      <td style={{ ...TD, color: '#64748b', whiteSpace: 'nowrap' }}>{fmtDate(row.openedAt)}</td>
                      <td style={{ ...TD, color: '#64748b', whiteSpace: 'nowrap' }}>{isOpen ? '—' : fmtDate(row.closedAt)}</td>
                      <td style={{ ...TD, textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {formatCryptoPrice(row.entryPrice)}
                      </td>
                      <td style={{ ...TD, textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {formatCryptoPrice(priceTo)}
                      </td>
                      <td style={{ ...TD, textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {fmtUsd(row.sizeUsd)}
                      </td>
                      <td style={{ ...TD, textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {fmtLeverage(row.leverage)}
                      </td>
                      <td style={{
                        ...TD,
                        textAlign: 'right',
                        fontWeight: 700,
                        color: pnlPositive ? '#15803d' : '#b91c1c',
                        whiteSpace: 'nowrap',
                      }}>
                        {fmtPnl(pnl)}
                      </td>
                      {showTargetCol && (
                        <td style={{ ...TD, textAlign: 'right' }}>
                          {isOpen ? <TargetProgressCell row={row} /> : '—'}
                        </td>
                      )}
                      <td style={{ ...TD, textAlign: 'right', whiteSpace: 'nowrap', width: isMobile ? undefined : 240 }}>
                        {renderRowActions(row, isOpen, true)}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={colCount} style={{ padding: 0, borderBottom: '1px solid #e2e8f0' }}>
                          <PositionDetailPanel
                            row={row}
                            isOpen={isOpen}
                            isMobile={false}
                            isAdminContext={isAdminContext}
                            actions={renderRowActions(row, isOpen, true)}
                            onToggleAutoClose={
                              row.canToggleAutoClose
                                ? enabled => handleToggleAutoClose(row, enabled)
                                : undefined
                            }
                            autoCloseToggling={autoCloseTogglingId === row.hedgePositionId}
                            onSaveExitRules={
                              row.canConfigureExitRules
                                ? payload => handleSaveExitRules(row, payload)
                                : undefined
                            }
                            exitRulesSaving={exitRulesSavingId === row.hedgePositionId}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderPositionList = (rows: CryptoPortfolioPositionRow[]) => {
    if (rows.length === 0) return null;
    if (isMobile) {
      return <>{rows.map(row => renderMobileRow(row))}</>;
    }
    return renderPositionsTable(rows);
  };

  return (
    <div
      style={{
        padding: isAdminContext
          ? (isMobile ? '8px 0 24px' : '12px 0 28px')
          : (isMobile ? '20px 16px 32px' : '20px 24px 32px'),
        maxWidth: DESKTOP_PAGE_MAX_WIDTH,
        width: '100%',
        margin: '0 auto',
        fontFamily: 'Nunito, system-ui, sans-serif',
        boxSizing: 'border-box',
        overflowX: 'hidden',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#1e293b' }}>Crypto Portfolio</h1>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: '#64748b' }}>
            KuCoin futures positions {isAdminContext && adminUser ? `· ${adminUser.fullName || adminUser.username}` : ''}
            {data?.updatedAt ? ` · updated ${timeAgo(data.updatedAt)}` : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={() => load()}
          disabled={loading || refreshing}
          style={{
            padding: '8px 16px',
            borderRadius: 999,
            border: '1px solid #e2e8f0',
            background: '#fff',
            color: '#334155',
            fontSize: 13,
            fontWeight: 600,
            cursor: loading || refreshing ? 'wait' : 'pointer',
          }}
        >
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {actionMsg && (
        <div
          role="status"
          style={{
            marginTop: 16,
            padding: '12px 14px',
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: 10,
            color: '#15803d',
            fontSize: 13,
          }}
        >
          {actionMsg}
        </div>
      )}

      {error && (
        <div
          role="alert"
          style={{
            marginTop: 16,
            padding: '12px 14px',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 10,
            color: '#b91c1c',
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {!loading && data && isAdminContext && profitTarget?.showAlert && (
        <div
          role="alert"
          style={{
            marginTop: 16,
            padding: '14px 16px',
            background: '#ecfdf5',
            border: '1px solid #6ee7b7',
            borderRadius: 10,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div style={{ fontWeight: 800, fontSize: 14, color: '#047857' }}>Profit target reached</div>
            <div style={{ fontSize: 13, color: '#065f46', marginTop: 4 }}>
              You hit your +{Number(profitTarget?.targetPct ?? 0).toFixed(1)}% unrealized goal ({fmtUsd(profitTarget?.targetAmountUsd)}).
              Current unrealized P&L: {fmtPnl(profitTarget?.currentNetPnl)}.
            </div>
          </div>
          <button
            type="button"
            onClick={handleDismissProfitAlert}
            style={{
              padding: '8px 14px',
              borderRadius: 999,
              border: 'none',
              background: '#047857',
              color: '#fff',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {loading && !data && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: isMobile ? '40vh' : 220,
            color: '#94a3b8',
            fontSize: 14,
            width: '100%',
          }}
        >
          Loading portfolio…
        </div>
      )}

      {data && (
        <>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              marginTop: 16,
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>
              Realized / Net P&L window:
            </span>
            {REALIZED_PNL_WINDOWS.map(n => {
              const active = realizedWindow === n;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => handleRealizedWindowChange(n)}
                  style={{
                    padding: '5px 12px',
                    borderRadius: 999,
                    border: `1px solid ${active ? '#086afb' : '#e2e8f0'}`,
                    background: active ? '#086afb' : '#fff',
                    color: active ? '#fff' : '#64748b',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Last {n}
                </button>
              );
            })}
            <span style={{ fontSize: 11, color: '#94a3b8' }}>
              ({closedForRealizedPnl.length} closed in range · 90d max)
            </span>
          </div>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 12,
              marginTop: 12,
            }}
          >
            {[
              { label: 'Total open value', value: fmtUsd(liveTotalOpenValue), sub: `${openPositionCount} open` },
              { label: 'Unrealized P&L', value: fmtPnl(liveUnrealizedPnl), sub: 'on open legs', color: liveUnrealizedPnl >= 0 ? '#15803d' : '#b91c1c' },
              {
                label: 'Realized P&L',
                value: fmtPnl(windowedRealizedPnl),
                sub: `last ${closedForRealizedPnl.length} closed`,
                color: windowedRealizedPnl >= 0 ? '#15803d' : '#b91c1c',
              },
              {
                label: 'Net P&L',
                value: fmtPnl(windowedNetPnl),
                sub: `unrealized + last ${realizedWindow} closed`,
                color: windowedNetPnl >= 0 ? '#15803d' : '#b91c1c',
              },
            ].map(item => (
              <div
                key={item.label}
                style={{
                  ...cardStyle,
                  flex: isMobile ? '1 1 100%' : '1 1 200px',
                  maxWidth: isMobile ? '100%' : 260,
                  minWidth: isMobile ? 0 : 180,
                  width: isMobile ? '100%' : undefined,
                  boxSizing: 'border-box',
                }}
              >
                <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>{item.label}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: item.color ?? '#1e293b', marginTop: 6 }}>{item.value}</div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{item.sub}</div>
              </div>
            ))}
          </div>

          <ProfitByCoinCard
            openPositions={data.openPositions ?? []}
            closedPositions={data.closedPositions ?? []}
            isMobile={isMobile}
            onViewLegs={handleViewCoinLegs}
          />

          {isAdminContext && (
            <div style={{ marginTop: 12 }}>
              <ProfitTargetCard target={profitTarget} onEdit={openTargetModal} />
            </div>
          )}

          {allocation && (allocation.topAssets?.length ?? 0) > 0 && (
            <div style={{ ...cardStyle, marginTop: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 10 }}>Allocation</div>
              <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>
                Bot-managed {Number(allocation.botManagedPct).toFixed(0)}% · Direct {Number(allocation.directPct).toFixed(0)}%
              </div>
              <div style={{ fontSize: 12, color: '#475569' }}>
                Top assets:{' '}
                {allocation.topAssets.map(a => `${a.symbol} ${Number(a.sharePct).toFixed(0)}%`).join(' · ')}
              </div>
            </div>
          )}

          {filteredOpen.length > 0 && (
            <ActiveOpenPositionsChartCard
              rows={filteredOpen}
              isAdminContext={isAdminContext}
              isMobile={isMobile}
              updatedAt={data?.updatedAt ?? null}
            />
          )}

          {isAdminContext && (
            <GrowthChallengeCard
              canManage={canManageClose}
              cardStyle={cardStyle}
              onChallengeBotIdsChange={setChallengeBotIds}
            />
          )}

          {isAdminContext && (
            <ActivityBotPanel
              canManage={canManageClose}
              onPortfolioRefresh={() => load(true)}
              challengeBotIds={challengeBotIds}
            />
          )}

          <div style={{ ...cardStyle, marginTop: 16 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value as StatusFilter)}
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid #e2e8f0',
                  fontSize: 13,
                  width: isMobile ? '100%' : undefined,
                  boxSizing: 'border-box',
                }}
              >
                <option value="OPEN">Open only</option>
                <option value="CLOSED">Closed only (90d)</option>
                <option value="CLOSED_LOSS">Losses (closed)</option>
                <option value="CLOSED_PROFIT">Profits (closed)</option>
                <option value="ALL">All (history)</option>
              </select>
              <select
                value={sourceFilter}
                onChange={e => setSourceFilter(e.target.value as SourceFilter)}
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid #e2e8f0',
                  fontSize: 13,
                  width: isMobile ? '100%' : undefined,
                  boxSizing: 'border-box',
                }}
              >
                <option value="ALL">All sources</option>
                <option value="BOT">Trading bot</option>
                <option value="DIRECT">Direct KuCoin</option>
              </select>
              <input
                type="search"
                placeholder="Search coin…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{
                  flex: isMobile ? '1 1 100%' : 1,
                  minWidth: isMobile ? 0 : 160,
                  width: isMobile ? '100%' : undefined,
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid #e2e8f0',
                  fontSize: 13,
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </div>

          <div ref={positionHistoryRef} style={{ marginTop: 20 }}>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
                Position history ({filteredOpen.length} open · {filteredClosed.length} closed)
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                Open and closed KuCoin legs — sorted by most recent activity. Direct opens on the same coin add to one aggregated KuCoin position (fills shown when expanded). Closed rows kept for 90 days (max 100).
                Use <strong>Ref #</strong> to find a position after it closes.
              </div>
            </div>

            {displayRows.length === 0 && !loading && (
              filteredOpen.length === 0 && filteredClosed.length === 0 ? (
                <div style={{ ...cardStyle, textAlign: 'center', padding: 32, color: '#64748b' }}>
                  <p style={{ margin: '0 0 12px' }}>No crypto positions yet.</p>
                  <button
                    type="button"
                    onClick={() => navigate(isAdminContext ? '/market' : '/market')}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 999,
                      border: 'none',
                      background: '#086afb',
                      color: '#fff',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Go to Market Signals
                  </button>
                </div>
              ) : (
                <div style={{ ...cardStyle, textAlign: 'center', color: '#64748b', fontSize: 13 }}>
                  {statusFilter === 'OPEN'
                    ? 'No open positions match your filters.'
                    : statusFilter === 'CLOSED'
                      ? 'No closed positions in the last 90 days match your filters.'
                      : statusFilter === 'CLOSED_LOSS'
                        ? 'No losing closed positions in the last 90 days match your filters.'
                        : statusFilter === 'CLOSED_PROFIT'
                          ? 'No profitable closed positions in the last 90 days match your filters.'
                          : 'No positions match your filters.'}
                </div>
              )
            )}

            {isAdminContext && canManageClose && (
              <div style={{ ...cardStyle, marginTop: 12, padding: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 14, color: '#1e293b' }}>Open single position</div>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4, lineHeight: 1.4 }}>
                      Search any coin/asset, then open a real KuCoin FUTURES LONG/SHORT (DIRECT mode).
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <div ref={directPositionSearchRef} style={{ position: 'relative', minWidth: 180 }}>
                      <input
                        type="text"
                        value={directPositionQuery}
                        placeholder="Search coin (e.g. BTC)"
                        aria-label="Search coin or asset"
                        autoComplete="off"
                        onFocus={() => setDirectPositionSuggestionsOpen(true)}
                        onChange={e => {
                          const next = e.target.value.toUpperCase();
                          setDirectPositionQuery(next);
                          setDirectPositionSymbol(normalizeDirectSymbol(next));
                          setDirectPositionSuggestionsOpen(true);
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const symbol = normalizeDirectSymbol(directPositionQuery);
                            if (!symbol) return;
                            setDirectPositionSymbol(symbol);
                            setDirectPositionQuery(symbol);
                            setDirectPositionSuggestionsOpen(false);
                          } else if (e.key === 'Escape') {
                            setDirectPositionSuggestionsOpen(false);
                          }
                        }}
                        style={{
                          width: '100%',
                          boxSizing: 'border-box',
                          padding: '8px 12px',
                          borderRadius: 8,
                          border: '1px solid #e2e8f0',
                          fontSize: 12,
                          fontWeight: 700,
                          color: '#1e293b',
                          outline: 'none',
                        }}
                      />
                      {directPositionSuggestionsOpen && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 'calc(100% + 4px)',
                            left: 0,
                            right: 0,
                            zIndex: 30,
                            maxHeight: 220,
                            overflowY: 'auto',
                            background: '#fff',
                            border: '1px solid #e2e8f0',
                            borderRadius: 8,
                            boxShadow: '0 10px 24px rgba(15, 23, 42, 0.12)',
                          }}
                        >
                          {directPositionMatches.length === 0 ? (
                            <div style={{ padding: '10px 12px', fontSize: 12, color: '#94a3b8' }}>
                              {normalizeDirectSymbol(directPositionQuery)
                                ? `Press Enter to use “${normalizeDirectSymbol(directPositionQuery)}”`
                                : 'Type a coin symbol…'}
                            </div>
                          ) : (
                            directPositionMatches.map(sym => (
                              <button
                                key={sym}
                                type="button"
                                onClick={() => {
                                  setDirectPositionSymbol(sym);
                                  setDirectPositionQuery(sym);
                                  setDirectPositionSuggestionsOpen(false);
                                }}
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  textAlign: 'left',
                                  padding: '8px 12px',
                                  border: 'none',
                                  background: sym === directPositionSymbol ? '#eff6ff' : '#fff',
                                  color: '#1e293b',
                                  fontSize: 12,
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                }}
                              >
                                {sym}
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      disabled={!normalizeDirectSymbol(directPositionSymbol || directPositionQuery)}
                      onClick={() => openDirectPosition('LONG')}
                      style={{
                        padding: '8px 14px',
                        borderRadius: 999,
                        border: 'none',
                        background: '#0dca9d',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: 12,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        opacity: !normalizeDirectSymbol(directPositionSymbol || directPositionQuery) ? 0.6 : 1,
                      }}
                    >
                      + Open LONG
                    </button>

                    <button
                      type="button"
                      disabled={!normalizeDirectSymbol(directPositionSymbol || directPositionQuery)}
                      onClick={() => openDirectPosition('SHORT')}
                      style={{
                        padding: '8px 14px',
                        borderRadius: 999,
                        border: 'none',
                        background: '#ff7171',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: 12,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        opacity: !normalizeDirectSymbol(directPositionSymbol || directPositionQuery) ? 0.6 : 1,
                      }}
                    >
                      + Open SHORT
                    </button>
                  </div>
                </div>
              </div>
            )}

            {renderPositionList(displayRows)}
          </div>
        </>
      )}

      <CoinPositionsDrawer
        coin={directPositionCoin}
        open={directPositionDrawerOpen}
        onClose={() => {
          setDirectPositionDrawerOpen(false);
          void load(true);
        }}
        isAdminContext={isAdminContext}
        canOpenPositions={canManageClose}
        onViewStrategy={() => undefined}
        defaultOpenMode="DIRECT"
      />

      {isAdminContext && (
        <ProfitTargetModal
          open={targetModalOpen}
          initialPct={profitTarget?.configured ? String(profitTarget.targetPct ?? '') : '10'}
          saving={targetSaving}
          error={targetError}
          hasTarget={Boolean(profitTarget?.configured)}
          onClose={() => setTargetModalOpen(false)}
          onSave={handleSaveProfitTarget}
          onClear={handleClearProfitTarget}
        />
      )}
    </div>
  );
};

export default CryptoPortfolio;
